/* global BigInt */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { decodeEventLog, formatUnits, parseUnits, isAddress } from "viem";
import { usePublicClient, useWriteContract, useSwitchChain, useWalletClient } from "wagmi";
import { useChainModal, useConnectModal } from "@rainbow-me/rainbowkit";
import useWallet from "../../hooks/useWallet";
import Nav from "../../components/Nav";
import "../presale/presale.scss";
import "./bobtest.scss";

const EXPECTED_CHAIN_ID = 143;
const DEFAULT_CONTRACT = (
  process.env.REACT_APP_BOBTEST_CONTRACT ||
  "0x5739e7760d37BB4176A9f40b1D5F59B90441C383"
).trim();
const START_BLOCK = BigInt(Number((process.env.REACT_APP_NFTTEST_START_BLOCK || "0").trim()));
const ASSET_BASE = (process.env.REACT_APP_NFTTEST_ASSET_BASE || "/nfttest-assets").trim();
const RENDERER_API_BASE = "https://api.bobmonad.com/api/nft-renderer";
const IPFS_GATEWAY = (process.env.REACT_APP_IPFS_GATEWAY || "https://gateway.pinata.cloud/ipfs/").trim();
const MONAD_EXPLORER_BASE = (process.env.REACT_APP_MONAD_MAINNET_EXPLORER_URL || "https://monadvision.com").trim().replace(/\/+$/, "");
const DEV_WALLET = (process.env.REACT_APP_NFTTEST_DEV_WALLET || "0x20A0158803dB7b2D2275Ff265d4a16EEb13179A4").trim().toLowerCase();
const DEV_WALLET_SET = new Set(
  [
    ...String(process.env.REACT_APP_NFTTEST_DEV_WALLETS || "")
      .split(",")
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean),
    DEV_WALLET,
    "0x334270d541d4f9a8717d30690c72159724094323",
  ],
);
const TRAIT_UI_ORDER = [
  ["head", "Head", (t) => Number(t.hat ?? 0) + 1],
  ["eyes", "Eyes", (t) => Number(t.eyes ?? 0) + 1],
  ["mouth", "Mouth", (t) => Number(t.mouth ?? 0) + 1],
  ["outfit", "Outfit", (t) => Number(t.clothes ?? 0) + 1],
  ["accessory", "Accessory", (t) => Number(t.accessory ?? 0) + 1],
  ["back", "Back", (t) => Number(t.background ?? 0) + 1],
];
const UI_STORAGE_KEY = "bob_nfttest_ui_state_v3";
const FALLBACK_MINT_PRICE_WEI = parseUnits("0.1", 18);
const DEV_STAGE_OPTIONS = ["live", "upcoming", "phase1", "phase2", "phase3", "ended"];
const DEFAULT_MINT_GAS_LIMIT = 1_500_000n;

const NFT_ABI = [
  { type: "function", stateMutability: "payable", name: "mintRandom", inputs: [{ type: "uint256" }], outputs: [] },
  { type: "function", stateMutability: "payable", name: "mintRandomBatch", inputs: [{ type: "uint256" }], outputs: [{ type: "uint256[]" }] },
  { type: "function", stateMutability: "payable", name: "recharge", inputs: [{ type: "uint256" }], outputs: [] },
  { type: "function", stateMutability: "payable", name: "levelUp", inputs: [{ type: "uint256" }], outputs: [] },
  { type: "function", stateMutability: "nonpayable", name: "syncXp", inputs: [{ type: "uint256" }], outputs: [] },
  { type: "function", stateMutability: "nonpayable", name: "accrueRewards", inputs: [], outputs: [] },
  { type: "function", stateMutability: "nonpayable", name: "claimAllRewards", inputs: [], outputs: [] },
  { type: "function", stateMutability: "nonpayable", name: "claimRewards", inputs: [{ type: "uint256[]" }], outputs: [] },
  { type: "function", stateMutability: "nonpayable", name: "claimRewardsByIndexRange", inputs: [{ type: "uint256" }, { type: "uint256" }], outputs: [] },
  { type: "function", stateMutability: "nonpayable", name: "setMintPriceUsdWad", inputs: [{ type: "uint256" }], outputs: [] },
  { type: "function", stateMutability: "nonpayable", name: "setMintPaused", inputs: [{ type: "bool" }], outputs: [] },
  { type: "function", stateMutability: "nonpayable", name: "setPhase", inputs: [{ type: "uint8" }], outputs: [] },
  { type: "function", stateMutability: "view", name: "phase", inputs: [], outputs: [{ type: "uint8" }] },
  { type: "function", stateMutability: "view", name: "currentPhase", inputs: [], outputs: [{ type: "uint8" }] },
  { type: "function", stateMutability: "nonpayable", name: "setPhaseCap", inputs: [{ type: "uint8" }, { type: "uint16" }], outputs: [] },
  { type: "function", stateMutability: "nonpayable", name: "setPhase1Whitelist", inputs: [{ type: "address" }, { type: "bool" }], outputs: [] },
  { type: "function", stateMutability: "nonpayable", name: "setPhase2Whitelist", inputs: [{ type: "address" }, { type: "bool" }], outputs: [] },
  { type: "function", stateMutability: "nonpayable", name: "setPhase1WhitelistBatch", inputs: [{ type: "address[]" }, { type: "bool" }], outputs: [] },
  { type: "function", stateMutability: "nonpayable", name: "setPhase2WhitelistBatch", inputs: [{ type: "address[]" }, { type: "bool" }], outputs: [] },
  { type: "function", stateMutability: "view", name: "getPhase1WhitelistCount", inputs: [], outputs: [{ type: "uint256" }] },
  { type: "function", stateMutability: "view", name: "getPhase2WhitelistCount", inputs: [], outputs: [{ type: "uint256" }] },
  { type: "function", stateMutability: "view", name: "getPhase1Whitelist", inputs: [{ type: "uint256" }, { type: "uint256" }], outputs: [{ type: "address[]" }] },
  { type: "function", stateMutability: "view", name: "getPhase2Whitelist", inputs: [{ type: "uint256" }, { type: "uint256" }], outputs: [{ type: "address[]" }] },
  { type: "function", stateMutability: "nonpayable", name: "setMintConfig", inputs: [{ type: "uint256" }, { type: "uint256" }], outputs: [] },
  { type: "function", stateMutability: "view", name: "tokenURI", inputs: [{ type: "uint256" }], outputs: [{ type: "string" }] },
  { type: "function", stateMutability: "view", name: "getTraits", inputs: [{ type: "uint256" }], outputs: [{ type: "tuple", components: [
    { name: "background", type: "uint16" }, { name: "accessory", type: "uint16" }, { name: "clothes", type: "uint16" },
    { name: "eyes", type: "uint16" }, { name: "hat", type: "uint16" }, { name: "mouth", type: "uint16" },
  ] }] },
  { type: "function", stateMutability: "view", name: "assetBaseURI", inputs: [], outputs: [{ type: "string" }] },
  { type: "function", stateMutability: "view", name: "mintPriceUsdWad", inputs: [], outputs: [{ type: "uint256" }] },
  { type: "function", stateMutability: "view", name: "mintPriceWei", inputs: [], outputs: [{ type: "uint256" }] },
  { type: "function", stateMutability: "view", name: "rechargePriceUsdWadByLevel", inputs: [{ type: "uint8" }], outputs: [{ type: "uint256" }] },
  { type: "function", stateMutability: "view", name: "levelUpPriceUsdWadByLevel", inputs: [{ type: "uint8" }], outputs: [{ type: "uint256" }] },
  { type: "function", stateMutability: "view", name: "rewardPoolBalance", inputs: [], outputs: [{ type: "uint256" }] },
  { type: "function", stateMutability: "view", name: "totalTreasuryCollected", inputs: [], outputs: [{ type: "uint256" }] },
  { type: "function", stateMutability: "view", name: "totalBobBoughtForRewards", inputs: [], outputs: [{ type: "uint256" }] },
  { type: "function", stateMutability: "view", name: "buybackReserveMon", inputs: [], outputs: [{ type: "uint256" }] },
  { type: "function", stateMutability: "view", name: "buybackSlippageBps", inputs: [], outputs: [{ type: "uint16" }] },
  { type: "function", stateMutability: "view", name: "maxSwapMonPerTx", inputs: [], outputs: [{ type: "uint256" }] },
  { type: "function", stateMutability: "view", name: "minBobPerMonWad", inputs: [], outputs: [{ type: "uint256" }] },
  { type: "function", stateMutability: "view", name: "bobToken", inputs: [], outputs: [{ type: "address" }] },
  { type: "function", stateMutability: "nonpayable", name: "processBuybackReserve", inputs: [{ type: "uint256" }], outputs: [{ type: "uint256" }] },
  { type: "function", stateMutability: "view", name: "rewardSplitBps", inputs: [], outputs: [{ type: "uint16" }] },
  { type: "function", stateMutability: "view", name: "treasurySplitBps", inputs: [], outputs: [{ type: "uint16" }] },
  { type: "function", stateMutability: "view", name: "lastRewardAccrualAt", inputs: [], outputs: [{ type: "uint64" }] },
  { type: "function", stateMutability: "view", name: "rewardDripRateWadPerSecond", inputs: [], outputs: [{ type: "uint256" }] },
  { type: "function", stateMutability: "view", name: "mintPaused", inputs: [], outputs: [{ type: "bool" }] },
  { type: "function", stateMutability: "view", name: "levelThresholdXp", inputs: [{ type: "uint8" }], outputs: [{ type: "uint256" }] },
  { type: "function", stateMutability: "view", name: "levelWeight", inputs: [{ type: "uint8" }], outputs: [{ type: "uint256" }] },
  { type: "function", stateMutability: "view", name: "owner", inputs: [], outputs: [{ type: "address" }] },
  { type: "function", stateMutability: "view", name: "MAX_CLAIM_BATCH", inputs: [], outputs: [{ type: "uint256" }] },
  { type: "function", stateMutability: "view", name: "getBobUsdPriceWad", inputs: [], outputs: [{ type: "uint256" }] },
  { type: "function", stateMutability: "view", name: "getTokenRuntime", inputs: [{ type: "uint256" }], outputs: [
    { type: "uint8" }, { type: "uint256" }, { type: "uint64" }, { type: "uint64" }, { type: "uint8" }, { type: "uint256" },
  ] },
  { type: "function", stateMutability: "view", name: "getOwnerTokenIds", inputs: [{ type: "address" }, { type: "uint256" }, { type: "uint256" }], outputs: [{ type: "uint256[]" }] },
  { type: "function", stateMutability: "view", name: "getTokenRuntimeBatch", inputs: [{ type: "uint256[]" }], outputs: [
    { type: "uint8[]" }, { type: "uint256[]" }, { type: "uint64[]" }, { type: "uint64[]" }, { type: "uint8[]" }, { type: "uint256[]" },
  ] },
  { type: "function", stateMutability: "view", name: "totalClaimedByOwner", inputs: [{ type: "address" }], outputs: [{ type: "uint256" }] },
  { type: "function", stateMutability: "view", name: "claimableForOwner", inputs: [{ type: "address" }], outputs: [{ type: "uint256" }] },
  { type: "function", stateMutability: "view", name: "getOwnerRewardStats", inputs: [{ type: "address" }], outputs: [{ type: "uint256" }, { type: "uint256" }, { type: "uint256" }] },
  { type: "function", stateMutability: "view", name: "balanceOf", inputs: [{ type: "address" }], outputs: [{ type: "uint256" }] },
  { type: "function", stateMutability: "view", name: "tokenOfOwnerByIndex", inputs: [{ type: "address" }, { type: "uint256" }], outputs: [{ type: "uint256" }] },
  { type: "function", stateMutability: "view", name: "ownerOf", inputs: [{ type: "uint256" }], outputs: [{ type: "address" }] },
  { type: "function", stateMutability: "view", name: "totalSupply", inputs: [], outputs: [{ type: "uint256" }] },
  { type: "function", stateMutability: "view", name: "MAX_SUPPLY", inputs: [], outputs: [{ type: "uint256" }] },
  { type: "event", name: "BobMinted", inputs: [
    { indexed: true, name: "tokenId", type: "uint256" }, { indexed: true, name: "owner", type: "address" },
    { indexed: false, name: "background", type: "uint16" }, { indexed: false, name: "accessory", type: "uint16" },
    { indexed: false, name: "clothes", type: "uint16" }, { indexed: false, name: "eyes", type: "uint16" },
    { indexed: false, name: "hat", type: "uint16" }, { indexed: false, name: "mouth", type: "uint16" },
  ] },
];

const parseDataUriJson = (dataUri) => {
  try {
    if (!dataUri?.startsWith("data:application/json;base64,")) return null;
    return JSON.parse(atob(dataUri.slice("data:application/json;base64,".length)));
  } catch {
    return null;
  }
};

const resolveNftImageSrc = (imageUri) => {
  if (!imageUri || typeof imageUri !== "string") return null;
  // Contract fallback image from older sessions should never be shown in UI
  // while current token metadata is still being finalized.
  const normalized = imageUri.trim().toLowerCase();
  if (normalized.includes("/base/bob-base.png")) return null;
  // Block known legacy/base-asset CID from old contract-era fallback metadata.
  if (normalized.startsWith("ipfs://bafybeibifxyxxksajwflmkyfydb4bx3behfppobnfl5pwmrjtmrx6omyui")) return null;
  if (normalized.includes("/bafybeibifxyxxksajwflmkyfydb4bx3behfppobnfl5pwmrjtmrx6omyui/")) return null;
  if (/^https?:\/\//i.test(imageUri)) return imageUri;
  if (imageUri.startsWith("ipfs://")) return `${IPFS_GATEWAY}${imageUri.slice("ipfs://".length).replace(/^\/+/, "")}`;
  return null;
};
const preloadImage = (src, timeoutMs = 12000) =>
  new Promise((resolve) => {
    if (!src) return resolve(false);
    const img = new Image();
    let done = false;
    const finish = (ok) => {
      if (done) return;
      done = true;
      resolve(ok);
    };
    const t = window.setTimeout(() => finish(false), timeoutMs);
    img.onload = () => {
      window.clearTimeout(t);
      finish(true);
    };
    img.onerror = () => {
      window.clearTimeout(t);
      finish(false);
    };
    img.src = src;
  });

const statusLabel = (value) => (Number(value) === 0 ? "ACTIVE" : "INACTIVE");
const fmtTs = (ts) => (!Number(ts) ? "-" : new Date(Number(ts) * 1000).toLocaleString());
const fmtRemaining = (targetTs, nowSec) => {
  const target = Number(targetTs || 0);
  if (!target) return "-";
  const remaining = Math.max(0, Math.floor(target - nowSec));
  if (remaining >= 86400) return `${Math.floor(remaining / 86400)}d`;
  if (remaining >= 3600) return `${Math.floor(remaining / 3600)}h`;
  if (remaining >= 60) return `${Math.floor(remaining / 60)}min`;
  return `${remaining}s`;
};
const sleep = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));
const fmtPct = (n) => `${Math.max(0, Math.min(100, Number(n || 0))).toFixed(1)}%`;
const formatPresaleLikeError = (raw) => {
  const original = String(raw || "").trim();
  const s = original.toLowerCase();
  if (!s) return "";
  if (s.includes("insufficient mon balance")) return original;
  if (s.includes("insufficient funds")) return "Insufficient MON balance for this mint quantity.";
  if (s.includes("max supply reached")) return "Mint sold out. No NFTs remaining.";
  if (s.includes("incorrect mon value")) return "Mint price changed. Please try again.";
  if (s.includes("wrong network")) return "Wrong network. Please switch to Monad Mainnet and try again.";
  if (s.includes("please switch wallet network")) return "Wrong network. Please switch to Monad Mainnet and try again.";
  if (s.includes("network switch was cancelled") || s.includes("cancelled")) return "Transaction cancelled. Please try again.";
  if (s.includes("network switched. click")) return "Wrong network. Please switch to Monad Mainnet and try again.";
  if (s.includes("connect wallet first")) return "Please connect your wallet to continue.";
  // Preserve human-readable custom app errors instead of collapsing to a generic message.
  if (original && !s.includes("rpc") && !s.includes("request failed")) return original;
  return "Something went wrong. Please try again.";
};
const fmtAmount = (value, decimals = 18, maxFractionDigits = 2) => {
  try {
    const raw = Number(formatUnits(value || 0n, decimals));
    if (!Number.isFinite(raw)) return "0";
    return raw.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: maxFractionDigits,
    });
  } catch {
    return "0";
  }
};
const errorText = (e) => String(e?.shortMessage || e?.message || e || "");
const isNonceTooLowError = (e) => /nonce too low/i.test(errorText(e));
const logFullMintError = (e, context = {}) => {
  try {
    const details = {
      shortMessage: e?.shortMessage,
      message: e?.message,
      reason: e?.reason,
      code: e?.code,
      name: e?.name,
      data: e?.data,
      cause: e?.cause,
      metaMessages: e?.metaMessages,
      details: e?.details,
      context,
    };
    console.group("[BobTest Mint Error]");
    console.error("Full error object:", e);
    console.error("Parsed details:", details);
    try {
      console.error("JSON:", JSON.stringify(details, null, 2));
    } catch {
      // ignore circular structures
    }
    console.groupEnd();
  } catch {
    // keep UI flow resilient even if logging fails
  }
};
const isTransientRpcReadError = (e) => {
  const s = String(e?.shortMessage || e?.message || e || "").toLowerCase();
  if (!s) return false;
  return (
    s.includes("http request failed") ||
    s.includes("rpc request failed") ||
    s.includes("failed to fetch") ||
    s.includes("fetch failed") ||
    s.includes("timeout") ||
    s.includes("timed out") ||
    s.includes("econnreset") ||
    s.includes("socket hang up") ||
    s.includes("429") ||
    s.includes("503")
  );
};
const isRpcHardLimitError = (e) => {
  const s = String(e?.shortMessage || e?.message || e || "").toLowerCase();
  if (!s) return false;
  return s.includes("429") || s.includes("413") || s.includes("too many requests") || s.includes("content too large");
};
const isChainMismatchError = (e) => {
  const s = String(e?.shortMessage || e?.message || e || "").toLowerCase();
  return s.includes("does not match the target chain") || (s.includes("current chain") && s.includes("target chain"));
};
const withTimeout = (promise, ms, message = "Timed out") =>
  Promise.race([
    promise,
    new Promise((_, reject) => window.setTimeout(() => reject(new Error(message)), ms)),
  ]);
const parseAddressList = (raw) =>
  String(raw || "")
    .split(/[\s,]+/g)
    .map((v) => v.trim())
    .filter(Boolean)
    .filter((v, i, arr) => arr.indexOf(v) === i)
    .filter((v) => isAddress(v));
function computeXpProgress(runtime, levelThresholds) {
  if (!runtime) return { current: 0n, displayCurrent: 0n, needed: 0n, pct: 0, nextLevel: null, needsKnown: false };
  const currentLevel = Number(runtime.level || 1);
  if (currentLevel >= 5) return { current: runtime.xp || 0n, displayCurrent: runtime.xp || 0n, needed: 0n, pct: 100, nextLevel: null, needsKnown: true };
  const nextLevel = currentLevel + 1;
  const threshold = levelThresholds?.[nextLevel];
  const needsKnown = Boolean(typeof threshold === "bigint" && threshold > 0n);
  if (!needsKnown) {
    return { current: runtime.xp || 0n, displayCurrent: runtime.xp || 0n, needed: 0n, pct: 0, nextLevel, needsKnown: false };
  }
  const needed = threshold;
  const current = runtime.xp || 0n;
  const displayCurrent = current > needed ? needed : current;
  const pct = Number((current * 10000n) / needed) / 100;
  return { current, displayCurrent, needed, pct: Math.min(100, pct), nextLevel, needsKnown: true };
}

function mapMint(log, index = 0) {
  return {
    id: `${log.transactionHash}-${log.logIndex ?? index}`,
    blockNumber: Number(log.blockNumber || 0n),
    txHash: log.transactionHash,
    tokenId: String(log.args.tokenId),
    owner: String(log.args.owner),
    traits: {
      background: Number(log.args.background),
      accessory: Number(log.args.accessory),
      clothes: Number(log.args.clothes),
      eyes: Number(log.args.eyes),
      hat: Number(log.args.hat),
      mouth: Number(log.args.mouth),
    },
    metadataImage: null,
  };
}

function parseTraitIndexFromValue(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return null;
  const m = raw.match(/trait\s*(\d+)/i) || raw.match(/^(\d+)$/);
  if (!m) return null;
  const oneBased = Number(m[1]);
  if (!Number.isFinite(oneBased) || oneBased <= 0) return null;
  return oneBased - 1;
}

function normalizeTraitsFromAny(input) {
  if (input && typeof input === "object" && !Array.isArray(input)) {
    const direct = {
      background: Number(input.background),
      accessory: Number(input.accessory),
      clothes: Number(input.clothes),
      eyes: Number(input.eyes),
      hat: Number(input.hat),
      mouth: Number(input.mouth),
    };
    if (Object.values(direct).every((v) => Number.isFinite(v))) return direct;
  }

  const attrs = Array.isArray(input) ? input : [];
  if (!attrs.length) return null;
  const byType = new Map(
    attrs.map((a) => [String(a?.trait_type || "").trim().toLowerCase(), parseTraitIndexFromValue(a?.value)])
  );
  const fromAttrs = {
    background: byType.get("background"),
    accessory: byType.get("back accessory"),
    clothes: byType.get("body"),
    eyes: byType.get("eyes"),
    hat: byType.get("head"),
    mouth: byType.get("mouth"),
  };
  if (Object.values(fromAttrs).every((v) => Number.isFinite(v))) return fromAttrs;
  return null;
}

function mapRecentFeedMint(item) {
  const normalizedTraits =
    normalizeTraitsFromAny(item?.traits) ||
    normalizeTraitsFromAny(item?.attributes) ||
    null;
  return {
    id: `${String(item?.txHash || "feed")}-${String(item?.tokenId || "")}-${String(item?.updatedAt || Date.now())}`,
    blockNumber: Number(item?.blockNumber || 0),
    txHash: String(item?.txHash || ""),
    tokenId: String(item?.tokenId || ""),
    owner: String(item?.owner || ""),
    traits: normalizedTraits,
    metadataImage: String(item?.imageURI || ""),
  };
}

function normalizeTraitsFromContract(value) {
  if (!value) return null;
  return {
    background: Number(value.background ?? value[0] ?? 0),
    accessory: Number(value.accessory ?? value[1] ?? 0),
    clothes: Number(value.clothes ?? value[2] ?? 0),
    eyes: Number(value.eyes ?? value[3] ?? 0),
    hat: Number(value.hat ?? value[4] ?? 0),
    mouth: Number(value.mouth ?? value[5] ?? 0),
  };
}

function MintImage({ mint, size }) {
  const hasExplicitEmptyImage = typeof mint?.metadataImage === "string" && mint.metadataImage.trim() === "";
  const imageForRender = resolveNftImageSrc(mint?.metadataImage);
  const fill = size === "100%";
  const boxStyle = {
    width: fill ? "100%" : size,
    height: fill ? "100%" : size,
    borderRadius: 12,
    border: "1px solid rgba(148, 163, 184, 0.32)",
    background: "radial-gradient(circle at 50% 0%, rgba(15, 23, 42, 0.9), rgba(2, 6, 23, 1))",
  };

  if (imageForRender) {
    return (
      <img
        alt={`Token ${mint.tokenId}`}
        src={imageForRender}
        referrerPolicy="no-referrer"
        style={{
          ...boxStyle,
          objectFit: "contain",
          display: "block",
          borderRadius: "inherit",
          boxSizing: "border-box",
        }}
      />
    );
  }
  if (hasExplicitEmptyImage) {
    return (
      <div
        style={{
          ...boxStyle,
          position: "relative",
          overflow: "hidden",
          borderColor: "rgba(148,163,184,0.35)",
          background: "linear-gradient(120deg, rgba(15,23,42,0.95) 10%, rgba(30,41,59,0.95) 38%, rgba(15,23,42,0.95) 62%)",
          backgroundSize: "220% 100%",
          animation: "nftSyncShimmer 1.4s ease-in-out infinite",
        }}
      >
        <div
          style={{
            position: "absolute",
            bottom: 10,
            left: 10,
            right: 10,
            height: 8,
            borderRadius: 999,
            background: "rgba(148,163,184,0.28)",
          }}
        />
      </div>
    );
  }
  return <div style={{ ...boxStyle, display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8", fontSize: 12 }}>Loading...</div>;
}

export default function BobTestPage({ mode = "bobtest" }) {
  const isBobTest = mode === "bobtest";
  const wallet = useWallet();
  const { openChainModal } = useChainModal();
  const { openConnectModal } = useConnectModal();
  const publicClient = usePublicClient({ chainId: EXPECTED_CHAIN_ID });
  const { writeContractAsync } = useWriteContract();
  const { switchChainAsync } = useSwitchChain();
  const { data: wagmiWalletClient } = useWalletClient();
  const initialUiState = useMemo(() => {
    try {
      const raw = window.localStorage.getItem(UI_STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }, []);

  const [contractAddress, setContractAddress] = useState(DEFAULT_CONTRACT);
  const [isLoading, setIsLoading] = useState(false);
  const [isMinting, setIsMinting] = useState(false);
  const [isClaimingAll, setIsClaimingAll] = useState(false);
  const [error, setError] = useState("");
  const [protocolReadError, setProtocolReadError] = useState("");
  const [contractCodePresent, setContractCodePresent] = useState(null);
  const [lastTxHash, setLastTxHash] = useState("");
  const [latestMint, setLatestMint] = useState(null);
  const [allMints, setAllMints] = useState([]);
  const [ownedMints, setOwnedMints] = useState([]);
  const [ownedLoadStatus, setOwnedLoadStatus] = useState("");
  const [ownedBalanceOnChain, setOwnedBalanceOnChain] = useState("0");
  const [ownedTokenIdsOnChain, setOwnedTokenIdsOnChain] = useState([]);
  const [resolvedAssetBase, setResolvedAssetBase] = useState(ASSET_BASE);
  const [rendererSyncStatus, setRendererSyncStatus] = useState("");
  const [isV5, setIsV5] = useState(null);
  const [nativeBalanceWei, setNativeBalanceWei] = useState(0n);
  const [protocolData, setProtocolData] = useState({});
  const [runtimeByTokenId, setRuntimeByTokenId] = useState({});
  const [ownerRewardStats, setOwnerRewardStats] = useState({ pending: 0n, claimed: 0n, earned: 0n });
  const [protocolBootstrapped, setProtocolBootstrapped] = useState(false);
  const [rewardsBootstrapped, setRewardsBootstrapped] = useState(false);
  const [inventoryBootstrapped, setInventoryBootstrapped] = useState(false);
  const [mintMon, setMintMon] = useState(initialUiState.mintMon || "1");
  const [maxSupplyPerRoundInput, setMaxSupplyPerRoundInput] = useState(initialUiState.maxSupplyPerRoundInput || "1000");
  const [phase1Cap, setPhase1Cap] = useState(initialUiState.phase1Cap || "4");
  const [phase2Cap, setPhase2Cap] = useState(initialUiState.phase2Cap || "2");
  const [phase3Cap, setPhase3Cap] = useState(initialUiState.phase3Cap || "2");
  const [whitelistPanelOpen, setWhitelistPanelOpen] = useState(false);
  const [phase1WhitelistInput, setPhase1WhitelistInput] = useState("");
  const [phase2WhitelistInput, setPhase2WhitelistInput] = useState("");
  const [phase1Whitelist, setPhase1Whitelist] = useState([]);
  const [phase2Whitelist, setPhase2Whitelist] = useState([]);
  const [whitelistLoading, setWhitelistLoading] = useState(false);
  const [whitelistSupported, setWhitelistSupported] = useState(true);
  const [mintQty, setMintQty] = useState(1);
  const [showDevConsole, setShowDevConsole] = useState(false);
  const [previewMint, setPreviewMint] = useState(null);
  const [isPreviewClosing, setIsPreviewClosing] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);
  const [previewSoldOut, setPreviewSoldOut] = useState(false);
  const [previewStage, setPreviewStage] = useState("live");
  const [previewWalletMaxed, setPreviewWalletMaxed] = useState(false);
  const [mintRevealOpen, setMintRevealOpen] = useState(false);
  const [mintRevealLoading, setMintRevealLoading] = useState(false);
  const [mintRevealItems, setMintRevealItems] = useState([]);
  const [mintRevealPendingIds, setMintRevealPendingIds] = useState([]);
  const [sortKey, setSortKey] = useState("name_desc");
  const [filterLevel, setFilterLevel] = useState("any");
  const [filterState, setFilterState] = useState("any");
  const [filterTraits, setFilterTraits] = useState({
    head: "any",
    eyes: "any",
    mouth: "any",
    outfit: "any",
    accessory: "any",
    back: "any",
  });
  const [showSortPanel, setShowSortPanel] = useState(false);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [openSelectKey, setOpenSelectKey] = useState("");
  const [dashboardPage, setDashboardPage] = useState(1);
  const [dashboardTotalPagesServer, setDashboardTotalPagesServer] = useState(1);
  const [dashboardTotalServer, setDashboardTotalServer] = useState(0);
  const [nowSec, setNowSec] = useState(() => Math.floor(Date.now() / 1000));
  const DASHBOARD_PAGE_SIZE = 10;
  const walletContextRef = useRef("");
  const allMintsRef = useRef([]);
  const inventoryBootstrappedRef = useRef(false);
  const protocolLoadSeqRef = useRef(0);
  const mintsLoadSeqRef = useRef(0);
  const inventoryLoadSeqRef = useRef(0);
  const batchSyncSupportedRef = useRef(true);
  const dashControlsRef = useRef(null);

  const closePreview = useCallback(() => {
    setIsPreviewClosing(true);
    window.setTimeout(() => {
      setPreviewMint(null);
      setIsPreviewClosing(false);
    }, 180);
  }, []);
  const mintedSupplyLive = Number(protocolData.totalSupply || 0n);
  const maxSupplyLive = Number(protocolData.maxSupply || 1000n) || 1000;
  const stagePreviewActive =
    Boolean(showDevConsole) &&
    Boolean(wallet.address) &&
    DEV_WALLET_SET.has(String(wallet.address || "").toLowerCase()) &&
    previewStage !== "live" &&
    DEV_STAGE_OPTIONS.includes(previewStage);
  const parsedLivePhase = Number(protocolData?.currentPhase);
  const livePhaseNum = Number.isFinite(parsedLivePhase) ? parsedLivePhase : null;
  const liveStage =
    livePhaseNum === null ? "unknown" :
    livePhaseNum === 0 ? "upcoming" :
    livePhaseNum === 2 ? "phase2" :
    livePhaseNum === 3 ? "phase3" :
    "phase1";
  const effectiveStage = stagePreviewActive ? previewStage : liveStage;
  const endedPreviewActive = stagePreviewActive && effectiveStage === "ended";
  const stageMintDisabled = effectiveStage === "upcoming" || effectiveStage === "ended";
  const soldOutPreviewActive = stagePreviewActive && previewSoldOut;
  const mintedSupply = (soldOutPreviewActive || endedPreviewActive) ? maxSupplyLive : mintedSupplyLive;
  const maxSupply = maxSupplyLive;
  const isSoldOut = maxSupply > 0 && mintedSupply >= maxSupply;
  const maxMintableNow = Math.max(0, maxSupply - mintedSupply);
  const walletMaxedPreviewActive = stagePreviewActive && previewWalletMaxed;
  const effectiveMaxMintableNow = walletMaxedPreviewActive ? 0 : maxMintableNow;
  const mintProgressPct = Math.max(0, Math.min(100, (mintedSupply / maxSupply) * 100));
  const safeMintQty = Math.max(1, Math.min(20, Number(mintQty || 1)));
  const mintUnitPriceWei =
    protocolData?.mintPriceMonWei && protocolData.mintPriceMonWei > 0n
      ? protocolData.mintPriceMonWei
      : FALLBACK_MINT_PRICE_WEI;
  const mintTotalPriceWei = mintUnitPriceWei * BigInt(safeMintQty);

  const canRead = isAddress(contractAddress) && Boolean(publicClient);
  const hasWallet = Boolean(wallet.address);
  const isWrongNetwork = hasWallet && Number(wallet.chainId || 0) !== EXPECTED_CHAIN_ID;
  const isInventoryLoading =
    hasWallet &&
    canRead &&
    !inventoryBootstrapped &&
    (ownedLoadStatus === "" || ownedLoadStatus === "loading");

  const levelThresholds = protocolData.levelThresholds || {};
  const displayError = useMemo(() => formatPresaleLikeError(error), [error]);
  const mintNotice = useMemo(() => {
    if (!hasWallet) return { level: "info", text: "Connect wallet to continue" };
    if (effectiveStage === "upcoming") return { level: "warning", text: "Mint is upcoming. Actions are disabled in this preview stage" };
    if (walletMaxedPreviewActive) return { level: "warning", text: "Wallet reached mint limit" };
    if (isWrongNetwork) return { level: "warning", text: "Wrong network. Please switch to Monad Mainnet" };
    if (isSoldOut) return { level: "warning", text: "Mint is sold out. No NFTs remaining" };
    if (displayError) return { level: "error", text: displayError };
    if (isMinting) return { level: "info", text: `Minting ${safeMintQty}...` };
    return { level: "info", text: `Ready to mint - You can mint ${Math.max(0, effectiveMaxMintableNow)} more Bob NFTs` };
  }, [hasWallet, effectiveStage, walletMaxedPreviewActive, isWrongNetwork, isSoldOut, displayError, isMinting, safeMintQty, effectiveMaxMintableNow]);
  const heroKickerText = useMemo(() => {
    if (effectiveStage === "unknown") return "Loading live phase from contract...";
    if (effectiveStage === "upcoming") return "Upcoming - Whitelist Pending";
    if (effectiveStage === "phase2") return "Phase 2 - Whitelisted Users";
    if (effectiveStage === "phase3") return "Phase 3 - Open to Everyone";
    if (effectiveStage === "ended") return "Mint Ended";
    return "Phase 1 - Top Bob Holders";
  }, [effectiveStage]);
  const heroStatus = useMemo(() => {
    if (effectiveStage === "upcoming") return { className: "status-upcoming", label: "Upcoming" };
    if (effectiveStage === "ended" || isSoldOut) return { className: "status-ended", label: "Ended" };
    return { className: "status-live", label: "Live" };
  }, [effectiveStage, isSoldOut]);
  const connectedAddress = String(wallet.address || "").toLowerCase();
  const ownerAddress = String(protocolData.owner || "").toLowerCase();
  const isDevConnected = hasWallet && Boolean(connectedAddress) && (
    DEV_WALLET_SET.has(connectedAddress) || (ownerAddress && connectedAddress === ownerAddress)
  );

  useEffect(() => {
    const walletKey = String(wallet.address || "").toLowerCase();
    const contractKey = String(contractAddress || "").toLowerCase();
    const chainKey = String(Number(wallet.chainId || 0));
    walletContextRef.current = `${walletKey}|${contractKey}|${chainKey}`;
  }, [wallet.address, contractAddress, wallet.chainId]);

  useEffect(() => {
    allMintsRef.current = allMints;
  }, [allMints]);

  useEffect(() => {
    inventoryBootstrappedRef.current = inventoryBootstrapped;
  }, [inventoryBootstrapped]);

  useEffect(() => {
    if (maxMintableNow <= 0) return;
    setMintQty((q) => {
      const current = Math.max(1, Number(q || 1));
      return Math.min(current, maxMintableNow);
    });
  }, [maxMintableNow]);

  useEffect(() => {
    const closeOnOutsidePress = (event) => {
      if (!dashControlsRef.current) return;
      if (!dashControlsRef.current.contains(event.target)) {
        setOpenSelectKey("");
        setShowSortPanel(false);
        setShowFilterPanel(false);
      }
    };
    document.addEventListener("mousedown", closeOnOutsidePress);
    document.addEventListener("touchstart", closeOnOutsidePress, { passive: true });
    return () => {
      document.removeEventListener("mousedown", closeOnOutsidePress);
      document.removeEventListener("touchstart", closeOnOutsidePress);
    };
  }, []);

  useEffect(() => {
    if (!error) {
      setToastVisible(false);
      return undefined;
    }
    setToastVisible(true);
    const t = window.setTimeout(() => setToastVisible(false), 5200);
    return () => window.clearTimeout(t);
  }, [error]);

  const readContractWithRetry = useCallback(async (params, attempts = 4) => {
    let lastErr;
    for (let i = 0; i < attempts; i += 1) {
      try {
        return await publicClient.readContract(params);
      } catch (err) {
        lastErr = err;
        if (isRpcHardLimitError(err)) break;
        if (i < attempts - 1) {
          const baseDelay = isTransientRpcReadError(err) ? 700 : 450;
          await sleep(baseDelay * (i + 1));
        }
      }
    }
    throw lastErr;
  }, [publicClient]);

  const getLogsWithRetry = useCallback(async (params, attempts = 2) => {
    let lastErr;
    for (let i = 0; i < attempts; i += 1) {
      try {
        return await publicClient.getLogs(params);
      } catch (err) {
        lastErr = err;
        if (i < attempts - 1) await sleep(600 * (i + 1));
      }
    }
    throw lastErr;
  }, [publicClient]);

  const writeWithNonceRetry = useCallback(async (request, attempts = 3) => {
    let forcedNonce = null;
    let lastErr = null;
    for (let i = 0; i < attempts; i += 1) {
      try {
        const nonceArg = forcedNonce !== null ? { nonce: forcedNonce } : {};
        return await writeContractAsync({ chainId: EXPECTED_CHAIN_ID, ...request, ...nonceArg });
      } catch (err) {
        lastErr = err;
        if (!isNonceTooLowError(err) || !wallet.address || !publicClient || i >= attempts - 1) {
          throw err;
        }
        const nextNonce = await publicClient.getTransactionCount({
          address: wallet.address,
          blockTag: "pending",
        });
        forcedNonce = typeof nextNonce === "bigint" ? nextNonce : BigInt(nextNonce);
        await sleep(500 + i * 500);
      }
    }
    throw lastErr;
  }, [wallet.address, publicClient, writeContractAsync]);

  const waitForReceiptRobust = useCallback(async (hash, confirmations = 1) => {
    if (!publicClient || !hash) return null;
    try {
      return await publicClient.waitForTransactionReceipt({ hash, confirmations });
    } catch {
      // Fallback polling path for RPCs that intermittently timeout on wait.
      for (let i = 0; i < 10; i += 1) {
        try {
          const receipt = await publicClient.getTransactionReceipt({ hash });
          if (receipt) return receipt;
        } catch {}
        await sleep(1500);
      }
      return null;
    }
  }, [publicClient]);

  const getActiveProviderChainId = useCallback(async () => {
    try {
      const eth = typeof window !== "undefined" ? window.ethereum : null;
      if (eth?.request) {
        const hex = await eth.request({ method: "eth_chainId" });
        const id = Number.parseInt(String(hex), 16);
        if (Number.isFinite(id) && id > 0) return id;
      }
    } catch {}
    return Number(wallet.chainId || 0);
  }, [wallet.chainId]);

  const requestNetworkSwitch = useCallback(async () => {
    try {
      const expectedHex = `0x${EXPECTED_CHAIN_ID.toString(16)}`;
      const eth = typeof window !== "undefined" ? window.ethereum : null;
      if (eth?.request) {
        const currentHex = await eth.request({ method: "eth_chainId" });
        if (String(currentHex).toLowerCase() === expectedHex.toLowerCase()) {
          return true;
        }
        await eth.request({
          method: "wallet_switchEthereumChain",
          params: [{ chainId: expectedHex }],
        });
        const afterHex = await eth.request({ method: "eth_chainId" });
        return String(afterHex).toLowerCase() === expectedHex.toLowerCase();
      }
      if (typeof switchChainAsync === "function") {
        await switchChainAsync({ chainId: EXPECTED_CHAIN_ID });
        const verifiedId = await getActiveProviderChainId();
        return verifiedId === EXPECTED_CHAIN_ID;
      }
      if (wagmiWalletClient && typeof wagmiWalletClient.request === "function") {
        await wagmiWalletClient.request({
          method: "wallet_switchEthereumChain",
          params: [{ chainId: expectedHex }],
        });
        const verifiedId = await getActiveProviderChainId();
        return verifiedId === EXPECTED_CHAIN_ID;
      }
      setError(`Please switch to chain ${EXPECTED_CHAIN_ID} in your wallet.`);
      return false;
    } catch (e) {
      const msg = String(e?.shortMessage || e?.message || e || "").toLowerCase();
      if (msg.includes("user rejected") || msg.includes("4001")) {
        setError("Network switch was cancelled.");
      } else {
        setError(`Please switch to chain ${EXPECTED_CHAIN_ID} in your wallet.`);
      }
      return false;
    }
  }, [switchChainAsync, wagmiWalletClient, getActiveProviderChainId]);

  const ensureCorrectNetwork = useCallback(async () => {
    if (!hasWallet) {
      setError("Connect wallet first.");
      return false;
    }
    const activeChainId = await getActiveProviderChainId();
    if (activeChainId === EXPECTED_CHAIN_ID) return true;

    const switched = await requestNetworkSwitch();
    if (switched) {
      const verifiedAfterSwitch = await getActiveProviderChainId();
      if (verifiedAfterSwitch === EXPECTED_CHAIN_ID) {
        // Stop this click path after a switch request to avoid write/switch race conditions.
        setError("Wrong network. Please switch to Monad Mainnet and try again.");
        return false;
      }
    }

    if (typeof openChainModal === "function") {
      openChainModal();
    }
    setError("Please switch wallet network to Monad Mainnet (143), then try again.");
    return false;
  }, [hasWallet, getActiveProviderChainId, requestNetworkSwitch, openChainModal]);

  useEffect(() => {
    setIsV5(null);
    setProtocolBootstrapped(false);
    setRewardsBootstrapped(false);
    setInventoryBootstrapped(false);
  }, [contractAddress]);

  useEffect(() => {
    // Wallet switched/disconnected: clear claimable stats immediately to avoid stale values.
    if (!wallet.address) {
      setOwnerRewardStats({ pending: 0n, claimed: 0n, earned: 0n });
      setRewardsBootstrapped(true);
      return;
    }
    setOwnerRewardStats({ pending: 0n, claimed: 0n, earned: 0n });
    setRewardsBootstrapped(false);
  }, [wallet.address]);

  useEffect(() => {
    // Wallet/contract context changed: reset inventory state so previous wallet holdings never bleed through.
    setOwnedMints([]);
    setRuntimeByTokenId({});
    setOwnedTokenIdsOnChain([]);
    setOwnedBalanceOnChain("0");
    setOwnedLoadStatus(wallet.address ? "loading" : "wallet_disconnected");
    setInventoryBootstrapped(false);
  }, [wallet.address, contractAddress]);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        UI_STORAGE_KEY,
        JSON.stringify({
          mintMon,
          maxSupplyPerRoundInput,
          phase1Cap,
          phase2Cap,
          phase3Cap,
        })
      );
    } catch {}
  }, [contractAddress, mintMon, maxSupplyPerRoundInput, phase1Cap, phase2Cap, phase3Cap]);

  const enrichWithTokenMetadata = useCallback(async (mints) => {
    if (!canRead || !mints?.length) return mints;
    return Promise.all(mints.map(async (m) => {
      try {
        const uri = await readContractWithRetry({ address: contractAddress, abi: NFT_ABI, functionName: "tokenURI", args: [BigInt(m.tokenId)] }, 2);
        const parsed = parseDataUriJson(uri);
        return { ...m, metadataImage: typeof parsed?.image === "string" ? parsed.image : null };
      } catch {
        return m;
      }
    }));
  }, [canRead, readContractWithRetry, contractAddress]);

  const loadProtocolData = useCallback(async () => {
    if (!canRead) return;
    const seq = ++protocolLoadSeqRef.current;
    const ctx = walletContextRef.current;
    const stale = () => protocolLoadSeqRef.current !== seq || walletContextRef.current !== ctx;
    try {
      setProtocolReadError("");
      // Isolated live phase read first: keep phase UI accurate even if deeper
      // protocol reads fail due transient RPC/ABI mismatches.
      try {
        const pDirect = await readContractWithRetry({ address: contractAddress, abi: NFT_ABI, functionName: "phase", args: [] }, 2);
        const parsed = Number(pDirect);
        if (!stale() && Number.isFinite(parsed)) {
          setProtocolData((prev) => ({ ...prev, currentPhase: parsed }));
        }
      } catch {
        try {
          const pCurrent = await readContractWithRetry({ address: contractAddress, abi: NFT_ABI, functionName: "currentPhase", args: [] }, 2);
          const parsed = Number(pCurrent);
          if (!stale() && Number.isFinite(parsed)) {
            setProtocolData((prev) => ({ ...prev, currentPhase: parsed }));
          }
        } catch {}
      }

      const bytecode = await publicClient.getBytecode({ address: contractAddress });
      const hasCode = typeof bytecode === "string" && bytecode !== "0x";
      if (!stale()) setContractCodePresent(hasCode);
      if (!hasCode) throw new Error(`No contract bytecode at ${contractAddress} for current RPC/network.`);

      const protocolReadAttempts = isBobTest ? 1 : 2;
      const owner = await readContractWithRetry({ address: contractAddress, abi: NFT_ABI, functionName: "owner", args: [] }, protocolReadAttempts);
      if (stale()) return;
      setIsV5(true);
      const safeRead = async (fn, args = [], fallbackValue = 0n) => {
        try {
          return await readContractWithRetry({ address: contractAddress, abi: NFT_ABI, functionName: fn, args }, protocolReadAttempts);
        } catch {
          return fallbackValue;
        }
      };
      const strictRead = async (fn, args = []) => {
        return await readContractWithRetry({ address: contractAddress, abi: NFT_ABI, functionName: fn, args }, protocolReadAttempts);
      };

      let mintPriceMonWei = await safeRead("mintPriceWei", [], 0n);
      if (!(typeof mintPriceMonWei === "bigint" && mintPriceMonWei > 0n)) {
        mintPriceMonWei = await safeRead("mintPriceUsdWad", [], 0n);
      }
      const rechargeL1UsdWad = isBobTest ? 0n : await safeRead("rechargePriceUsdWadByLevel", [1]);
      const rechargeL2UsdWad = isBobTest ? 0n : await safeRead("rechargePriceUsdWadByLevel", [2]);
      const rechargeL3UsdWad = isBobTest ? 0n : await safeRead("rechargePriceUsdWadByLevel", [3]);
      const rechargeL4UsdWad = isBobTest ? 0n : await safeRead("rechargePriceUsdWadByLevel", [4]);
      const rechargeL5UsdWad = isBobTest ? 0n : await safeRead("rechargePriceUsdWadByLevel", [5]);
      const levelUpL1UsdWad = isBobTest ? 0n : await safeRead("levelUpPriceUsdWadByLevel", [1]);
      const levelUpL2UsdWad = isBobTest ? 0n : await safeRead("levelUpPriceUsdWadByLevel", [2]);
      const levelUpL3UsdWad = isBobTest ? 0n : await safeRead("levelUpPriceUsdWadByLevel", [3]);
      const levelUpL4UsdWad = isBobTest ? 0n : await safeRead("levelUpPriceUsdWadByLevel", [4]);
      const rewardPoolBalance = isBobTest ? 0n : await safeRead("rewardPoolBalance");
      const totalTreasuryCollected = isBobTest ? 0n : await safeRead("totalTreasuryCollected");
      const totalBobBoughtForRewards = isBobTest ? 0n : await safeRead("totalBobBoughtForRewards");
      const buybackReserveMon = isBobTest ? 0n : await safeRead("buybackReserveMon");
      const buybackSlippageBps = isBobTest ? 0n : await safeRead("buybackSlippageBps");
      const maxSwapMonPerTx = isBobTest ? 0n : await safeRead("maxSwapMonPerTx");
      const minBobPerMonWad = isBobTest ? 0n : await safeRead("minBobPerMonWad");
      const rewardSplitBps = isBobTest ? 0n : await safeRead("rewardSplitBps");
      const treasurySplitBps = isBobTest ? 0n : await safeRead("treasurySplitBps");
      const lastRewardAccrualAt = isBobTest ? 0n : await safeRead("lastRewardAccrualAt");
      const rewardDripRateWadPerSecond = isBobTest ? 0n : await safeRead("rewardDripRateWadPerSecond");
      const mintPaused = await safeRead("mintPaused", [], false);
      const phaseDirect = await safeRead("phase", [], null);
      const phaseCurrent = await safeRead("currentPhase", [], null);
      // Live contract exposes phase(); currentPhase() may not exist on some deployments.
      // Treat phase() as authoritative and only use currentPhase() as fallback.
      const parsedPhaseDirect = Number(phaseDirect);
      const parsedPhaseCurrent = Number(phaseCurrent);
      const hasDirect = Number.isFinite(parsedPhaseDirect);
      const hasCurrent = Number.isFinite(parsedPhaseCurrent);
      const currentPhase = hasDirect ? parsedPhaseDirect : (hasCurrent ? parsedPhaseCurrent : null);
      const totalSupply = await strictRead("totalSupply");
      const maxSupply = await strictRead("MAX_SUPPLY");
      const l2 = isBobTest ? 0n : await safeRead("levelThresholdXp", [2]);
      const l3 = isBobTest ? 0n : await safeRead("levelThresholdXp", [3]);
      const l4 = isBobTest ? 0n : await safeRead("levelThresholdXp", [4]);
      const l5 = isBobTest ? 0n : await safeRead("levelThresholdXp", [5]);
      const w1 = isBobTest ? 0n : await safeRead("levelWeight", [1]);
      const w2 = isBobTest ? 0n : await safeRead("levelWeight", [2]);
      const w3 = isBobTest ? 0n : await safeRead("levelWeight", [3]);
      const w4 = isBobTest ? 0n : await safeRead("levelWeight", [4]);
      const w5 = isBobTest ? 0n : await safeRead("levelWeight", [5]);
      const walletBalance = wallet.address
        ? await publicClient.getBalance({ address: wallet.address })
        : 0n;
      if (stale()) return;

      setProtocolData((prev) => ({
        owner,
        mintPriceMonWei,
        rechargePriceUsdWadByLevel: { 1: rechargeL1UsdWad, 2: rechargeL2UsdWad, 3: rechargeL3UsdWad, 4: rechargeL4UsdWad, 5: rechargeL5UsdWad },
        levelUpPriceUsdWadByLevel: { 1: levelUpL1UsdWad, 2: levelUpL2UsdWad, 3: levelUpL3UsdWad, 4: levelUpL4UsdWad },
        rewardPoolBalance,
        totalTreasuryCollected,
        totalBobBoughtForRewards,
        buybackReserveMon,
        buybackSlippageBps,
        maxSwapMonPerTx,
        minBobPerMonWad,
        rewardSplitBps,
        treasurySplitBps,
        lastRewardAccrualAt,
        rewardDripRateWadPerSecond,
        mintPaused,
        currentPhase: Number.isFinite(currentPhase) ? currentPhase : Number(prev?.currentPhase ?? 1),
        totalSupply,
        maxSupply,
        levelThresholds: { 2: l2, 3: l3, 4: l4, 5: l5 },
        levelWeights: { 1: w1, 2: w2, 3: w3, 4: w4, 5: w5 },
      }));
      setProtocolBootstrapped(true);
      setNativeBalanceWei(walletBalance);
      if (wallet.address) {
        if (!isBobTest) {
          try {
          const stats = await readContractWithRetry({
            address: contractAddress,
            abi: NFT_ABI,
            functionName: "getOwnerRewardStats",
            args: [wallet.address],
          }, protocolReadAttempts);
          if (stale()) return;
          setOwnerRewardStats({
            pending: stats[0],
            claimed: stats[1],
            earned: stats[2],
          });
          setRewardsBootstrapped(true);
          } catch {
            if (stale()) return;
            setOwnerRewardStats({ pending: 0n, claimed: 0n, earned: 0n });
            setRewardsBootstrapped(true);
          }
        } else {
          setOwnerRewardStats({ pending: 0n, claimed: 0n, earned: 0n });
          setRewardsBootstrapped(true);
        }
      } else {
        if (stale()) return;
        setOwnerRewardStats({ pending: 0n, claimed: 0n, earned: 0n });
        setRewardsBootstrapped(true);
      }
    } catch (e) {
      if (stale()) return;
      const msg = String(e?.shortMessage || e?.message || e);
      setProtocolReadError(msg);
      setIsV5(false);
      setNativeBalanceWei(0n);
      // Keep last good values in UI to avoid loading flicker on intermittent read failures.
      setError((prev) => prev || `Protocol read failed: ${msg}`);
    }
  }, [canRead, readContractWithRetry, contractAddress, wallet.address, publicClient]);

  const loadMints = useCallback(async () => {
    if (!canRead) return;
    const seq = ++mintsLoadSeqRef.current;
    const ctx = walletContextRef.current;
    const stale = () => mintsLoadSeqRef.current !== seq || walletContextRef.current !== ctx;
    setIsLoading(true);
    try {
      try {
        const chainBase = await readContractWithRetry({ address: contractAddress, abi: NFT_ABI, functionName: "assetBaseURI", args: [] }, 2);
        if (stale()) return;
        setResolvedAssetBase(typeof chainBase === "string" && chainBase.trim() ? chainBase.trim() : ASSET_BASE);
      } catch {
        if (stale()) return;
        setResolvedAssetBase(ASSET_BASE);
      }

      const latest = await publicClient.getBlockNumber();
      const windowCandidates = [START_BLOCK, latest > 120000n ? latest - 120000n : 0n, latest > 60000n ? latest - 60000n : 0n, latest > 20000n ? latest - 20000n : 0n];
      let logs = null;
      let lastLogErr = null;
      for (const fromBlock of windowCandidates) {
        try {
          logs = await getLogsWithRetry({
            address: contractAddress,
            event: {
              type: "event",
              name: "BobMinted",
              inputs: [
                { indexed: true, name: "tokenId", type: "uint256" },
                { indexed: true, name: "owner", type: "address" },
                { indexed: false, name: "background", type: "uint16" },
                { indexed: false, name: "accessory", type: "uint16" },
                { indexed: false, name: "clothes", type: "uint16" },
                { indexed: false, name: "eyes", type: "uint16" },
                { indexed: false, name: "hat", type: "uint16" },
                { indexed: false, name: "mouth", type: "uint16" },
              ],
            },
            fromBlock,
            toBlock: "latest",
          });
          break;
        } catch (err) {
          lastLogErr = err;
        }
      }
      if (!logs) throw lastLogErr || new Error("Failed to load logs");
      const mapped = logs.map((log, i) => mapMint(log, i));
      const recentMapped = mapped.slice(-80);
      const enriched = await enrichWithTokenMetadata(recentMapped);
      if (stale()) return;
      setAllMints(enriched);
      if (enriched.length > 0) {
        const runtimeTargets = enriched.slice(0, 24);
        const entries = await Promise.all(
          runtimeTargets.map(async (m) => {
            try {
              const rt = await readContractWithRetry({
                address: contractAddress,
                abi: NFT_ABI,
                functionName: "getTokenRuntime",
                args: [BigInt(m.tokenId)],
              }, 2);
              return [
                String(m.tokenId),
                {
                  level: Number(rt[0]),
                  xp: rt[1],
                  activeUntil: rt[2],
                  lastXpSync: rt[3],
                  status: Number(rt[4]),
                  pendingReward: rt[5],
                },
              ];
            } catch {
              return [String(m.tokenId), null];
            }
          })
        );
        if (stale()) return;
        setRuntimeByTokenId(Object.fromEntries(entries.filter(([, v]) => Boolean(v))));
      } else {
        if (stale()) return;
        setRuntimeByTokenId({});
      }
    } catch {
      if (stale()) return;
      // Keep last known feed on transient log-provider issues.
    } finally {
      if (stale()) return;
      setIsLoading(false);
    }
  }, [canRead, publicClient, contractAddress, enrichWithTokenMetadata, getLogsWithRetry, readContractWithRetry]);

  const mergeRecentMints = useCallback((incoming) => {
    const nextRows = Array.isArray(incoming) ? incoming.map(mapRecentFeedMint).filter((m) => m.tokenId) : [];
    if (!nextRows.length) return;
    setAllMints((prev) => {
      const map = new Map((Array.isArray(prev) ? prev : []).map((m) => [String(m.tokenId), m]));
      for (const row of nextRows) {
        const key = String(row.tokenId);
        const existing = map.get(key);
        map.set(key, {
          ...(existing || {}),
          ...row,
          traits: row.traits || existing?.traits || null,
          metadataImage: row.metadataImage || existing?.metadataImage || "",
        });
      }
      const merged = [...map.values()].sort((a, b) => Number(b?.tokenId || 0) - Number(a?.tokenId || 0));
      return merged.slice(0, 120);
    });
  }, []);

  const loadOwnedInventory = useCallback(async () => {
    const seq = ++inventoryLoadSeqRef.current;
    const ctx = walletContextRef.current;
    const stale = () => inventoryLoadSeqRef.current !== seq || walletContextRef.current !== ctx;
    if (!canRead || !wallet.address) {
      setOwnedMints([]);
      setOwnedLoadStatus("wallet_disconnected");
      setOwnedBalanceOnChain("0");
      setOwnedTokenIdsOnChain([]);
      setInventoryBootstrapped(false);
      return;
    }
    if (!inventoryBootstrappedRef.current) {
      setOwnedLoadStatus("loading");
    }
    try {
      const balance = await readContractWithRetry({
        address: contractAddress,
        abi: NFT_ABI,
        functionName: "balanceOf",
        args: [wallet.address],
      }, 3);

      const total = Number(balance || 0n);
      if (stale()) return;
      setOwnedBalanceOnChain(String(balance || 0n));

      let tokenIds = [];
      if (Number.isFinite(total) && total > 0) {
        const pageSize = 100;
        for (let i = 0; i < total; i += pageSize) {
          const remaining = total - i;
          const count = remaining > pageSize ? pageSize : remaining;
          try {
            const page = await readContractWithRetry({
              address: contractAddress,
              abi: NFT_ABI,
              functionName: "getOwnerTokenIds",
              args: [wallet.address, BigInt(i), BigInt(count)],
            }, 3);
            tokenIds.push(...page.map((id) => String(id)));
          } catch {
            for (let j = i; j < i + count; j += 1) {
              try {
                const tokenId = await readContractWithRetry({
                  address: contractAddress,
                  abi: NFT_ABI,
                  functionName: "tokenOfOwnerByIndex",
                  args: [wallet.address, BigInt(j)],
                }, 2);
                tokenIds.push(String(tokenId));
              } catch {
                // Keep loading remaining indexes so one bad read doesn't blank inventory.
              }
            }
          }
        }
      }
      tokenIds = [...new Set(tokenIds)];
      if (stale()) return;
      setOwnedTokenIdsOnChain(tokenIds);

      if (!tokenIds.length) {
        if (stale()) return;
        setOwnedMints([]);
        setOwnedLoadStatus(total > 0 ? "onchain_index_read_incomplete" : "ok_empty");
        setInventoryBootstrapped(true);
        return;
      }

      const mintsByTokenId = new Map((allMintsRef.current || []).map((m) => [String(m.tokenId), m]));
      const shouldHydrateMetadata = tokenIds.length <= 20;
      const rows = await Promise.all(tokenIds.map(async (tokenId) => {
        let mint = mintsByTokenId.get(tokenId) || null;
        if (!mint) {
          let traits = null;
          let metadataImage = null;
          if (shouldHydrateMetadata) {
            try {
              const traitsRaw = await readContractWithRetry({
                address: contractAddress,
                abi: NFT_ABI,
                functionName: "getTraits",
                args: [BigInt(tokenId)],
              }, 2);
              traits = normalizeTraitsFromContract(traitsRaw);
            } catch {}
            try {
              const uri = await readContractWithRetry({
                address: contractAddress,
                abi: NFT_ABI,
                functionName: "tokenURI",
                args: [BigInt(tokenId)],
              }, 2);
              const parsed = parseDataUriJson(uri);
              metadataImage = typeof parsed?.image === "string" ? parsed.image : null;
            } catch {}
          }

          mint = {
            id: `owned-${tokenId}`,
            blockNumber: 0,
            txHash: "-",
            tokenId,
            owner: wallet.address,
            traits,
            metadataImage,
          };
        }
        return { mint, runtime: null };
      }));

      const runtimeById = {};
      if (stale()) return;

      setOwnedMints(rows.map((r) => r.mint));
      setRuntimeByTokenId((prev) => {
        const next = { ...prev };
        Object.entries(runtimeById).forEach(([tokenId, runtime]) => {
          next[tokenId] = runtime;
        });
        return next;
      });
      setOwnedLoadStatus("ok");
      setInventoryBootstrapped(true);
    } catch {
      if (stale()) return;
      setOwnedLoadStatus("error");
      // Keep last good holdings visible for the current wallet context only.
      setInventoryBootstrapped(true);
    }
  }, [canRead, wallet.address, readContractWithRetry, contractAddress]);

  const refreshAll = useCallback(async () => {
    await loadProtocolData();
  }, [loadProtocolData]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  useEffect(() => {
    if (!canRead) return;
    const id = window.setInterval(() => {
      refreshAll();
    }, isBobTest ? 30000 : 12000);
    return () => window.clearInterval(id);
  }, [canRead, refreshAll, isBobTest]);

  useEffect(() => {
    if (!canRead || isBobTest) return;
    loadMints();
    const pollId = window.setInterval(() => {
      loadMints();
    }, 30000);
    return () => window.clearInterval(pollId);
  }, [canRead, isBobTest, loadMints]);

  useEffect(() => {
    if (!canRead || !contractAddress) return;
    const network = "mainnet";
    const caddr = String(contractAddress || "").toLowerCase();
    let closed = false;

    const loadSnapshot = async () => {
      try {
        const qs = new URLSearchParams({
          network,
          contractAddress: caddr,
          limit: String(DASHBOARD_PAGE_SIZE),
          page: String(dashboardPage),
        });
        const resp = await fetch(`${RENDERER_API_BASE}/recent-mints?${qs.toString()}`);
        const json = await resp.json();
        if (closed) return;
        if (resp.ok && json?.ok && Array.isArray(json?.mints)) {
          if (isBobTest) {
            const mapped = json.mints.map(mapRecentFeedMint).filter((m) => m.tokenId);
            setAllMints(mapped);
            setDashboardTotalServer(Number(json?.total || mapped.length || 0));
            setDashboardTotalPagesServer(Number(json?.totalPages || 1));
          } else {
            mergeRecentMints(json.mints);
          }
        }
      } catch {
        // silent fallback to existing log polling
      }
    };

    loadSnapshot();
    const snapId = window.setInterval(loadSnapshot, 15000);

    const streamUrl = `${RENDERER_API_BASE}/recent-mints/stream?network=${encodeURIComponent(
      network
    )}&contractAddress=${encodeURIComponent(caddr)}`;
    const es = new EventSource(streamUrl);
    es.addEventListener("mint", (ev) => {
      try {
        const payload = JSON.parse(ev.data);
        if (!payload) return;
        const evtNet = String(payload.network || "").toLowerCase();
        const evtContract = String(payload.contractAddress || "").toLowerCase();
        if (evtNet && evtNet !== network) return;
        if (evtContract && evtContract !== caddr) return;
        if (isBobTest) {
          // Keep server pagination authoritative, but avoid flicker:
          // decode-time events may have traits without image URI yet.
          // Only force-refresh page on image-ready events; periodic polling
          // will pick up non-image events naturally.
          const imgSrc = resolveNftImageSrc(String(payload?.imageURI || ""));
          if (imgSrc) void loadSnapshot();
        } else {
          mergeRecentMints([payload]);
        }
      } catch {
        // ignore parse errors
      }
    });
    es.onerror = () => {
      // snapshot polling continues as fallback
    };

    return () => {
      closed = true;
      window.clearInterval(snapId);
      es.close();
    };
  }, [canRead, contractAddress, mergeRecentMints, isBobTest, dashboardPage, DASHBOARD_PAGE_SIZE]);

  useEffect(() => {
    return undefined;
  }, []);

  useEffect(() => {
    const id = window.setInterval(() => {
      setNowSec(Math.floor(Date.now() / 1000));
    }, 1000);
    return () => window.clearInterval(id);
  }, []);

  const runWrite = useCallback(async (request) => {
    if (!hasWallet) return setError("Connect wallet first.");
    const networkOk = await ensureCorrectNetwork();
    if (!networkOk) return;
    try {
      setError("");
      let req = { ...request };
      if (req.value === undefined) {
        if (req.functionName === "recharge" && req.args?.length) {
          const tokenId = BigInt(req.args[0]);
          const runtime = await readContractWithRetry({
            address: contractAddress,
            abi: NFT_ABI,
            functionName: "getTokenRuntime",
            args: [tokenId],
          }, 2);
          const level = Number(runtime[0] || 1);
          const rechargeUsdWad = await readContractWithRetry({
            address: contractAddress,
            abi: NFT_ABI,
            functionName: "rechargePriceUsdWadByLevel",
            args: [level],
          }, 2);
          req.value = rechargeUsdWad;
        }
        if (req.functionName === "levelUp" && req.args?.length) {
          const tokenId = BigInt(req.args[0]);
          const runtime = await readContractWithRetry({
            address: contractAddress,
            abi: NFT_ABI,
            functionName: "getTokenRuntime",
            args: [tokenId],
          }, 2);
          const level = Number(runtime[0] || 1);
          const levelUsdWad = await readContractWithRetry({
            address: contractAddress,
            abi: NFT_ABI,
            functionName: "levelUpPriceUsdWadByLevel",
            args: [level],
          }, 2);
          req.value = levelUsdWad;
        }
      }

      const hash = await writeWithNonceRetry(req, 3);
      setLastTxHash(hash);
      await waitForReceiptRobust(hash, 1);
      await refreshAll();
    } catch (e) {
      if (isChainMismatchError(e)) {
        const switched = await requestNetworkSwitch();
        if (switched) {
          setError("Network switched. Click again to continue.");
        } else {
          if (typeof openChainModal === "function") openChainModal();
          setError("Wrong network detected. Please switch to Monad Mainnet (143), then try again.");
        }
        return;
      }
      setError(errorText(e));
    }
  }, [hasWallet, ensureCorrectNetwork, writeWithNonceRetry, publicClient, refreshAll, readContractWithRetry, contractAddress, requestNetworkSwitch, openChainModal]);

  const refreshWhitelists = useCallback(async () => {
    if (!canRead) return;
    setWhitelistLoading(true);
    try {
      let phase1CountRaw;
      let phase2CountRaw;
      try {
        [phase1CountRaw, phase2CountRaw] = await Promise.all([
          readContractWithRetry({ address: contractAddress, abi: NFT_ABI, functionName: "getPhase1WhitelistCount", args: [] }, 2),
          readContractWithRetry({ address: contractAddress, abi: NFT_ABI, functionName: "getPhase2WhitelistCount", args: [] }, 2),
        ]);
        setWhitelistSupported(true);
      } catch {
        setWhitelistSupported(false);
        setPhase1Whitelist([]);
        setPhase2Whitelist([]);
        return;
      }
      const phase1Count = Number(phase1CountRaw || 0n);
      const phase2Count = Number(phase2CountRaw || 0n);
      const pageSize = 200;
      const loadAll = async (fnName, count) => {
        const out = [];
        for (let offset = 0; offset < count; offset += pageSize) {
          const page = await readContractWithRetry({
            address: contractAddress,
            abi: NFT_ABI,
            functionName: fnName,
            args: [BigInt(offset), BigInt(Math.min(pageSize, count - offset))],
          }, 2);
          out.push(...(Array.isArray(page) ? page : []));
        }
        return out;
      };
      const [p1, p2] = await Promise.all([
        loadAll("getPhase1Whitelist", phase1Count),
        loadAll("getPhase2Whitelist", phase2Count),
      ]);
      setPhase1Whitelist(p1);
      setPhase2Whitelist(p2);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setWhitelistLoading(false);
    }
  }, [canRead, contractAddress, readContractWithRetry]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const api = {
      contractAddress,
      setPhase: async (phase) => {
        const n = Number(phase);
        if (!Number.isFinite(n) || n < 0 || n > 255) {
          throw new Error("Phase must be a uint8 number.");
        }
        await runWrite({ address: contractAddress, abi: NFT_ABI, functionName: "setPhase", args: [n] });
      },
      setPhaseCap: async (phase, cap) => {
        const p = Number(phase);
        const c = Number(cap);
        if (!Number.isFinite(p) || !Number.isFinite(c) || p < 0 || p > 255 || c < 0 || c > 65535) {
          throw new Error("Invalid phase/cap. Expected uint8 phase and uint16 cap.");
        }
        await runWrite({ address: contractAddress, abi: NFT_ABI, functionName: "setPhaseCap", args: [p, c] });
      },
      getPhase: async () => {
        const p = await readContractWithRetry({
          address: contractAddress,
          abi: NFT_ABI,
          functionName: "phase",
        }, 2);
        return Number(p);
      },
    };
    window.bobAdmin = api;
    return () => {
      if (window.bobAdmin === api) delete window.bobAdmin;
    };
  }, [contractAddress, runWrite, readContractWithRetry]);

  useEffect(() => {
    if (!showDevConsole || !whitelistPanelOpen) return;
    void refreshWhitelists();
  }, [showDevConsole, whitelistPanelOpen, refreshWhitelists]);

  const mintRandom = useCallback(async () => {
    if (!hasWallet) return setError("Connect wallet first.");
    const networkOk = await ensureCorrectNetwork();
    if (!networkOk) return;
    await sleep(300);
    if (!isAddress(contractAddress)) return setError("Invalid contract address.");
    const qty = Math.max(1, Math.min(20, Number(mintQty || 1)));
    setIsMinting(true);
    setError("");
    setRendererSyncStatus("");
    setMintRevealOpen(true);
    setMintRevealLoading(true);
    setMintRevealItems([]);
    try {
      setRendererSyncStatus("Waiting for wallet confirmation...");
      let mintValueWei = mintUnitPriceWei;
      try {
        let liveMintPrice = await readContractWithRetry({
          address: contractAddress,
          abi: NFT_ABI,
          functionName: "mintPriceWei",
          args: [],
        }, 2);
        if (!(typeof liveMintPrice === "bigint" && liveMintPrice > 0n)) {
          liveMintPrice = await readContractWithRetry({
            address: contractAddress,
            abi: NFT_ABI,
            functionName: "mintPriceUsdWad",
            args: [],
          }, 2);
        }
        if (typeof liveMintPrice === "bigint" && liveMintPrice > 0n) {
          mintValueWei = liveMintPrice;
        }
      } catch {
        // use current UI value fallback
      }

      const totalMintValue = mintValueWei * BigInt(qty);
      const txGas = DEFAULT_MINT_GAS_LIMIT;
      try {
        const bal = await publicClient.getBalance({ address: wallet.address });
        const gasBuffer = parseUnits("0.02", 18);
        if (bal < (totalMintValue + gasBuffer)) {
          setMintRevealLoading(false);
          setMintRevealOpen(false);
          setError("Insufficient MON balance for this mint quantity.");
          return;
        }
      } catch {
        // non-blocking; continue write path
      }
      const hash = await writeWithNonceRetry({
        address: contractAddress,
        abi: NFT_ABI,
        functionName: "mintRandom",
        args: [BigInt(qty)],
        value: totalMintValue,
        gas: txGas,
      }, 3);
      setLastTxHash(hash);
      setRendererSyncStatus("Transaction submitted. Confirming on-chain...");

      const receipt = await waitForReceiptRobust(hash, 1);
      setRendererSyncStatus("Mint confirmed. Preparing NFT reveal...");

      const mintsFromTx = [];
      if (receipt) {
        for (const log of receipt.logs || []) {
          if (String(log.address).toLowerCase() !== String(contractAddress).toLowerCase()) continue;
          try {
            const decoded = decodeEventLog({ abi: NFT_ABI, data: log.data, topics: log.topics });
            if (decoded.eventName === "BobMinted") {
              mintsFromTx.push(
                mapMint({
                  transactionHash: receipt.transactionHash,
                  logIndex: log.logIndex,
                  blockNumber: receipt.blockNumber,
                  args: decoded.args,
                })
              );
            }
          } catch {}
        }
      }

      if (mintsFromTx.length > 0) {
        const network = EXPECTED_CHAIN_ID === 143 ? "mainnet" : "testnet";
        const mintedIdSet = new Set(mintsFromTx.map((m) => String(m.tokenId)));
        const backendSeedById = new Map();
        try {
          const qs = new URLSearchParams({
            network,
            contractAddress: String(contractAddress || "").toLowerCase(),
            limit: "200",
          });
          const snapResp = await fetch(`${RENDERER_API_BASE}/recent-mints?${qs.toString()}`);
          const snapJson = await snapResp.json().catch(() => ({}));
          if (snapResp.ok && snapJson?.ok && Array.isArray(snapJson?.mints)) {
            // Keep dashboard + popup fed from the same backend source of truth.
            mergeRecentMints(snapJson.mints);
            for (const row of snapJson.mints) {
              const id = String(row?.tokenId || "");
              if (!mintedIdSet.has(id)) continue;
              backendSeedById.set(id, mapRecentFeedMint(row));
            }
          }
        } catch {
          // Non-fatal: chain-derived receipt data remains fallback.
        }

        // Fast confirmed reveal first (prioritize popup UX before dashboard updates):
        // BobMinted traits are known after receipt, so we can render cards immediately
        // with layered images while tokenImageURI/IPFS finishes in the background.
        setMintRevealItems(
          mintsFromTx.map((m) => {
            const seeded = backendSeedById.get(String(m.tokenId));
            return {
              ...(seeded || m),
              metadataImage: String(seeded?.metadataImage || ""),
              revealResolved: true,
              imageReady: true,
            };
          })
        );
        setMintRevealPendingIds(mintsFromTx.map((m) => String(m.tokenId)));
        const newestMintBase = mintsFromTx[mintsFromTx.length - 1];
        const newestMintSeeded = backendSeedById.get(String(newestMintBase?.tokenId || ""));
        setLatestMint({
          ...(newestMintSeeded || newestMintBase),
          metadataImage: String(newestMintSeeded?.metadataImage || ""),
        });
        // Show cards immediately from confirmed traits; URI sync continues in background.
        setMintRevealLoading(false);
        // For BobTest dashboard, keep server pagination authoritative to avoid
        // local transient rows appearing/disappearing during refresh.
        if (!isBobTest) {
          mergeRecentMints(
            mintsFromTx.map((m) => ({
              tokenId: m.tokenId,
              owner: m.owner,
              traits: m.traits,
              imageURI: "",
              txHash: m.txHash,
              blockNumber: m.blockNumber,
              contractAddress,
              network,
            }))
          );
        }
        setRendererSyncStatus(
          mintsFromTx.length > 1
            ? `Syncing ${mintsFromTx.length} images...`
            : "Syncing image..."
        );
        let syncResults = [];
        try {
          if (batchSyncSupportedRef.current) {
            const controller = new AbortController();
            const timeoutId = window.setTimeout(() => controller.abort(), 90000);
            const resp = await fetch(`${RENDERER_API_BASE}/sync-tokens`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                network,
                contractAddress,
                tokenIds: mintsFromTx.map((m) => Number(m.tokenId)),
              }),
              signal: controller.signal,
            }).finally(() => window.clearTimeout(timeoutId));
            const json = await resp.json().catch(() => ({}));
            if (!resp.ok || json?.ok === false) {
              if (resp.status === 400 || resp.status === 404) batchSyncSupportedRef.current = false;
              throw new Error(String(json?.error || `Renderer batch sync failed (${resp.status})`));
            }
            const rows = Array.isArray(json?.results) ? json.results : [];
            syncResults = rows.map((r) => ({
              tokenId: String(r.tokenId),
              metadataImage: String(r.imageURI || ""),
            }));
          } else {
            throw new Error("Renderer batch sync not supported; using per-token sync.");
          }
        } catch {
          // Fallback path for older renderer versions that only support /sync-token.
          syncResults = await Promise.all(
            mintsFromTx.map(async (minted) => {
              try {
                const controller = new AbortController();
                const timeoutId = window.setTimeout(() => controller.abort(), 20000);
                const resp = await fetch(`${RENDERER_API_BASE}/sync-token`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ network, contractAddress, tokenId: Number(minted.tokenId) }),
                  signal: controller.signal,
                }).finally(() => window.clearTimeout(timeoutId));
                const json = await resp.json().catch(() => ({}));
                if (!resp.ok || json?.ok === false) throw new Error(String(json?.error || `Renderer sync failed (${resp.status})`));
                return { tokenId: String(minted.tokenId), metadataImage: String(json?.imageURI || "") };
              } catch (syncErr) {
                return { tokenId: String(minted.tokenId), metadataImage: "", error: String(syncErr?.message || syncErr) };
              }
            })
          );
        }

        // Rescue pass: if batch response missed some token URIs, sync those tokens
        // individually, then poll backend recent-mints (source of truth) for readiness.
        const gotUriIdSet = new Set(
          syncResults
            .filter((r) => r && r.metadataImage)
            .map((r) => String(r.tokenId))
        );
        const missingIds = [...mintedIdSet].filter((id) => !gotUriIdSet.has(id));
        if (missingIds.length > 0) {
          setRendererSyncStatus(
            `Finalizing ${missingIds.length}/${mintsFromTx.length} remaining image${missingIds.length === 1 ? "" : "s"}...`
          );
          for (const tokenIdStr of missingIds) {
            try {
              const controller = new AbortController();
              const timeoutId = window.setTimeout(() => controller.abort(), 20000);
              await fetch(`${RENDERER_API_BASE}/sync-token`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  network,
                  contractAddress,
                  tokenId: Number(tokenIdStr),
                }),
                signal: controller.signal,
              }).finally(() => window.clearTimeout(timeoutId));
            } catch {
              // Non-fatal; backend polling below may still surface the ready image URI.
            }
          }

          // Poll backend snapshot for image-ready rows for just-minted IDs.
          const refreshedById = new Map();
          const pollStart = Date.now();
          const pollMaxMs = 12000;
          while (Date.now() - pollStart < pollMaxMs) {
            try {
              const qs = new URLSearchParams({
                network,
                contractAddress: String(contractAddress || "").toLowerCase(),
                limit: "200",
                page: "1",
              });
              const snapResp = await fetch(`${RENDERER_API_BASE}/recent-mints?${qs.toString()}`);
              const snapJson = await snapResp.json().catch(() => ({}));
              if (snapResp.ok && snapJson?.ok && Array.isArray(snapJson?.mints)) {
                for (const row of snapJson.mints) {
                  const id = String(row?.tokenId || "");
                  if (!mintedIdSet.has(id)) continue;
                  const uri = String(row?.imageURI || "").trim();
                  if (uri) refreshedById.set(id, uri);
                }
              }
            } catch {
              // keep polling until timeout
            }
            const stillMissing = [...mintedIdSet].filter((id) => !refreshedById.get(id));
            if (stillMissing.length === 0) break;
            await new Promise((resolve) => window.setTimeout(resolve, 400));
          }
          syncResults = mintsFromTx.map((m) => ({
            tokenId: String(m.tokenId),
            metadataImage: refreshedById.get(String(m.tokenId)) || "",
          }));
        }

        const syncById = new Map(syncResults.map((r) => [String(r.tokenId), r]));
        const mergedItems = mintsFromTx.map((m) => ({
          ...m,
          metadataImage: syncById.get(String(m.tokenId))?.metadataImage,
          imageReady: false,
        }));

        // Fastest reveal mode: trust renderer batch response (and rescue pass if needed),
        // then reveal all cards at once immediately.
        const revealItems = mergedItems.map((it) => ({
          ...it,
          imageReady: Boolean(it.metadataImage),
          revealResolved: true,
        }));

        // Keep reveal snappy: don't block on preloading every image.
        // Fire-and-forget preloads so popup timing matches Recently Minted speed.
        const revealImageUrls = revealItems
          .map((it) => resolveNftImageSrc(it.metadataImage))
          .filter(Boolean);
        if (revealImageUrls.length > 0) {
          void Promise.allSettled(revealImageUrls.map((src) => preloadImage(src, 7000)));
        }

        setMintRevealItems(revealItems);
        // Upsert synced image URIs for non-BobTest paths; BobTest uses server-paged snapshot/SSE only.
        if (!isBobTest) {
          mergeRecentMints(
            revealItems.map((m) => ({
              tokenId: m.tokenId,
              owner: m.owner,
              traits: m.traits,
              imageURI: m.metadataImage || "",
              txHash: m.txHash,
              blockNumber: m.blockNumber,
              contractAddress,
              network: EXPECTED_CHAIN_ID === 143 ? "mainnet" : "testnet",
            }))
          );
        }

        const newestSyncedImage =
          mergedItems.find((it) => String(it.tokenId) === String(newestMintBase.tokenId))?.metadataImage || "";
        setLatestMint({
          ...(newestMintSeeded || newestMintBase),
          metadataImage: newestSyncedImage,
        });

        const failed = syncResults.filter((r) => r.error).length;
        if (failed > 0) {
          setRendererSyncStatus(`Synced ${mintsFromTx.length - failed}/${mintsFromTx.length} images.`);
        } else {
          setRendererSyncStatus(
            mintsFromTx.length > 1
              ? `All ${mintsFromTx.length} images synced.`
              : "Image synced."
          );
        }
      } else {
        setRendererSyncStatus("Mint confirmed, but receipt logs were delayed. Please refresh in a few seconds.");
        setMintRevealLoading(false);
        setMintRevealOpen(false);
      }
      try {
        await withTimeout(refreshAll(), 15000, "Refresh timed out");
      } catch {
        // Keep UI responsive even if RPC is saturated.
      }
    } catch (e) {
      setMintRevealLoading(false);
      setMintRevealOpen(false);
      logFullMintError(e, {
        contractAddress,
        qty: Math.max(1, Math.min(20, Number(mintQty || 1))),
        chainId: wallet.chainId,
      });
      if (isChainMismatchError(e)) {
        const switched = await requestNetworkSwitch();
        if (switched) {
          setError("Network switched. Click Mint again.");
        } else {
          if (typeof openChainModal === "function") openChainModal();
          setError("Wrong network detected. Please switch to Monad Mainnet (143), then mint again.");
        }
        return;
      }
      setError(errorText(e));
    } finally {
      setIsMinting(false);
    }
  }, [
    hasWallet,
    ensureCorrectNetwork,
    contractAddress,
    writeWithNonceRetry,
    publicClient,
    wallet.address,
    waitForReceiptRobust,
    mintUnitPriceWei,
    mintQty,
    readContractWithRetry,
    refreshAll,
    mergeRecentMints,
    requestNetworkSwitch,
    openChainModal,
  ]);

  useEffect(() => {
    if (!mintRevealOpen && mintRevealPendingIds.length) {
      setMintRevealPendingIds([]);
    }
  }, [mintRevealOpen, mintRevealPendingIds.length]);

  const dashboardSummary = useMemo(() => {
    const rows = ownedMints
      .map((m) => runtimeByTokenId[String(m.tokenId)])
      .filter(Boolean);
    const counts = { active: 0, inactive: 0 };
    let levelSum = 0;
    rows.forEach((r) => {
      levelSum += Number(r.level || 0);
      if (r.status === 0) counts.active += 1;
      else counts.inactive += 1;
    });
    return {
      total: rows.length,
      counts,
      pending: ownerRewardStats.pending,
      claimed: ownerRewardStats.claimed,
      earned: ownerRewardStats.earned,
      avgLevel: rows.length ? (levelSum / rows.length).toFixed(2) : "0.00",
    };
  }, [ownedMints, runtimeByTokenId, ownerRewardStats]);

  const displayedMints = useMemo(() => {
    const getTraitValue = (mint, key) => {
      if (!mint?.traits) return null;
      if (key === "head") return Number(mint.traits.hat ?? 0) + 1;
      if (key === "eyes") return Number(mint.traits.eyes ?? 0) + 1;
      if (key === "mouth") return Number(mint.traits.mouth ?? 0) + 1;
      if (key === "outfit") return Number(mint.traits.clothes ?? 0) + 1;
      if (key === "accessory") return Number(mint.traits.accessory ?? 0) + 1;
      if (key === "back") return Number(mint.traits.background ?? 0) + 1;
      return null;
    };

    const filtered = allMints.filter((m) => {
      const rt = runtimeByTokenId[String(m.tokenId)];
      const level = Number(rt?.level || 1);
      const isActive = Number(rt?.status ?? 1) === 0;

      if (filterLevel !== "any" && level !== Number(filterLevel)) return false;
      if (filterState === "active" && !isActive) return false;
      if (filterState === "inactive" && isActive) return false;

      const traitKeys = Object.keys(filterTraits);
      for (const k of traitKeys) {
        const wanted = filterTraits[k];
        if (wanted === "any") continue;
        const actual = getTraitValue(m, k);
        if (actual !== Number(wanted)) return false;
      }
      return true;
    });

    const sorted = [...filtered].sort((a, b) => {
      const ar = runtimeByTokenId[String(a.tokenId)];
      const br = runtimeByTokenId[String(b.tokenId)];
      const aId = Number(a.tokenId);
      const bId = Number(b.tokenId);
      const aLevel = Number(ar?.level || 1);
      const bLevel = Number(br?.level || 1);
      const aActive = Number(ar?.status ?? 1) === 0 ? 1 : 0;
      const bActive = Number(br?.status ?? 1) === 0 ? 1 : 0;

      switch (sortKey) {
        case "name_desc":
          return bId - aId;
        case "level_desc":
          if (bLevel !== aLevel) return bLevel - aLevel;
          return aId - bId;
        case "level_asc":
          if (aLevel !== bLevel) return aLevel - bLevel;
          return aId - bId;
        case "state_active_first":
          if (bActive !== aActive) return bActive - aActive;
          return aId - bId;
        case "state_inactive_first":
          if (aActive !== bActive) return aActive - bActive;
          return aId - bId;
        case "name_asc":
        default:
          return aId - bId;
      }
    });
    return sorted;
  }, [allMints, runtimeByTokenId, sortKey, filterLevel, filterState, filterTraits]);

  const hasRenderableTraits = (m) =>
    Boolean(
      m?.traits &&
      Number.isFinite(Number(m.traits.hat)) &&
      Number.isFinite(Number(m.traits.eyes)) &&
      Number.isFinite(Number(m.traits.mouth)) &&
      Number.isFinite(Number(m.traits.clothes)) &&
      Number.isFinite(Number(m.traits.accessory)) &&
      Number.isFinite(Number(m.traits.background))
    );

  const hasRenderableMintImage = (m) => Boolean(resolveNftImageSrc(m?.metadataImage));

  const dashboardVisibleMints = useMemo(
    () =>
      displayedMints.filter((m) => hasRenderableTraits(m) && hasRenderableMintImage(m)),
    [displayedMints]
  );

  const dashboardTotalPages = isBobTest
    ? Math.max(1, Number(dashboardTotalPagesServer || 1))
    : Math.max(1, Math.ceil(dashboardVisibleMints.length / DASHBOARD_PAGE_SIZE));
  const dashboardPageSafe = Math.min(Math.max(1, dashboardPage), dashboardTotalPages);
  const dashboardPageMints = useMemo(() => {
    if (isBobTest) return dashboardVisibleMints;
    const start = (dashboardPageSafe - 1) * DASHBOARD_PAGE_SIZE;
    return dashboardVisibleMints.slice(start, start + DASHBOARD_PAGE_SIZE);
  }, [dashboardVisibleMints, dashboardPageSafe, isBobTest]);

  useEffect(() => {
    setDashboardPage(1);
  }, [sortKey, filterLevel, filterState, filterTraits]);

  useEffect(() => {
    if (dashboardPage !== dashboardPageSafe) setDashboardPage(dashboardPageSafe);
  }, [dashboardPage, dashboardPageSafe]);

  const previewNav = useMemo(() => {
    const source = displayedMints?.length ? displayedMints : allMints;
    if (!previewMint || !source?.length) return { source: [], index: -1, hasPrev: false, hasNext: false };
    const idx = source.findIndex((m) => String(m.tokenId) === String(previewMint.tokenId));
    return {
      source,
      index: idx,
      hasPrev: idx > 0,
      hasNext: idx >= 0 && idx < source.length - 1,
    };
  }, [previewMint, displayedMints, allMints]);

  const goPreviewBy = useCallback((delta) => {
    if (!previewNav.source.length || previewNav.index < 0) return;
    const nextIndex = previewNav.index + delta;
    if (nextIndex < 0 || nextIndex >= previewNav.source.length) return;
    setPreviewMint(previewNav.source[nextIndex]);
  }, [previewNav]);

  const openMockMintReveal = useCallback((count = 1) => {
    const qty = Math.max(1, Math.min(10, Number(count || 1)));
    const seed = Date.now();
    const items = Array.from({ length: qty }, (_, i) => {
      const n = seed + i * 97;
      const pick = (m) => Number(BigInt(Math.abs(n * (m + 11))) % 5n);
      return {
        id: `mock-${seed}-${i}`,
        tokenId: String(900000 + i + 1),
        owner: wallet.address || "0x0000000000000000000000000000000000000000",
        txHash: "mock",
        blockNumber: 0,
        metadataImage: "",
        traits: {
          background: pick(1),
          accessory: pick(2),
          clothes: pick(3),
          eyes: pick(4),
          hat: pick(5),
          mouth: pick(6),
        },
      };
    });
    setMintRevealItems(items);
    setMintRevealLoading(false);
    setMintRevealOpen(true);
  }, [wallet.address]);

  const claimAllRewardsSingleTx = useCallback(async () => {
    if (!hasWallet) return setError("Connect wallet first.");
    const networkOk = await ensureCorrectNetwork();
    if (!networkOk) return;

    setError("");
    setIsClaimingAll(true);
    try {
      let livePending = 0n;
      try {
        livePending = await readContractWithRetry({
          address: contractAddress,
          abi: NFT_ABI,
          functionName: "claimableForOwner",
          args: [wallet.address],
        }, 2);
      } catch {
        // If this read fails we still attempt write once and surface any write error.
      }
      if (livePending <= 0n) {
        setError("No claimable rewards on-chain yet.");
        return;
      }

      const hash = await writeWithNonceRetry({
        address: contractAddress,
        abi: NFT_ABI,
        functionName: "claimAllRewards",
        args: [],
      }, 3);
      setLastTxHash(hash);
      try {
        await publicClient.waitForTransactionReceipt({ hash, confirmations: 1 });
      } catch {
        await sleep(3500);
      }
      await refreshAll();
    } catch (e) {
      if (isChainMismatchError(e)) {
        const switched = await requestNetworkSwitch();
        if (switched) {
          setError("Network switched. Click Claim again.");
        } else {
          if (typeof openChainModal === "function") openChainModal();
          setError("Wrong network detected. Please switch to Monad Mainnet (143), then try claim again.");
        }
        return;
      }
      setError(errorText(e));
    } finally {
      setIsClaimingAll(false);
    }
  }, [
    hasWallet,
    wallet.address,
    ensureCorrectNetwork,
    readContractWithRetry,
    writeWithNonceRetry,
    contractAddress,
    publicClient,
    refreshAll,
    requestNetworkSwitch,
    openChainModal,
  ]);

  useEffect(() => {
    if (!isDevConnected && showDevConsole) {
      setShowDevConsole(false);
    }
  }, [isDevConnected, showDevConsole]);

  useEffect(() => {
      if (!isDevConnected) {
      if (previewSoldOut) setPreviewSoldOut(false);
      if (previewStage !== "live") setPreviewStage("live");
      if (previewWalletMaxed) setPreviewWalletMaxed(false);
    }
  }, [isDevConnected, previewSoldOut, previewStage, previewWalletMaxed]);

  return (
    <>
    <Nav useSmoothScroll={false} />
    <div id="nfttest-page" className={isBobTest ? "bobtest-mode" : ""}>
      <style>{`
        @keyframes nftSyncShimmer {
          0% { background-position: 140% 0; }
          100% { background-position: -40% 0; }
        }
        #nfttest-page h1, #nfttest-page h2, #nfttest-page h3 { color: #f8fafc; margin: 0 0 10px 0; font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif; }
        #nfttest-page p, #nfttest-page div, #nfttest-page label { color: #d1d5db; }
        #nfttest-page hr { border: 0; border-top: 1px solid #334155; margin: 18px 0; }
        #nfttest-page input {
          border: 1px solid rgba(148, 163, 184, 0.45);
          border-radius: 10px;
          padding: 8px 10px;
          background: rgba(2, 6, 23, 0.95);
          color: #f8fafc;
        }
        #nfttest-page button {
          border: 1px solid #4a5f8a;
          border-radius: 999px;
          padding: 11px 18px;
          background: linear-gradient(135deg, #020617, #020617);
          color: #e5e7eb;
          font-weight: 700;
          font-size: 13px;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          cursor: pointer;
          transition: transform 150ms ease, box-shadow 150ms ease, border-color 150ms ease, background 150ms ease, color 150ms ease;
          box-shadow: 0 6px 15px rgba(0, 0, 0, 0.55);
        }
        #nfttest-page button:hover {
          transform: translateY(-1px);
          border-color: #93c5fd;
          box-shadow: 0 8px 18px rgba(0, 0, 0, 0.7);
        }
        #nfttest-page button:active {
          transform: translateY(0);
          box-shadow: 0 4px 10px rgba(0, 0, 0, 0.6);
        }
        #nfttest-page .nft-btn-primary {
          color: #020617;
          background: var(--orange);
          border-color: var(--orange);
          box-shadow: 0 8px 22px rgba(0, 0, 0, 0.7), 0 0 14px rgba(0, 0, 0, 0.4);
        }
        #nfttest-page .nft-btn-primary:hover {
          background: #ffb26b;
          border-color: #ffb26b;
          box-shadow: 0 10px 26px rgba(0, 0, 0, 0.8), 0 0 18px rgba(242, 131, 60, 0.75);
        }
        #nfttest-page button:disabled {
          opacity: 0.55;
          cursor: not-allowed;
          transform: none;
        }
        #nfttest-page .nft-card {
          border: 1px solid rgba(30, 64, 175, 0.85);
          border-radius: 18px;
          padding: 18px 18px 20px;
          background: radial-gradient(circle at 0% 0%, rgba(15, 23, 42, 0.9), #020617);
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.55);
          position: relative;
          overflow: hidden;
        }
        #nfttest-page .nft-card.nft-card--dashboard {
          border: 1px solid rgba(30, 64, 175, 0.85);
          padding-top: 10px;
          overflow: visible;
          position: relative;
          z-index: 60;
          isolation: isolate;
          background: radial-gradient(circle at 0% 0%, rgba(15, 23, 42, 0.9), #020617);
        }
        #nfttest-page .nft-card.nft-card--dashboard::before {
          background-image: none !important;
          opacity: 0 !important;
        }
        #nfttest-page .nft-card::before {
          content: "";
          position: absolute;
          inset: 0;
          border-radius: inherit;
          clip-path: inset(0 round 18px);
          background-image: var(--panel-bg-image, url('/presale/bobmoon.png'));
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          opacity: 1;
          mix-blend-mode: soft-light;
          filter: brightness(1.18) contrast(1.28) saturate(1.35);
          pointer-events: none;
        }
        #nfttest-page .nft-card > * {
          position: relative;
          z-index: 1;
        }
        #nfttest-page .nft-card-inline {
          display: grid;
          grid-template-columns: minmax(0, 1fr) 188px;
          align-items: center;
          gap: 12px;
        }
        #nfttest-page .nft-inline-action {
          width: 188px;
          min-width: 188px;
          max-width: 188px;
          height: 44px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          justify-self: end;
          align-self: center;
          margin: 0;
        }
        #nfttest-page .nft-inline-action-wrap {
          display: inline-flex;
          align-items: center;
          justify-content: flex-end;
          gap: 12px;
          justify-self: end;
        }
        #nfttest-page .nft-mint-qty {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          height: 44px;
          padding: 0;
          box-sizing: border-box;
        }
        #nfttest-page .nft-mint-step {
          width: 44px;
          min-width: 44px;
          max-width: 44px;
          height: 44px;
          min-height: 44px;
          max-height: 44px;
          padding: 0;
          line-height: 1;
          text-align: center;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          box-sizing: border-box;
          font-size: 20px !important;
          font-weight: 700;
          letter-spacing: 0.08em;
          padding-top: 0;
          padding-bottom: 0;
        }
        #nfttest-page .nft-mint-step-glyph {
          display: inline-grid;
          place-items: center;
          width: 18px;
          height: 18px;
        }
        #nfttest-page .nft-mint-step-icon {
          width: 18px;
          height: 18px;
          display: block;
        }
        #nfttest-page .nft-mint-step-icon line {
          stroke: currentColor;
          stroke-width: 2.2;
          stroke-linecap: round;
        }
        #nfttest-page .nft-mint-qty-value {
          min-width: 28px;
          text-align: center;
          font-size: 20px !important;
          font-weight: 700;
          letter-spacing: 0.08em;
          color: #cfd8ff;
          line-height: 1;
          font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
        }
        #nfttest-page .nft-dashboard-head {
          display: grid;
          grid-template-columns: 1fr auto;
          gap: 10px;
          align-items: center;
          margin-top: 0;
          margin-bottom: 10px;
          position: relative;
          z-index: 61;
          font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
          font-style: normal;
        }
        #nfttest-page .nft-dashboard-head > div:first-child {
          display: flex;
          align-items: center;
        }
        #nfttest-page .nft-dashboard-title {
          margin: 0;
          font-size: 22px;
          font-weight: 600;
          letter-spacing: 0.02em;
          line-height: 1;
          color: #dbe6ff;
          text-shadow: 0 1px 0 rgba(0, 0, 0, 0.35);
          font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
          font-style: normal;
        }
        #nfttest-page .nft-dash-controls {
          position: relative;
          z-index: 62;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }
        #nfttest-page .nft-dash-control-btn {
          border: 1px solid #233456;
          background: rgba(2, 6, 23, 0.72);
          color: #cbd5e1;
          border-radius: 999px;
          width: 84px;
          min-width: 84px;
          justify-content: center;
          font-size: 10px;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          padding: 7px 10px;
          display: inline-flex;
          align-items: center;
          line-height: 1;
          cursor: pointer;
          box-shadow: 0 6px 14px rgba(0, 0, 0, 0.35);
        }
        #nfttest-page .nft-dash-control-btn:hover {
          border-color: rgba(245, 158, 11, 0.82);
          color: #f8fafc;
        }
        #nfttest-page .nft-dash-control-btn.nft-dash-control-btn--active {
          border-color: rgba(245, 158, 11, 0.9);
          color: #f8fafc;
          background: rgba(245, 158, 11, 0.16);
          box-shadow: 0 0 0 1px rgba(245, 158, 11, 0.28) inset;
        }
        #nfttest-page .nft-dash-panel {
          position: absolute;
          top: calc(100% + 8px);
          right: 0;
          width: min(320px, 92vw);
          border: 1px solid rgba(35, 52, 86, 0.9);
          border-radius: 14px;
          background: rgba(5, 10, 20, 0.98);
          box-shadow: 0 14px 30px rgba(0, 0, 0, 0.55);
          padding: 10px;
          z-index: 9999;
          display: grid;
          gap: 8px;
          transition: opacity 180ms ease, transform 180ms ease, max-height 180ms ease, padding 180ms ease, border-width 180ms ease;
          transform-origin: top right;
          opacity: 0;
          transform: translateY(-6px) scale(0.985);
          overflow: visible;
          pointer-events: none;
          margin-top: 2px;
        }
        #nfttest-page .nft-dash-panel.nft-dash-panel--open {
          opacity: 1;
          transform: translateY(0) scale(1);
          pointer-events: auto;
        }
        #nfttest-page .nft-dash-panel-title {
          margin: 0;
          font-size: 11px;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #9fb2df;
          font-weight: 700;
        }
        #nfttest-page .nft-dash-panel-row {
          display: grid;
          gap: 6px;
        }
        #nfttest-page .nft-dash-panel-two-col {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 8px;
        }
        #nfttest-page .nft-dash-panel-row label {
          font-size: 10px;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #8ea9e6;
          font-weight: 700;
        }
        #nfttest-page .nft-dash-select {
          position: relative;
          isolation: isolate;
          z-index: 1;
        }
        #nfttest-page .nft-dash-select.nft-dash-select--open {
          z-index: 500;
        }
        #nfttest-page .nft-dash-select-trigger {
          width: 100%;
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          justify-content: unset;
          align-items: center;
          border: 1px solid rgba(37, 99, 235, 0.42);
          background: linear-gradient(180deg, rgba(7, 14, 30, 0.96), rgba(4, 9, 22, 0.96));
          color: #dbe6ff;
          border-radius: 10px;
          padding: 7px 10px;
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.01em;
          text-transform: none;
          line-height: 1.15;
          box-shadow: none;
          transform: none !important;
          transition: border-color 140ms ease, background 140ms ease, box-shadow 140ms ease;
        }
        #nfttest-page .nft-dash-select-trigger > span {
          display: flex;
          align-items: center;
          justify-content: flex-start;
          line-height: 1;
          min-height: 14px;
          transform: translateY(-1px);
        }
        #nfttest-page .nft-dash-select-trigger:hover {
          border-color: rgba(59, 130, 246, 0.72);
          background: linear-gradient(180deg, rgba(9, 18, 38, 0.98), rgba(6, 12, 28, 0.98));
          transform: none !important;
          box-shadow: none;
        }
        #nfttest-page .nft-dash-select-trigger.nft-dash-select-trigger--open {
          border-color: rgba(245, 158, 11, 0.9);
          box-shadow: none;
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
        }
        #nfttest-page .nft-dash-select-chevron {
          width: 14px;
          height: 14px;
          display: inline-flex;
          flex: 0 0 auto;
          margin-left: 8px;
          opacity: 0.9;
          transition: transform 140ms ease;
        }
        #nfttest-page .nft-dash-select-trigger--open .nft-dash-select-chevron {
          transform: rotate(180deg);
        }
        #nfttest-page .nft-dash-select-menu {
          position: absolute;
          top: calc(100% - 1px);
          left: 0;
          right: 0;
          border: 1px solid rgba(37, 99, 235, 0.5);
          border-top: 0;
          border-bottom-left-radius: 10px;
          border-bottom-right-radius: 10px;
          border-top-left-radius: 0;
          border-top-right-radius: 0;
          background: linear-gradient(180deg, rgba(6, 13, 29, 0.98), rgba(4, 9, 22, 0.98));
          box-shadow: 0 14px 24px rgba(0, 0, 0, 0.45);
          overflow: hidden;
          opacity: 0;
          transform: translateY(-4px);
          max-height: 0;
          pointer-events: none;
          transition: opacity 140ms ease, transform 140ms ease, max-height 180ms ease;
          z-index: 120;
        }
        #nfttest-page .nft-dash-select-menu.nft-dash-select-menu--open {
          opacity: 1;
          transform: translateY(0);
          max-height: 280px;
          pointer-events: auto;
          border-color: rgba(245, 158, 11, 0.9);
        }
        #nfttest-page .nft-dash-select-option {
          width: 100%;
          text-align: left;
          border: 0;
          border-radius: 0;
          background: transparent;
          color: #dbe6ff;
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.01em;
          text-transform: none;
          padding: 8px 10px;
          box-shadow: none;
          transform: none !important;
          transition: background 120ms ease, color 120ms ease;
        }
        #nfttest-page .nft-dash-select-option:hover {
          background: rgba(30, 64, 175, 0.26);
          color: #f8fafc;
          transform: none !important;
          box-shadow: none;
        }
        #nfttest-page .nft-dash-select-option.nft-dash-select-option--active {
          background: rgba(245, 158, 11, 0.16);
          color: #ffd7a4;
        }
        #nfttest-page .nft-dash-trait-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 8px;
        }
        #nfttest-page .nft-dash-filter-footer {
          display: flex;
          justify-content: flex-end;
          margin-top: 2px;
        }
        #nfttest-page .nft-dash-clear-btn {
          border: 1px solid rgba(37, 99, 235, 0.42);
          background: linear-gradient(180deg, rgba(7, 14, 30, 0.96), rgba(4, 9, 22, 0.96));
          color: #dbe6ff;
          border-radius: 8px;
          padding: 6px 9px;
          font-size: 10px;
          letter-spacing: 0.07em;
          text-transform: uppercase;
          font-weight: 700;
          cursor: pointer;
        }
        #nfttest-page .nft-dash-clear-btn:hover {
          border-color: rgba(245, 158, 11, 0.82);
          color: #f8fafc;
        }
        #nfttest-page .nft-summary-layout {
          display: grid;
          grid-template-columns: 230px minmax(0, 1fr);
          gap: 10px;
          margin-bottom: 12px;
          align-items: stretch;
          font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
          font-style: normal;
        }
        #nfttest-page .nft-summary-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 8px;
        }
        #nfttest-page .nft-summary-item {
          border: 1px solid #233456;
          border-radius: 12px;
          padding: 10px 12px;
          background: rgba(2, 6, 23, 0.55);
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          text-align: center;
          min-height: 76px;
          box-shadow: inset 0 1px 0 rgba(148, 163, 184, 0.08);
        }
        #nfttest-page .nft-summary-label {
          width: 100%;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #8ea9e6;
          margin-bottom: 2px;
          line-height: 1.2;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        #nfttest-page .nft-summary-value {
          width: 100%;
          font-size: 16px;
          font-weight: 600;
          color: #e7edff;
          line-height: 1.25;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        #nfttest-page .nft-summary-item:nth-child(4) .nft-summary-value {
          color: #fbbf24;
        }
        #nfttest-page .nft-summary-explainer {
          border: 1px solid #233456;
          border-radius: 12px;
          padding: 14px 16px;
          background: linear-gradient(160deg, rgba(6, 14, 32, 0.92), rgba(2, 6, 23, 0.72));
          box-shadow: inset 0 1px 0 rgba(148, 163, 184, 0.12);
          display: grid;
          align-content: center;
          justify-items: stretch;
          gap: 8px;
          min-width: 0;
        }
        #nfttest-page .nft-summary-explainer-title {
          margin: 0;
          font-size: 15px;
          font-weight: 800;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: var(--orange);
          text-align: center;
          text-shadow: 0 1px 0 rgba(0, 0, 0, 0.35);
        }
        #nfttest-page .nft-summary-explainer-lines {
          width: fit-content;
          max-width: 100%;
          margin: 0 auto;
          display: grid;
          gap: 8px;
          justify-self: center;
          justify-items: start;
        }
        #nfttest-page .nft-summary-explainer-line {
          margin: 0;
          font-size: 16px;
          line-height: 1.45;
          color: #e3ebff;
          text-align: left;
          width: 100%;
          font-weight: 600;
          text-shadow: 0 1px 0 rgba(0, 0, 0, 0.3);
          position: relative;
          padding-left: 14px;
          white-space: normal;
          overflow-wrap: anywhere;
          word-break: normal;
        }
        #nfttest-page .nft-summary-explainer-line::before {
          content: "";
          position: absolute;
          left: 0;
          top: 0.58em;
          width: 6px;
          height: 6px;
          border-radius: 999px;
          background: var(--orange);
          box-shadow: 0 0 10px rgba(249, 115, 22, 0.5);
        }
        #nfttest-page .nft-summary-line-accent {
          color: var(--orange);
          font-weight: 800;
        }
        #nfttest-page .nft-empty-cta {
          margin-top: 12px;
          border: 1px solid rgba(35, 52, 86, 0.85);
          border-radius: 12px;
          background: rgba(2, 6, 23, 0.48);
          padding: 14px 14px 12px;
          display: grid;
          gap: 8px;
          justify-items: center;
          text-align: center;
        }
        #nfttest-page .nft-empty-cta-title {
          margin: 0;
          font-size: 16px;
          font-weight: 700;
          color: #dbe6ff;
          line-height: 1.25;
          font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
          font-style: normal;
          letter-spacing: 0.01em;
        }
        #nfttest-page .nft-empty-cta-sub {
          margin: 0;
          font-size: 13px;
          color: #9fb2df;
          line-height: 1.35;
          font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
          font-style: normal;
          letter-spacing: 0.01em;
        }
        #nfttest-page .nft-token-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 10px;
          margin-top: 12px;
          font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
          font-style: normal;
        }
        @media (max-width: 768px) {
          #nfttest-page .nft-token-grid {
            grid-template-columns: 1fr;
          }
          #nfttest-page .nft-token-grid.nft-token-grid--bobtest {
            grid-template-columns: 1fr;
          }
          #nfttest-page .nft-token-card {
            --nft-token-media-size: 92px;
          }
          /* Bobtest narrow layout (includes narrow desktop windows): keep image + traits side-by-side */
          #nfttest-page .nft-token-card--bobtest {
            --bobtest-row-h: 26px;
            --bobtest-trait-gap: 5px;
            --bobtest-media-size: calc((6 * var(--bobtest-row-h)) + (5 * var(--bobtest-trait-gap)));
          }
          #nfttest-page .nft-token-card--bobtest .nft-bobtest-card-layout {
            grid-template-columns: var(--bobtest-media-size) minmax(0, 1fr) !important;
            align-items: stretch !important;
            gap: 8px !important;
          }
          #nfttest-page .nft-token-card--bobtest .nft-token-media {
            width: var(--bobtest-media-size) !important;
            height: var(--bobtest-media-size) !important;
          }
          #nfttest-page .nft-token-card--bobtest .nft-token-media-image {
            width: 100% !important;
            height: 100% !important;
            aspect-ratio: 1 / 1;
          }
          #nfttest-page .nft-token-card--bobtest .nft-traits-grid--bobtest {
            grid-template-columns: 1fr !important;
            grid-template-rows: repeat(6, var(--bobtest-row-h)) !important;
            gap: var(--bobtest-trait-gap) !important;
            height: var(--bobtest-media-size) !important;
          }
          #nfttest-page .nft-token-card--bobtest .nft-traits-grid--bobtest .nft-trait-item {
            min-height: var(--bobtest-row-h) !important;
            height: var(--bobtest-row-h) !important;
            padding: 0 6px !important;
            gap: 4px !important;
            flex-direction: row !important;
            align-items: center !important;
            justify-content: space-between !important;
          }
          #nfttest-page .nft-token-card--bobtest .nft-traits-grid--bobtest .nft-trait-label,
          #nfttest-page .nft-token-card--bobtest .nft-traits-grid--bobtest .nft-trait-value {
            margin: 0 !important;
            line-height: 1 !important;
            white-space: nowrap !important;
          }
          #nfttest-page .nft-token-card--bobtest .nft-trait-label {
            font-size: 8px !important;
            letter-spacing: 0.02em !important;
          }
          #nfttest-page .nft-token-card--bobtest .nft-trait-value {
            font-size: 9.5px !important;
          }
        }
        @media (max-width: 980px) {
          #nfttest-page .nft-image-modal {
            padding: max(12px, env(safe-area-inset-top)) 12px max(12px, env(safe-area-inset-bottom));
            align-items: stretch;
            justify-content: stretch;
          }
          #nfttest-page .nft-image-modal-panel {
            width: 100%;
            height: calc(100dvh - (max(12px, env(safe-area-inset-top)) + max(12px, env(safe-area-inset-bottom))));
            min-height: 0;
            max-height: calc(100dvh - (max(12px, env(safe-area-inset-top)) + max(12px, env(safe-area-inset-bottom))));
            overflow: hidden;
            border-radius: 14px;
            padding: 14px 12px;
          }
          #nfttest-page .nft-image-modal-content {
            width: min(100%, 760px);
          }
          #nfttest-page .nft-image-modal-image {
            width: min(100%, 420px);
            margin: 0 auto;
          }
          #nfttest-page .nft-image-modal-traits {
            grid-template-columns: 1fr;
            width: min(100%, 420px);
            margin-left: auto;
            margin-right: auto;
            gap: 8px;
          }
          #nfttest-page .nft-image-modal-traits .nft-trait-item {
            min-height: 42px;
          }
        }
        #nfttest-page .nft-token-card {
          --nft-token-label-h: 24px;
          --nft-token-media-size: 100px;
          border: 1px solid rgba(59, 130, 246, 0.4);
          background: rgba(2, 6, 23, 0.55);
        }
        #nfttest-page .nft-token-card.nft-token-card--fade-in {
          animation: nftCardFadeIn 220ms ease-out both;
        }
        #nfttest-page .nft-token-card::before {
          display: none;
        }
        #nfttest-page .nft-skeleton {
          position: relative;
          overflow: hidden;
        }
        #nfttest-page .nft-skeleton::after {
          content: "";
          position: absolute;
          top: 0;
          bottom: 0;
          left: -60%;
          width: 55%;
          background: linear-gradient(
            110deg,
            rgba(148, 163, 184, 0) 0%,
            rgba(148, 163, 184, 0.03) 24%,
            rgba(148, 163, 184, 0.12) 50%,
            rgba(148, 163, 184, 0.03) 76%,
            rgba(148, 163, 184, 0) 100%
          );
          filter: blur(1.5px);
          transform: translateX(0);
          animation: nftSkeletonShimmer 1.2s ease-in-out infinite;
          pointer-events: none;
        }
        #nfttest-page .nft-skel-line,
        #nfttest-page .nft-skel-pill,
        #nfttest-page .nft-skel-box {
          background: rgba(51, 65, 85, 0.62);
          border: 1px solid rgba(71, 85, 105, 0.7);
          border-radius: 8px;
        }
        #nfttest-page .nft-skel-line {
          height: 11px;
          width: 100%;
        }
        #nfttest-page .nft-skel-pill {
          height: 18px;
          width: 88px;
          border-radius: 999px;
        }
        #nfttest-page .nft-skel-box {
          border-radius: 12px;
        }
        #nfttest-page .nft-skeleton .nft-token-top {
          align-items: start;
        }
        #nfttest-page .nft-skeleton .nft-token-media-label-wrap {
          transform: translateY(-8px);
          padding: 2px 0 0;
        }
        #nfttest-page .nft-skel-pill-corner {
          position: absolute;
          top: 12px;
          right: 12px;
          z-index: 3;
          pointer-events: none;
        }
        #nfttest-page .nft-skel-traits {
          margin-top: 8px;
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 8px;
        }
        #nfttest-page .nft-skel-trait {
          height: 46px;
          border-radius: 10px;
          background: rgba(30, 41, 59, 0.56);
          border: 1px solid rgba(51, 65, 85, 0.8);
        }
        #nfttest-page .nft-skel-xp {
          margin-top: 8px;
          display: grid;
          gap: 6px;
        }
        #nfttest-page .nft-skel-xp-bar {
          height: 9px;
          border-radius: 999px;
          background: rgba(31, 41, 55, 0.92);
          border: 1px solid #233456;
        }
        @keyframes nftSkeletonShimmer {
          0% { transform: translateX(0%); }
          100% { transform: translateX(320%); }
        }
        @keyframes nftCardFadeIn {
          0% {
            opacity: 0;
            transform: translateY(2px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        #nfttest-page .nft-token-top {
          display: grid;
          grid-template-columns: var(--nft-token-media-size) minmax(0, 1fr) !important;
          gap: 10px;
          align-items: start;
        }
        #nfttest-page .nft-token-media {
          display: grid;
          grid-template-rows: var(--nft-token-label-h) var(--nft-token-media-size);
          row-gap: 0;
          justify-items: center;
          align-items: stretch;
          align-content: start;
          height: calc(var(--nft-token-label-h) + var(--nft-token-media-size));
          width: var(--nft-token-media-size);
        }
        #nfttest-page .nft-token-media-label-wrap {
          position: static;
          height: 26px;
          display: flex;
          align-items: flex-start;
          justify-content: center;
          margin: 0;
          padding: 2px 0 0;
          transform: translateY(-8px);
        }
        #nfttest-page .nft-token-media-label {
          font-size: 19px;
          font-weight: 800;
          color: #cfd8ff;
          letter-spacing: 0.01em;
          line-height: 1;
          height: auto;
          display: block;
          text-align: center;
          white-space: nowrap;
          text-shadow: none;
          font-style: normal;
          margin: 0;
          transform: none;
          padding: 0;
        }
        #nfttest-page .nft-token-media-image {
          position: static;
          margin: 0;
          width: var(--nft-token-media-size);
          height: var(--nft-token-media-size);
          align-self: start;
          justify-self: center;
        }
        #nfttest-page .nft-token-media-image-button {
          appearance: none;
          border: 0;
          background: transparent;
          padding: 0;
          margin: 0;
          width: 100%;
          height: 100%;
          border-radius: 12px;
          overflow: hidden;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: zoom-in;
        }
        #nfttest-page .nft-token-media-image-button > img,
        #nfttest-page .nft-token-media-image-button > div {
          width: 100% !important;
          height: 100% !important;
        }
        #nfttest-page .nft-image-modal {
          position: fixed;
          inset: 0;
          z-index: 9998;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          background: rgba(2, 6, 23, 0.82);
          animation: nftModalBackdropIn 180ms ease-out both;
        }
        #nfttest-page .nft-image-modal.is-closing {
          animation: nftModalBackdropOut 180ms ease-in both;
        }
        #nfttest-page .nft-image-modal-panel {
          --nft-image-modal-top-band: 52px;
          --nft-image-modal-label-h: 44px;
          position: relative;
          width: min(760px, 96vw);
          max-height: 94vh;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          border: 1px solid rgba(59, 130, 246, 0.55);
          border-radius: 14px;
          background: rgba(2, 6, 23, 0.96);
          box-shadow: 0 24px 60px rgba(0, 0, 0, 0.45);
          padding: 12px;
          padding-top: var(--nft-image-modal-top-band);
          font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
          font-style: normal;
          transform-origin: 50% 30%;
          animation: nftModalPanelIn 200ms cubic-bezier(0.2, 0.75, 0.2, 1) both;
        }
        #nfttest-page .nft-image-modal.is-closing .nft-image-modal-panel {
          animation: nftModalPanelOut 180ms ease-in both;
        }
        @keyframes nftModalBackdropIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes nftModalBackdropOut {
          from { opacity: 1; }
          to { opacity: 0; }
        }
        @keyframes nftModalPanelIn {
          from { opacity: 0; transform: translateY(10px) scale(0.985); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes nftModalPanelOut {
          from { opacity: 1; transform: translateY(0) scale(1); }
          to { opacity: 0; transform: translateY(8px) scale(0.985); }
        }
        #nfttest-page .nft-image-modal-close {
          position: absolute;
          top: 10px;
          right: 10px;
          width: 34px;
          height: 34px;
          border-radius: 999px;
          border: 1px solid rgba(71, 85, 105, 0.72);
          background: rgba(15, 23, 42, 0.78);
          color: #cbd5e1;
          font-size: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          box-shadow: 0 0 0 1px rgba(2, 6, 23, 0.35), 0 8px 18px rgba(0, 0, 0, 0.26);
          transition: border-color 140ms ease, color 140ms ease, background-color 140ms ease, transform 120ms ease, box-shadow 140ms ease, opacity 140ms ease;
          z-index: 5;
        }
        #nfttest-page .nft-image-modal-close::before,
        #nfttest-page .nft-image-modal-close::after {
          content: "";
          position: absolute;
          width: 15px;
          height: 2px;
          border-radius: 999px;
          background: currentColor;
          opacity: 0.95;
        }
        #nfttest-page .nft-image-modal-close::before {
          transform: rotate(45deg);
        }
        #nfttest-page .nft-image-modal-close::after {
          transform: rotate(-45deg);
        }
        #nfttest-page .nft-image-modal-close:hover {
          border-color: rgba(59, 130, 246, 0.7);
          color: #e2e8f0;
          background: rgba(30, 41, 59, 0.9);
          box-shadow: 0 0 0 1px rgba(59, 130, 246, 0.22), 0 10px 22px rgba(0, 0, 0, 0.34);
          transform: translateY(-1px);
        }
        #nfttest-page .nft-image-modal-close:active {
          transform: translateY(0);
          box-shadow: 0 0 0 1px rgba(59, 130, 246, 0.2), 0 6px 14px rgba(0, 0, 0, 0.28);
        }
        #nfttest-page .nft-image-modal-close:focus-visible {
          outline: 2px solid rgba(59, 130, 246, 0.72);
          outline-offset: 2px;
        }
        #nfttest-page .nft-image-modal-close-icon {
          width: 15px;
          height: 15px;
          display: block;
          stroke: currentColor;
          stroke-width: 2.1;
          stroke-linecap: round;
          fill: none;
          opacity: 0.95;
        }
        #nfttest-page .nft-image-modal-title {
          margin: 0 0 10px;
          text-align: center;
          font-size: 15px;
          font-weight: 800;
          color: #dbe6ff;
          letter-spacing: 0.02em;
        }
        #nfttest-page .nft-image-modal-image {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          margin: 0;
          aspect-ratio: 1 / 1;
          flex: 1 1 auto;
          min-height: 0;
          position: relative;
        }
        #nfttest-page .nft-image-modal-image > img,
        #nfttest-page .nft-image-modal-image > div {
          width: 100% !important;
          height: 100% !important;
          aspect-ratio: 1 / 1;
          border-radius: 14px !important;
          border: 1px solid rgba(148, 163, 184, 0.32) !important;
          box-sizing: border-box;
        }
        #nfttest-page .nft-image-modal-traits .nft-trait-label {
          font-size: 11px;
          letter-spacing: 0.04em;
          line-height: 1.1;
        }
        #nfttest-page .nft-image-modal-traits .nft-trait-value {
          font-size: 13px;
          line-height: 1.1;
          margin-top: 3px;
        }
        #nfttest-page .nft-image-nav-btn {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 86px;
          height: 28%;
          min-height: 126px;
          max-height: 182px;
          border-radius: 14px;
          border: none;
          background: transparent;
          color: rgba(148, 163, 184, 0.42);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          cursor: pointer;
          box-shadow: none;
          transition: color 180ms ease, transform 140ms ease, opacity 220ms ease;
          z-index: 10;
          opacity: 0;
          pointer-events: none;
        }
        #nfttest-page .nft-image-modal-image:hover .nft-image-nav-btn {
          opacity: 1;
          pointer-events: auto;
        }
        @media (hover: none), (pointer: coarse) {
          #nfttest-page .nft-image-nav-btn {
            opacity: 0.62;
            pointer-events: auto;
          }
          #nfttest-page .nft-image-nav-btn:active {
            opacity: 0.95;
          }
        }
        #nfttest-page .nft-image-nav-btn:hover {
          color: rgba(148, 163, 184, 0.95);
          background: transparent;
          box-shadow: none;
          transform: translateY(-50%);
        }
        #nfttest-page .nft-image-nav-btn:active {
          transform: translateY(-50%);
          box-shadow: none;
        }
        #nfttest-page .nft-image-nav-btn:disabled {
          opacity: 0.2;
          cursor: default;
          transform: translateY(-50%);
          box-shadow: none;
        }
        #nfttest-page .nft-image-nav-btn--prev { left: 6px; }
        #nfttest-page .nft-image-nav-btn--next { right: 6px; }
        #nfttest-page .nft-image-nav-icon {
          width: 120px;
          height: 120px;
          min-width: 120px;
          min-height: 120px;
          max-width: none;
          max-height: none;
          display: block;
          flex: 0 0 auto;
          stroke: none;
          fill: currentColor;
          opacity: 1;
        }
        #nfttest-page .nft-image-modal-traits {
          margin-top: 10px;
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 8px;
          width: 100%;
        }
        #nfttest-page .nft-image-modal-content {
          width: 100%;
          margin: 0 auto;
          flex: 1 1 auto;
          min-height: 0;
          display: flex;
          flex-direction: column;
        }
        #nfttest-page .nft-image-modal-content .nft-bobtest-card-head {
          position: absolute;
          left: 0;
          right: 0;
          top: calc((var(--nft-image-modal-top-band) - var(--nft-image-modal-label-h)) / 2);
          height: var(--nft-image-modal-label-h);
          margin: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          pointer-events: none;
        }
        #nfttest-page .nft-image-modal-content .nft-token-media-label {
          font-size: 26px;
          font-weight: 800;
          letter-spacing: 0.015em;
          line-height: 1;
        }
        #nfttest-page .nft-mint-reveal {
          position: fixed;
          inset: 0;
          z-index: 9997;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 16px;
          background: rgba(2, 6, 23, 0.74);
          backdrop-filter: blur(5px);
          -webkit-backdrop-filter: blur(5px);
        }
        #nfttest-page .nft-mint-reveal-panel {
          --nft-reveal-pad: 16px;
          width: min(560px, 96vw);
          border: 1px solid rgba(30, 64, 175, 0.85);
          border-radius: 18px;
          background: radial-gradient(circle at 0% 0%, rgba(15, 23, 42, 0.96), rgba(2, 6, 23, 0.98));
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.55);
          padding: 16px;
          position: relative;
          overflow: hidden;
          max-height: min(86vh, 820px);
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        #nfttest-page .nft-mint-reveal-head {
          flex: 0 0 auto;
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 10px;
          flex-wrap: wrap;
          padding-bottom: 4px;
        }
        #nfttest-page .nft-mint-reveal-scroll-shell {
          position: relative;
          flex: 1 1 auto;
          min-height: 0;
          overflow-y: auto;
          overflow-x: hidden;
          margin-right: calc(var(--nft-reveal-pad) * -1);
          padding-right: var(--nft-reveal-pad);
          scrollbar-width: thin;
          scrollbar-color: rgba(71, 105, 168, 0.78) rgba(2, 6, 23, 0.5);
        }
        #nfttest-page .nft-mint-reveal-scroll {
          height: auto;
        }
        #nfttest-page .nft-mint-reveal-scroll-shell::-webkit-scrollbar {
          width: 8px;
          -webkit-appearance: none;
        }
        #nfttest-page .nft-mint-reveal-scroll-shell::-webkit-scrollbar-button {
          width: 0;
          height: 0;
          display: none;
        }
        #nfttest-page .nft-mint-reveal-scroll-shell::-webkit-scrollbar-button:single-button,
        #nfttest-page .nft-mint-reveal-scroll-shell::-webkit-scrollbar-button:single-button:vertical:decrement,
        #nfttest-page .nft-mint-reveal-scroll-shell::-webkit-scrollbar-button:single-button:vertical:increment,
        #nfttest-page .nft-mint-reveal-scroll-shell::-webkit-scrollbar-button:single-button:horizontal:decrement,
        #nfttest-page .nft-mint-reveal-scroll-shell::-webkit-scrollbar-button:single-button:horizontal:increment {
          width: 0 !important;
          height: 0 !important;
          display: none !important;
          background: transparent !important;
        }
        #nfttest-page .nft-mint-reveal-scroll-shell::-webkit-scrollbar-button:vertical:decrement,
        #nfttest-page .nft-mint-reveal-scroll-shell::-webkit-scrollbar-button:vertical:increment,
        #nfttest-page .nft-mint-reveal-scroll-shell::-webkit-scrollbar-button:horizontal:decrement,
        #nfttest-page .nft-mint-reveal-scroll-shell::-webkit-scrollbar-button:horizontal:increment,
        #nfttest-page .nft-mint-reveal-scroll-shell::-webkit-scrollbar-button:start:decrement,
        #nfttest-page .nft-mint-reveal-scroll-shell::-webkit-scrollbar-button:end:increment {
          width: 0 !important;
          height: 0 !important;
          display: none !important;
          background: none !important;
          border: 0 !important;
          box-shadow: none !important;
        }
        #nfttest-page .nft-mint-reveal-scroll-shell::-webkit-scrollbar-track {
          background: rgba(2, 6, 23, 0.5);
          border-radius: 999px;
          margin: 10px 2px;
        }
        #nfttest-page .nft-mint-reveal-scroll-shell::-webkit-scrollbar-thumb {
          background: linear-gradient(180deg, rgba(92, 123, 182, 0.86), rgba(68, 95, 150, 0.84));
          border: 1px solid rgba(35, 52, 86, 0.95);
          border-radius: 999px;
        }
        #nfttest-page .nft-mint-reveal-scroll-shell::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(180deg, rgba(106, 140, 198, 0.9), rgba(78, 108, 165, 0.88));
        }
        #nfttest-page .nft-mint-reveal-inner {
          position: relative;
          z-index: 1;
          display: grid;
          gap: 10px;
        }
        #nfttest-page .nft-mint-reveal-title {
          margin: 0;
          font-size: 20px;
          line-height: 1.1;
          font-weight: 800;
          color: #f3f7ff;
          font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
          font-style: normal;
          letter-spacing: 0.01em;
          text-shadow: 0 1px 0 rgba(0, 0, 0, 0.18);
        }
        #nfttest-page .nft-mint-reveal-sub {
          margin: 0;
          font-size: 16px;
          line-height: 1.25;
          color: #d6e4ff;
          font-weight: 700;
          font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
          font-style: normal;
        }
        #nfttest-page .nft-mint-reveal-sub-accent {
          color: var(--orange);
          font-weight: 700;
        }
        #nfttest-page .nft-mint-reveal-loader {
          height: 8px;
          border-radius: 999px;
          border: 1px solid #233456;
          background: rgba(2, 6, 23, 0.8);
          overflow: hidden;
        }
        #nfttest-page .nft-mint-reveal-loader-fill {
          height: 100%;
          width: 42%;
          background: linear-gradient(90deg, #f97316, #facc15);
          animation: nftRevealLoad 1.1s ease-in-out infinite;
        }
        #nfttest-page .nft-mint-reveal-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 12px;
        }
        #nfttest-page .nft-mint-reveal-card {
          border: 1px solid rgba(30, 64, 175, 0.85);
          border-radius: 18px;
          background: radial-gradient(circle at 0% 0%, rgba(15, 23, 42, 0.9), #020617);
          padding: 12px;
          display: grid;
          gap: 8px;
          align-items: start;
          justify-items: stretch;
          box-shadow: 0 16px 38px rgba(0, 0, 0, 0.42);
          position: relative;
        }
        #nfttest-page .nft-mint-reveal-top-actions {
          position: absolute;
          top: 10px;
          right: 10px;
          z-index: 2;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        #nfttest-page .nft-mint-reveal-card-header {
          margin: 0 0 2px;
          font-size: 17px;
          font-weight: 700;
          color: #dbe6ff;
          text-align: center;
          font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
        }
        #nfttest-page .nft-mint-reveal-top {
          display: grid;
          grid-template-columns: 142px minmax(0, 1fr);
          gap: 12px;
          align-items: center;
        }
        #nfttest-page .nft-mint-reveal-image-wrap {
          width: 142px;
          height: 142px;
          box-sizing: border-box;
          border-radius: 12px;
          border: 1px solid rgba(148, 163, 184, 0.25);
          overflow: hidden;
          position: relative;
          background: radial-gradient(circle at 50% 0%, rgba(15, 23, 42, 0.9), rgba(2, 6, 23, 1));
        }
        #nfttest-page .nft-mint-reveal-traits {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 6px;
          width: 100%;
          height: 142px;
          grid-auto-rows: 1fr;
          align-self: center;
        }
        #nfttest-page .nft-mint-reveal-trait {
          border: 1px solid #233456;
          border-radius: 10px;
          padding: 6px 8px;
          background: rgba(15, 23, 42, 0.42);
          min-height: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
        }
        #nfttest-page .nft-mint-reveal-trait-label {
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #8ea9e6;
          line-height: 1.1;
        }
        #nfttest-page .nft-mint-reveal-trait-value {
          margin-top: 3px;
          font-size: 12px;
          font-weight: 700;
          color: #dbe6ff;
          line-height: 1.1;
        }
        #nfttest-page .nft-mint-reveal-skeleton .nft-mint-reveal-card-header {
          width: 96px;
          height: 16px;
          border-radius: 8px;
          background: rgba(51, 65, 85, 0.62);
          border: 1px solid rgba(71, 85, 105, 0.7);
          margin: 0 auto 2px;
        }
        #nfttest-page .nft-mint-reveal-skeleton .nft-mint-reveal-image-wrap,
        #nfttest-page .nft-mint-reveal-skeleton .nft-mint-reveal-trait {
          background: rgba(51, 65, 85, 0.62);
          border: 1px solid rgba(71, 85, 105, 0.7);
        }
        #nfttest-page .nft-mint-reveal-skeleton .nft-mint-reveal-trait {
          min-height: 38px;
        }
        #nfttest-page .nft-mint-reveal-card-loading {
          min-height: 190px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 18px;
        }
        #nfttest-page .nft-mint-reveal-card-loading-text {
          color: rgba(226, 232, 240, 0.9);
          font-size: 12px;
          letter-spacing: 0.02em;
          text-transform: uppercase;
        }
        #nfttest-page .nft-mint-reveal-actions {
          flex: 0 0 auto;
          display: flex;
          justify-content: flex-end;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
          padding-top: 2px;
        }
        @keyframes nftRevealLoad {
          0% { transform: translateX(-120%); }
          100% { transform: translateX(320%); }
        }
        #nfttest-page .nft-token-body {
          display: grid;
          gap: 4px;
          min-width: 0;
          /* Match media block height: label row + image row */
          padding-top: var(--nft-token-label-h);
          height: calc(var(--nft-token-label-h) + var(--nft-token-media-size));
        }
        #nfttest-page .nft-token-mainrow {
          display: block;
          min-width: 0;
          height: 100%;
        }
        #nfttest-page .nft-token-meta-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 10px;
          min-width: 0;
          align-items: start;
          height: var(--nft-token-media-size);
        }
        #nfttest-page .nft-token-stat {
          display: grid;
          gap: 5px;
          min-width: 0;
          align-content: stretch;
          background: rgba(15, 23, 42, 0.42);
          border: 1px solid rgba(35, 52, 86, 0.85);
          border-radius: 10px;
          padding: 9px 8px 8px;
          height: 100%;
          grid-template-rows: 1fr auto;
        }
        #nfttest-page .nft-token-stat.nft-token-stat--no-action {
          grid-template-rows: 1fr;
          gap: 0;
        }
        #nfttest-page .nft-token-stat-label {
          font-size: 13.5px;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #8ea9e6;
          line-height: 1;
          font-style: normal;
          display: inline-flex;
          align-items: center;
        }
        #nfttest-page .nft-token-stat-value {
          font-size: 13.5px;
          color: #dbe6ff;
          line-height: 1;
          font-weight: 700;
          font-style: normal;
          display: inline-flex;
          align-items: center;
          transform: none;
        }
        #nfttest-page .nft-token-stat-line {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          min-width: 0;
          white-space: nowrap;
          min-height: 0;
          height: 100%;
          text-align: center;
        }
        #nfttest-page .nft-token-stat.nft-token-stat--no-action .nft-token-stat-line {
          align-self: center;
        }
        #nfttest-page .nft-token-stat-line > span {
          display: inline-flex;
          align-items: center;
        }
        #nfttest-page .nft-token-actions {
          width: 100%;
          min-height: 0;
          display: flex;
          flex-direction: row;
          justify-content: stretch;
          align-items: center;
          gap: 0;
          margin-top: 0;
        }
        #nfttest-page .nft-token-actions--empty {
          display: none;
        }
        #nfttest-page .nft-token-action-btn {
          width: auto;
          min-width: 96px;
          padding: 7px 10px;
          border-radius: 999px;
          font-size: 10px;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          line-height: 1;
          color: #cfd8ff !important;
        }
        #nfttest-page .nft-token-action-btn.nft-token-action-btn--stack {
          width: 100%;
          min-width: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 2px;
          line-height: 1.05;
          min-height: 46px;
          padding: 11px 10px;
          border-radius: 10px;
          animation: nftActionBorderPulse 2.8s ease-in-out infinite;
        }
        #nfttest-page .nft-token-action-btn.nft-token-action-btn--stack:disabled {
          animation: none;
        }
        #nfttest-page .nft-token-action-btn.nft-token-action-btn--stack:hover,
        #nfttest-page .nft-token-action-btn.nft-token-action-btn--stack:focus-visible {
          border-color: rgba(245, 158, 11, 0.9) !important;
          box-shadow: 0 0 0 1px rgba(245, 158, 11, 0.24) !important;
          background: rgba(30, 41, 59, 0.9);
          transform: none;
        }
        @keyframes nftActionBorderPulse {
          0%, 100% {
            border-color: rgba(35, 52, 86, 0.95);
            box-shadow: 0 0 0 rgba(251, 191, 36, 0);
          }
          50% {
            border-color: rgba(245, 158, 11, 0.78);
            box-shadow: 0 0 0 1px rgba(245, 158, 11, 0.22);
          }
        }
        #nfttest-page .nft-token-action-title {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: #8ea9e6 !important;
        }
        #nfttest-page .nft-token-action-cost {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.04em;
          text-transform: none;
          color: #8ea9e6 !important;
          opacity: 1;
        }
        #nfttest-page .nft-token-name {
          font-size: 17px;
          font-weight: 700;
          color: #dbe6ff;
          line-height: 1.2;
          letter-spacing: 0.01em;
          text-shadow: 0 1px 0 rgba(0, 0, 0, 0.3);
          font-style: normal;
        }
        #nfttest-page .nft-token-sub {
          font-size: 12.5px;
          color: #c3d0f1;
          line-height: 1.25;
          font-style: normal;
        }
        #nfttest-page .nft-token-sub--timer {
          white-space: nowrap;
          overflow: visible;
          text-overflow: clip;
        }
        #nfttest-page .nft-status-chip {
          display: inline-flex;
          width: fit-content;
          margin-top: 2px;
          padding: 3px 9px;
          border-radius: 999px;
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.11em;
          text-transform: uppercase;
          border: 1px solid rgba(148, 163, 184, 0.45);
          color: #cbd5f5;
          background: rgba(15, 23, 42, 0.55);
          font-style: normal;
        }
        #nfttest-page .nft-status-chip.nft-status-chip--corner {
          position: static;
          margin-top: 0;
          pointer-events: none;
        }
        #nfttest-page .nft-token-top-actions {
          position: absolute;
          top: 12px;
          right: 12px;
          z-index: 3;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        #nfttest-page .nft-token-explorer-link {
          width: 22px;
          height: 22px;
          border-radius: 999px;
          border: 1px solid #233456;
          background: rgba(15, 23, 42, 0.58);
          color: #cbd5f5;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          text-decoration: none;
          transition: border-color 150ms ease, color 150ms ease, background-color 150ms ease, transform 120ms ease;
        }
        #nfttest-page .nft-token-explorer-link:hover {
          border-color: rgba(245, 158, 11, 0.9);
          color: #f8fafc;
          background: rgba(30, 41, 59, 0.82);
          transform: translateY(-1px);
        }
        #nfttest-page .nft-token-explorer-link svg {
          width: 12px;
          height: 12px;
          display: block;
          stroke: currentColor;
          stroke-width: 2;
          fill: none;
        }
        #nfttest-page .nft-status-chip.active {
          border-color: #34d399;
          color: #a7f3d0;
          background: rgba(16, 185, 129, 0.14);
        }
        #nfttest-page .nft-status-chip.inactive {
          border-color: #f87171;
          color: #fecaca;
          background: rgba(248, 113, 113, 0.14);
        }
        #nfttest-page .nft-xp-label {
          font-size: 11px;
          color: #b9c7e9;
          letter-spacing: 0.02em;
        }
        #nfttest-page .nft-traits-grid {
          margin-top: 8px;
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 8px;
        }
        #nfttest-page .nft-token-card--bobtest {
          --bobtest-trait-gap: 6px;
          --bobtest-media-size: 126px;
          position: relative;
        }
        #nfttest-page .nft-token-card--bobtest .nft-token-top-actions {
          position: absolute;
          top: 10px;
          right: 10px;
          display: inline-flex;
          justify-content: flex-end;
          margin: 0;
          z-index: 3;
        }
        #nfttest-page .nft-bobtest-card-head {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 28px;
          margin: -6px 0 8px;
        }
        #nfttest-page .nft-bobtest-card-head .nft-token-media-label {
          transform: none;
          line-height: 1;
          margin: 0;
        }
        #nfttest-page .nft-bobtest-card-layout {
          display: grid;
          grid-template-columns: var(--bobtest-media-size) minmax(0, 1fr);
          gap: 10px;
          align-items: stretch;
        }
        #nfttest-page .nft-bobtest-card-layout .nft-token-media {
          display: block;
          width: var(--bobtest-media-size);
          height: auto;
          margin-bottom: 0;
        }
          #nfttest-page .nft-bobtest-card-layout .nft-token-media-image {
            width: 100%;
            height: auto;
            aspect-ratio: 1 / 1;
          }
          #nfttest-page .nft-bobtest-card-layout .nft-token-media-image-button {
            width: 100%;
            height: 100%;
          }
          #nfttest-page .nft-bobtest-card-layout .nft-token-media-image-button > img,
          #nfttest-page .nft-bobtest-card-layout .nft-token-media-image-button > div {
            width: 100% !important;
            height: 100% !important;
            aspect-ratio: 1 / 1;
          }
        #nfttest-page .nft-traits-grid--bobtest {
          margin-top: 0;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: var(--bobtest-trait-gap);
          height: var(--bobtest-media-size);
          grid-template-rows: repeat(3, calc((var(--bobtest-media-size) - (2 * var(--bobtest-trait-gap))) / 3));
          align-content: stretch;
        }
        #nfttest-page .nft-traits-grid--bobtest .nft-trait-item {
          min-height: 0;
          height: 100%;
          padding: 4px 6px;
        }
        #nfttest-page .nft-trait-item {
          background: rgba(15, 23, 42, 0.42);
          border: 1px solid rgba(35, 52, 86, 0.85);
          border-radius: 10px;
          min-height: 46px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 6px 8px;
          text-align: center;
        }
        #nfttest-page .nft-trait-label {
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #8ea9e6;
          line-height: 1.1;
        }
        #nfttest-page .nft-trait-value {
          margin-top: 3px;
          font-size: 12px;
          font-weight: 700;
          color: #dbe6ff;
          line-height: 1.1;
        }
        #nfttest-page .nft-xp-percent {
          font-weight: 600;
          color: #fbbf24;
          letter-spacing: 0;
        }
        #nfttest-page .nft-xp-bar {
          height: 8px;
          border-radius: 999px;
          background: #1f2937;
          overflow: hidden;
          margin-top: 5px;
        }
        #nfttest-page .nft-xp-bar-fill {
          height: 100%;
          background: #f59e0b;
        }
        #nfttest-page .nft-action-row {
          margin-top: 10px;
          display: flex;
          gap: 6px;
          flex-wrap: wrap;
        }
        #nfttest-page .nft-action-row button {
          padding: 8px 13px;
          border-radius: 999px;
          font-size: 11px;
          letter-spacing: 0.08em;
        }
        #nfttest-page .nft-muted {
          color: #94a3b8 !important;
          font-size: 12px;
        }
        #nfttest-page {
          position: relative;
          background: radial-gradient(1400px 800px at 10% 0%, rgba(56, 189, 248, 0.15), transparent),
            radial-gradient(1200px 700px at 90% 10%, rgba(249, 115, 22, 0.12), transparent),
            radial-gradient(1400px 800px at 50% 90%, rgba(59, 130, 246, 0.13), transparent),
            #020617 !important;
          min-height: 100vh;
          color: #ffffff;
          width: 100%;
          max-width: 100%;
          margin: 0 auto;
          overflow-x: hidden;
          padding-top: 110px;
        }
        #nfttest-page.bobtest-mode {
          background: #020617 !important;
        }
        #nfttest-page .nfttest-hero,
        #nfttest-page .nfttest-main {
          position: relative;
          z-index: 1;
        }
        #nfttest-page *,
        #nfttest-page *::before,
        #nfttest-page *::after {
          box-sizing: border-box;
        }
        #nfttest-page .presale-bg {
          position: fixed;
          inset: 0;
          z-index: 0;
          background-image: var(--presale-bg-image);
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          filter: blur(22px) brightness(0.5) saturate(0.8);
          opacity: 0.22;
          transform: scale(1.08);
          pointer-events: none;
        }
        #nfttest-page .presale-bg::before {
          content: "";
          position: fixed;
          inset: 0;
          pointer-events: none;
          background: radial-gradient(circle at 20% -10%, rgba(15, 23, 42, 0.55), transparent 55%),
            radial-gradient(circle at 80% 110%, rgba(15, 23, 42, 0.9), transparent 55%),
            linear-gradient(to bottom, rgba(2, 6, 23, 0.95), rgba(2, 6, 23, 0.98));
          opacity: 0.9;
          mix-blend-mode: soft-light;
        }
        #nfttest-page.bobtest-mode .presale-bg::before {
          display: none !important;
        }
        #nfttest-page .nfttest-hero,
        #nfttest-page .nfttest-main {
          position: relative;
          z-index: 1;
        }
        #nfttest-page .nfttest-container {
          max-width: 1100px;
          margin: 0 auto;
          padding: 0 16px;
        }
        #nfttest-page .nfttest-hero {
          padding: 34px 0 28px;
          margin-bottom: 8px;
        }
        #nfttest-page .nfttest-hero-copy {
          min-width: 0;
          display: flex;
          align-items: center;
          align-self: center;
          justify-content: flex-start;
        }
        #nfttest-page .nfttest-main {
          padding: 0 0 80px;
        }
        #nfttest-page .nfttest-hero-grid {
          display: grid;
          grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr);
          grid-template-areas:
            "title image"
            "copy image"
            "progress image";
          gap: 32px;
          align-items: center;
        }
        #nfttest-page .nfttest-hero-grid--bobtest {
          grid-template-columns: minmax(0, 1fr);
          grid-template-areas:
            "title";
          gap: 8px;
        }
        #nfttest-page .nfttest-hero-grid--bobtest .nfttest-hero-copy {
          display: none;
        }
        #nfttest-page .nfttest-hero-title-row {
          grid-area: title;
          min-width: 0;
        }
        #nfttest-page .nfttest-hero-copy {
          grid-area: copy;
        }
        #nfttest-page .nft-hero-progress-block {
          grid-area: progress;
        }
        #nfttest-page .nfttest-hero-card {
          grid-area: image;
        }
        #nfttest-page .nfttest-kicker {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 5px 14px;
          border-radius: 999px;
          font-size: 12px;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          border: 1px solid rgba(148, 163, 184, 0.8);
          background: rgba(15, 23, 42, 0.96);
          color: #e5e7eb;
          white-space: nowrap;
          margin-bottom: 10px;
        }
        #nfttest-page .nfttest-title {
          margin: 0 0 10px;
          font-size: 60px;
          line-height: 0.95;
          letter-spacing: 1px;
          overflow-wrap: anywhere;
          font-family: var(--display-font, inherit);
          font-style: normal;
        }
        #nfttest-page .nfttest-title strong {
          color: var(--orange);
          font-size: 72px;
          -webkit-text-stroke: 2px var(--ink);
          text-stroke: 2px var(--ink);
          text-shadow: none;
          font-family: inherit;
          font-style: normal;
        }
        #nfttest-page .nfttest-title .nfttest-outline {
          color: #cfd8ff;
          font-size: 60px;
          -webkit-text-stroke: 2px #000000;
          text-stroke: 2px #000000;
          text-shadow: none;
          font-family: inherit;
          font-style: normal;
        }
        #nfttest-page .nfttest-subtitle {
          margin: 0;
          max-width: 640px;
          font-style: normal;
          position: relative;
          padding-left: 14px;
        }
        #nfttest-page .bobtest-hero-info {
          width: 100%;
          padding: 18px 18px 20px;
        }
        #nfttest-page .bobtest-hero-info-title {
          margin: 0 0 8px;
        }
        #nfttest-page .bobtest-hero-info-lines {
          display: grid;
          gap: 6px;
        }
        #nfttest-page .bobtest-hero-info-line {
          margin: 0;
        }
        #nfttest-page .bobtest-hero-info .nft-summary-explainer-title,
        #nfttest-page .bobtest-hero-info .nft-summary-explainer-line,
        #nfttest-page .bobtest-hero-info .nft-summary-line-accent {
          font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
          font-style: normal;
        }
        #nfttest-page .nfttest-subtitle::before {
          content: "";
          position: absolute;
          left: 0;
          top: 2px;
          bottom: 2px;
          width: 3px;
          border-radius: 999px;
          background: var(--orange);
        }
        #nfttest-page .nfttest-summary-lines {
          display: grid;
          gap: 6px;
          margin: 0;
          padding: 0;
          width: max-content;
          max-width: 100%;
          justify-self: start;
          justify-items: start;
          text-align: left;
          list-style: none;
          font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
          font-weight: 600;
          font-size: 18px;
          line-height: 1.35;
          letter-spacing: 0.01em;
          color: #dbe6ff;
          text-shadow: none;
          -webkit-text-stroke: 0;
          text-stroke: 0;
        }
        #nfttest-page .nfttest-summary-lines li {
          margin: 0;
          padding: 0;
          width: 100%;
          text-align: left;
        }
        #nfttest-page .nfttest-summary-accent {
          color: var(--orange);
          font-weight: 700;
        }
        #nfttest-page .progress-block {
          margin-top: 12px;
          max-width: 640px;
          font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
          font-style: normal;
        }
        #nfttest-page .progress-block .progress-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
          font-size: 15px;
          color: #c6d2ff;
          font-family: inherit;
          font-style: normal;
        }
        #nfttest-page .progress-block .progress-header .progress-percent {
          font-weight: 700;
          font-size: 16px;
          color: #fbbf24;
        }
        #nfttest-page .progress-block .progress-bar {
          position: relative;
          height: 12px;
          border-radius: 999px;
          background: rgba(15, 23, 42, 0.9);
          overflow: hidden;
          border: 1px solid #233456;
          margin-bottom: 6px;
        }
        #nfttest-page .progress-block .progress-bar .progress-fill {
          position: absolute;
          inset: 0;
          width: 0;
          background: linear-gradient(90deg, #f97316, #facc15);
          box-shadow: 0 0 12px rgba(248, 181, 0, 0.65);
          transition: width 500ms ease;
        }
        #nfttest-page .progress-block .progress-meta {
          display: flex;
          justify-content: space-between;
          font-size: 13px;
          color: #9ca3af;
          gap: 10px;
          font-family: inherit;
          font-style: normal;
        }
        #nfttest-page .bobtest-mint-row {
          margin-top: 14px;
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 10px;
        }
        #nfttest-page .bobtest-mint-card .nft-card-inline > div:first-child h3 {
          white-space: nowrap;
        }
        #nfttest-page .bobtest-mint-price-line {
          display: block;
          margin-bottom: 0;
          line-height: 1.1;
          white-space: nowrap;
        }
        #nfttest-page .bobtest-mint-label {
          display: block;
          white-space: nowrap;
        }
        #nfttest-page .nft-inline-progress {
          margin-top: 0;
          max-width: 100%;
        }
        #nfttest-page .nft-inline-progress-centered {
          min-height: 100%;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }
        #nfttest-page .nft-inline-progress .progress-header {
          margin-bottom: 6px;
          font-size: 14px;
        }
        #nfttest-page .nft-inline-progress .progress-header .progress-percent {
          font-size: 15px;
        }
        #nfttest-page .nft-inline-progress .progress-bar {
          height: 10px;
          margin-bottom: 5px;
        }
        #nfttest-page .nft-inline-progress .progress-meta {
          font-size: 12px;
        }
        #nfttest-page .nfttest-hero-actions {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          margin-top: 12px;
        }
        #nfttest-page .nft-dev-fab {
          position: fixed;
          right: 16px;
          bottom: 16px;
          z-index: 1200;
          padding: 8px 12px;
          border-radius: 999px;
          font-size: 10px;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          border: 1px solid rgba(148, 163, 184, 0.45);
          background: rgba(2, 6, 23, 0.72);
          color: #cbd5e1;
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.45);
          opacity: 0.78;
          backdrop-filter: blur(4px);
        }
        #nfttest-page .nft-dev-fab:hover {
          opacity: 1;
          border-color: rgba(251, 191, 36, 0.7);
          color: #f8fafc;
        }
        #nfttest-page .nft-status-toast-wrap {
          position: fixed;
          top: 104px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 2200;
          width: min(92vw, 720px);
          pointer-events: none;
        }
        #nfttest-page .nft-status-toast {
          pointer-events: auto;
          border: 1px solid rgba(114, 47, 55, 0.92);
          background: rgba(54, 25, 31, 0.92);
          color: rgba(255, 230, 230, 0.96);
          border-radius: 12px;
          padding: 10px 14px;
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          gap: 10px;
          align-items: center;
          box-shadow: 0 14px 34px rgba(0, 0, 0, 0.45);
          opacity: 0;
          transform: translateY(-8px) scale(0.99);
          transition: opacity 180ms ease, transform 180ms ease;
        }
        #nfttest-page .nft-status-toast.nft-status-toast--show {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
        #nfttest-page .nft-status-toast-msg {
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.01em;
          line-height: 1.4;
          font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
        }
        #nfttest-page .nft-status-toast-close {
          width: 28px;
          height: 28px;
          min-width: 28px;
          padding: 0;
          border-radius: 999px;
          border: 1px solid rgba(255, 140, 140, 0.35);
          background: rgba(127, 29, 29, 0.24);
          color: rgba(255, 230, 230, 0.96);
          font-size: 15px;
          line-height: 1;
          text-transform: none;
          letter-spacing: 0;
          box-shadow: none;
        }
        #nfttest-page .nft-status-toast-close:hover {
          border-color: rgba(255, 190, 190, 0.55);
          color: #fff5f5;
          background: rgba(127, 29, 29, 0.38);
          transform: none;
        }
        #nfttest-page .nft-dev-panel {
          scrollbar-width: thin;
          scrollbar-color: rgba(71, 105, 168, 0.78) rgba(2, 6, 23, 0.5);
        }
        #nfttest-page .nft-dev-panel::-webkit-scrollbar {
          width: 8px;
        }
        #nfttest-page .nft-dev-panel::-webkit-scrollbar-button,
        #nfttest-page .nft-dev-panel::-webkit-scrollbar-button:single-button,
        #nfttest-page .nft-dev-panel::-webkit-scrollbar-button:single-button:vertical:decrement,
        #nfttest-page .nft-dev-panel::-webkit-scrollbar-button:single-button:vertical:increment,
        #nfttest-page .nft-dev-panel::-webkit-scrollbar-button:single-button:horizontal:decrement,
        #nfttest-page .nft-dev-panel::-webkit-scrollbar-button:single-button:horizontal:increment {
          width: 0 !important;
          height: 0 !important;
          display: none !important;
          background: transparent !important;
        }
        #nfttest-page .nft-dev-panel::-webkit-scrollbar-track {
          background: rgba(2, 6, 23, 0.5);
          border-radius: 999px;
          margin: 10px 2px;
        }
        #nfttest-page .nft-dev-panel::-webkit-scrollbar-thumb {
          background: linear-gradient(180deg, rgba(92, 123, 182, 0.86), rgba(68, 95, 150, 0.84));
          border: 1px solid rgba(35, 52, 86, 0.95);
          border-radius: 999px;
        }
        #nfttest-page .nft-dev-panel::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(180deg, rgba(106, 140, 198, 0.9), rgba(78, 108, 165, 0.88));
        }
        #nfttest-page .nfttest-hero-card {
          width: 100%;
          max-width: clamp(170px, 32vw, 280px);
          margin-left: auto;
          border-radius: 20px;
          padding: 10px;
          background: radial-gradient(circle at 0% 0%, rgba(15, 23, 42, 0.98), rgba(2, 6, 23, 1));
          box-shadow: 0 0 0 1px rgba(15, 23, 42, 1), 0 22px 55px rgba(0, 0, 0, 0.85);
          border: 1px solid rgba(37, 99, 235, 0.2);
        }
        #nfttest-page .nfttest-hero-card-inner {
          position: relative;
          width: 100%;
          aspect-ratio: 1 / 1;
          border-radius: 16px;
          overflow: hidden;
          background: radial-gradient(circle at 50% 0%, rgba(15, 23, 42, 0.9), rgba(2, 6, 23, 1));
          border: 1px solid rgba(148, 163, 184, 0.25);
        }
        #nfttest-page .nfttest-hero-card-inner img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        @media (min-width: 901px) {
          #nfttest-page .nfttest-hero-grid {
            grid-template-columns: minmax(0, 1fr) minmax(220px, 300px);
            gap: 18px;
            align-items: start;
          }
          #nfttest-page .nfttest-hero-card {
            max-width: clamp(190px, 34vw, 300px);
            align-self: start;
            margin-top: -8px;
            margin-left: 0;
            justify-self: start;
          }
        }
        @media (max-width: 900px) {
          #nfttest-page .nfttest-hero {
            padding-top: 28px;
            padding-bottom: 32px;
          }
          #nfttest-page .nfttest-hero-grid {
            grid-template-columns: minmax(0, 1.1fr) minmax(0, 0.9fr);
            grid-template-areas:
              "title image"
              "copy image"
              "progress image";
            gap: 24px;
          }
          #nfttest-page .nfttest-title {
            font-size: 48px;
          }
          #nfttest-page .nfttest-subtitle {
            font-size: inherit;
          }
          #nfttest-page .nfttest-summary-lines {
            font-size: 17px;
          }
          #nfttest-page .nfttest-hero-card {
            margin-left: auto;
          }
        }
        @media (max-width: 980px) {
          #nfttest-page .nfttest-hero {
            padding-top: 20px;
            padding-bottom: 20px;
          }
          #nfttest-page .nfttest-hero-grid {
            grid-template-columns: minmax(0, 1fr) minmax(176px, 225px);
            grid-template-areas:
              "title title"
              "copy image"
              "progress progress";
            gap: 6px;
            align-items: center;
          }
          #nfttest-page .nfttest-hero-title-row {
            text-align: center;
          }
          #nfttest-page .nfttest-hero-copy {
            text-align: left;
          }
          #nfttest-page .nfttest-subtitle {
            margin-left: 0;
            margin-right: 0;
            padding-left: 12px;
          }
          #nfttest-page .nfttest-summary-lines {
            font-size: clamp(14px, 4.1vw, 18px);
            line-height: 1.26;
            justify-self: start;
            justify-items: start;
            text-align: left;
          }
          #nfttest-page .nft-hero-progress-block {
            width: 100%;
            max-width: none;
            margin: 10px 0 0;
            align-self: center;
          }
          #nfttest-page .nfttest-hero-card {
            margin: 0;
            justify-self: end;
            max-width: 225px;
          }
          #nfttest-page .nfttest-title {
            font-size: 40px;
          }
          #nfttest-page .nfttest-subtitle {
            font-size: inherit;
          }
          #nfttest-page .nft-card-inline {
            grid-template-columns: 1fr;
          }
          #nfttest-page .bobtest-mint-card .nft-card-inline {
            grid-template-columns: minmax(0, 1fr) auto;
            align-items: center;
          }
          #nfttest-page .bobtest-mint-price-line {
            display: inline-flex;
            align-items: center;
            gap: 8px;
          }
          #nfttest-page .bobtest-mint-label {
            display: inline;
          }
          #nfttest-page .bobtest-mint-label + .bobtest-mint-label {
            margin-left: 4px;
          }
          #nfttest-page .bobtest-mint-card .nft-card-inline > div:first-child {
            text-align: left;
            align-items: flex-start !important;
            justify-content: center;
          }
          #nfttest-page .bobtest-mint-card .nft-inline-action-wrap {
            width: auto;
            justify-self: end;
            gap: 10px;
          }
          #nfttest-page .bobtest-mint-card .nft-inline-action {
            width: 188px;
            min-width: 188px;
            max-width: 188px;
          }
          #nfttest-page .bobtest-mint-card .nft-mint-qty {
            height: 44px;
            gap: 4px;
          }
          #nfttest-page .bobtest-mint-card .nft-mint-step {
            width: 44px;
            min-width: 44px;
            max-width: 44px;
            height: 44px;
            min-height: 44px;
            max-height: 44px;
          }
          #nfttest-page .bobtest-mint-row {
            grid-template-columns: 1fr;
          }
          #nfttest-page .nft-card-inline > div:first-child {
            text-align: center;
            align-items: center !important;
            justify-content: center;
          }
          #nfttest-page .nft-inline-action {
            width: 100%;
            min-width: 0;
            max-width: none;
            justify-self: stretch;
          }
          #nfttest-page .nft-inline-action-wrap {
            width: 100%;
            justify-self: stretch;
            justify-content: stretch;
            gap: 8px;
          }
          #nfttest-page .nft-mint-qty {
            flex: 0 0 auto;
            gap: 2px;
            height: 40px;
          }
          #nfttest-page .nft-mint-step {
            width: 40px;
            min-width: 40px;
            max-width: 40px;
            height: 40px;
            min-height: 40px;
            max-height: 40px;
          }
          #nfttest-page .nft-mint-qty-value {
            min-width: 22px;
            font-size: 18px !important;
          }
        }
        @media (max-width: 640px) {
          #nfttest-page .nfttest-title {
            font-size: 34px;
          }
          #nfttest-page .nfttest-summary-lines {
            font-size: 15px;
          }
          #nfttest-page .nft-token-grid.nft-token-grid--bobtest {
            grid-template-columns: 1fr !important;
          }
          #nfttest-page .bobtest-mint-card .nft-inline-action-wrap {
            width: 100% !important;
            justify-self: stretch !important;
            justify-content: stretch !important;
            gap: 6px !important;
            min-width: 0;
          }
          #nfttest-page .bobtest-mint-card .nft-inline-action {
            flex: 1 1 auto;
            width: auto !important;
            min-width: 0 !important;
            max-width: none !important;
          }
          #nfttest-page .bobtest-mint-card .nft-mint-qty {
            flex: 0 0 auto;
            min-width: 0;
            gap: 2px;
            height: 40px;
          }
          #nfttest-page .bobtest-mint-card .nft-mint-step {
            width: 40px;
            min-width: 40px;
            max-width: 40px;
            height: 40px;
            min-height: 40px;
            max-height: 40px;
          }
        }
        @media (max-width: 700px) {
          #nfttest-page .nfttest-hero {
            padding-bottom: 16px;
          }
          #nfttest-page .nfttest-hero-grid {
            grid-template-columns: minmax(0, 1fr) minmax(176px, 225px);
            grid-template-areas:
              "title title"
              "copy image"
              "progress progress";
            gap: 6px;
          }
          #nfttest-page .nft-hero-progress-block {
            max-width: none;
            width: 100%;
            justify-self: start;
            margin-top: 12px;
          }
          #nfttest-page .nfttest-hero-card {
            max-width: 225px;
            justify-self: end;
          }
          #nfttest-page .nfttest-summary-lines {
            font-size: clamp(14px, 4.1vw, 18px);
            line-height: 1.26;
          }
          #nfttest-page .nfttest-summary-lines li {
            white-space: nowrap;
          }
          #nfttest-page .progress-block .progress-header {
            font-size: 13px;
          }
          #nfttest-page .progress-block .progress-header > span:first-child {
            font-size: 14px;
          }
          #nfttest-page .progress-block .progress-header .progress-percent {
            font-size: 14px;
          }
          #nfttest-page .progress-block .progress-bar {
            height: 10px;
          }
          #nfttest-page .progress-block .progress-meta {
            font-size: 11px;
            justify-content: flex-start;
          }
          #nfttest-page .progress-block .progress-meta > span:first-child {
            font-size: 13px;
            color: #c6d2ff;
            font-weight: 600;
          }
          #nfttest-page .nft-card-inline {
            grid-template-columns: 1fr;
          }
          #nfttest-page .nft-card-inline > div:first-child {
            text-align: center;
            align-items: center !important;
            justify-content: center;
          }
          #nfttest-page .nft-inline-action {
            width: 100%;
            min-width: 0;
            max-width: none;
            justify-self: stretch;
          }
          #nfttest-page .nft-inline-action-wrap {
            width: 100%;
            justify-self: stretch;
            justify-content: stretch;
            gap: 8px;
          }
          #nfttest-page .nft-mint-qty {
            flex: 0 0 auto;
            gap: 2px;
            height: 40px;
          }
          #nfttest-page .nft-mint-step {
            width: 40px;
            min-width: 40px;
            max-width: 40px;
            height: 40px;
            min-height: 40px;
            max-height: 40px;
          }
          #nfttest-page .nft-mint-qty-value {
            min-width: 22px;
            font-size: 18px !important;
          }
          #nfttest-page .nft-dashboard-head {
            grid-template-columns: 1fr auto;
            align-items: center;
          }
          #nfttest-page .nft-token-top {
            gap: 10px;
          }
          #nfttest-page .nft-token-body {
            padding-left: 4px;
          }
          #nfttest-page .nft-token-meta-grid {
            gap: 8px;
          }
          #nfttest-page .nft-token-stat {
            padding: 6px;
            gap: 4px;
            grid-template-rows: minmax(0, 1fr) auto;
          }
          #nfttest-page .nft-token-stat-label {
            font-size: 11.5px;
            letter-spacing: 0.05em;
          }
          #nfttest-page .nft-token-stat-value {
            font-size: 11.5px;
          }
          #nfttest-page .nft-token-stat-line {
            gap: 1px;
            margin: 0;
            height: 100%;
            min-height: 0;
            align-self: stretch;
            align-items: center;
            justify-content: center;
            padding: 0;
            flex-direction: column;
            white-space: normal;
          }
          #nfttest-page .nft-token-actions {
            width: auto;
            min-height: 0;
            gap: 10px;
            margin-top: 0;
          }
          #nfttest-page .nft-token-action-btn {
            font-size: 13px;
            min-width: 116px;
            padding: 10px 14px;
          }
          #nfttest-page .nft-token-action-btn.nft-token-action-btn--stack {
            min-width: 0;
            width: 100%;
            padding: 8px 8px;
          }
          #nfttest-page .nft-token-action-title {
            font-size: 11px;
            font-weight: 700;
            letter-spacing: 0.04em;
          }
          #nfttest-page .nft-token-action-cost {
            font-size: 11px;
            font-weight: 700;
            letter-spacing: 0.04em;
          }
          #nfttest-page .nft-summary-item {
            min-height: 70px;
            padding: 8px 10px;
          }
          #nfttest-page .nft-summary-label {
            font-size: 10px;
          }
          #nfttest-page .nft-summary-value {
            font-size: 14px;
          }
          #nfttest-page .nft-summary-layout {
            grid-template-columns: 1fr;
            gap: 8px;
          }
          #nfttest-page .nft-summary-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
          #nfttest-page .nft-summary-explainer {
            padding: 10px 12px;
          }
          #nfttest-page .nft-summary-explainer-title {
            font-size: 13px;
          }
          #nfttest-page .nft-summary-explainer-line {
            font-size: 15px;
          }
          #nfttest-page .nft-trait-label {
            font-size: 9px;
            letter-spacing: 0.04em;
          }
          #nfttest-page .nft-trait-value {
            font-size: 10.5px;
          }
          #nfttest-page .nft-traits-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
          #nfttest-page .nft-traits-grid--bobtest {
            grid-template-columns: 1fr;
            --bobtest-row-h: 26px;
            height: var(--bobtest-media-size);
            grid-template-rows: repeat(6, var(--bobtest-row-h));
          }
          #nfttest-page .nft-bobtest-card-layout {
            grid-template-columns: var(--bobtest-media-size) minmax(0, 1fr);
            gap: 8px;
            align-items: stretch;
          }
          #nfttest-page .nft-bobtest-card-layout .nft-token-media {
            width: var(--bobtest-media-size);
            height: var(--bobtest-media-size);
          }
          #nfttest-page .nft-traits-grid--bobtest .nft-trait-item {
            padding: 0 8px;
            min-height: var(--bobtest-row-h);
          }
          #nfttest-page .nft-token-card--bobtest .nft-trait-label {
            line-height: 1;
          }
          #nfttest-page .nft-token-card--bobtest .nft-trait-value {
            margin-top: 1px;
            line-height: 1;
          }
          #nfttest-page .nft-skel-traits {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
        }
        @media (max-width: 430px) {
          #nfttest-page .nfttest-hero {
            padding-top: 0;
          }
          #nfttest-page .nfttest-hero-grid {
            grid-template-columns: minmax(0, 1fr);
            grid-template-areas:
              "title"
              "copy"
              "image"
              "progress";
            gap: 10px;
          }
          #nfttest-page .nfttest-hero-copy {
            text-align: center;
          }
          #nfttest-page .nfttest-subtitle {
            display: inline-block;
            text-align: left;
            padding-left: 10px;
            margin-left: auto;
            margin-right: auto;
          }
          #nfttest-page .nfttest-hero-card {
            width: 100%;
            max-width: min(81vw, 288px);
            justify-self: center;
            margin-top: 12px;
          }
          #nfttest-page .nfttest-summary-lines {
            font-size: clamp(15px, 4.9vw, 20px);
            line-height: 1.3;
            justify-self: center;
            justify-items: start;
            text-align: left;
          }
          #nfttest-page .nfttest-summary-lines li {
            text-align: left;
          }
        }
        @media (max-width: 460px) {
          #nfttest-page .nft-token-card--bobtest {
            --bobtest-row-h: 26px;
            --bobtest-trait-gap: 5px;
            --bobtest-media-size: calc((6 * var(--bobtest-row-h)) + (5 * var(--bobtest-trait-gap)));
          }
          #nfttest-page .nft-status-toast-wrap {
            top: 92px;
            width: min(94vw, 560px);
          }
          #nfttest-page .nft-token-grid.nft-token-grid--bobtest {
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 8px;
          }
          #nfttest-page .nft-status-toast {
            padding: 9px 11px;
            border-radius: 11px;
            gap: 8px;
          }
          #nfttest-page .nft-status-toast-msg {
            font-size: 12.5px;
          }
          #nfttest-page .nft-token-card {
            --nft-token-media-size: 84px;
          }
          #nfttest-page .nfttest-summary-lines {
            font-size: clamp(14px, 4.6vw, 18px);
            line-height: 1.26;
          }
          #nfttest-page .nfttest-subtitle {
            padding-left: 10px;
          }
          #nfttest-page .nfttest-subtitle::before {
            width: 2px;
          }
          #nfttest-page .nft-token-top {
            gap: 8px;
          }
          #nfttest-page .nft-token-stat-label {
            font-size: 10.5px;
          }
          #nfttest-page .nft-token-stat-value {
            font-size: 10.5px;
          }
          #nfttest-page .nft-token-action-title,
          #nfttest-page .nft-token-action-cost {
            font-size: 10px;
          }
          #nfttest-page .nft-token-top-actions {
            top: 10px;
            right: 10px;
            gap: 5px;
          }
          #nfttest-page .nft-token-explorer-link {
            width: 20px;
            height: 20px;
          }
          #nfttest-page .nft-token-explorer-link svg {
            width: 11px;
            height: 11px;
          }
          #nfttest-page .nft-traits-grid--bobtest {
            height: var(--bobtest-media-size) !important;
            grid-template-columns: 1fr !important;
            grid-template-rows: repeat(6, var(--bobtest-row-h)) !important;
            gap: var(--bobtest-trait-gap) !important;
          }
          #nfttest-page .nft-token-card--bobtest .nft-bobtest-card-layout {
            grid-template-columns: var(--bobtest-media-size) minmax(0, 1fr) !important;
            align-items: stretch !important;
            gap: 8px !important;
          }
          #nfttest-page .nft-token-card--bobtest .nft-token-media {
            width: var(--bobtest-media-size) !important;
            height: var(--bobtest-media-size) !important;
          }
          #nfttest-page .nft-token-card--bobtest .nft-token-media-image {
            width: 100% !important;
            height: 100% !important;
            aspect-ratio: 1 / 1;
          }
          #nfttest-page .nft-token-card--bobtest .nft-traits-grid--bobtest .nft-trait-item {
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
            gap: 4px;
            padding: 0 6px;
            min-height: var(--bobtest-row-h) !important;
            height: var(--bobtest-row-h) !important;
          }
          #nfttest-page .nft-token-card--bobtest .nft-traits-grid--bobtest .nft-trait-label,
          #nfttest-page .nft-token-card--bobtest .nft-traits-grid--bobtest .nft-trait-value {
            margin: 0;
            line-height: 1;
            white-space: nowrap;
          }
          #nfttest-page .nft-traits-grid--bobtest .nft-trait-item {
            padding: 3px 5px;
          }
          #nfttest-page .nft-token-card--bobtest .nft-trait-label {
            font-size: 8px;
            letter-spacing: 0.02em;
          }
          #nfttest-page .nft-token-card--bobtest .nft-trait-value {
            font-size: 9.5px;
          }
          #nfttest-page .nft-image-modal {
            padding: max(10px, env(safe-area-inset-top)) 10px max(10px, env(safe-area-inset-bottom));
            align-items: stretch;
            justify-content: stretch;
          }
          #nfttest-page .nft-image-modal-panel {
            width: 100%;
            height: calc(100dvh - (max(10px, env(safe-area-inset-top)) + max(10px, env(safe-area-inset-bottom))));
            min-height: 0;
            max-height: calc(100dvh - (max(10px, env(safe-area-inset-top)) + max(10px, env(safe-area-inset-bottom))));
            overflow: hidden;
            border-radius: 12px;
            padding: 12px 10px;
            padding-top: 50px;
          }
          #nfttest-page .nft-image-modal-close {
            top: 8px;
            right: 8px;
            width: 28px;
            height: 28px;
          }
          #nfttest-page .nft-image-modal-close::before,
          #nfttest-page .nft-image-modal-close::after {
            width: 13px;
          }
          #nfttest-page .nft-image-nav-btn {
            width: 74px;
            height: 26%;
            min-height: 108px;
            max-height: 152px;
            border-radius: 14px;
          }
          #nfttest-page .nft-image-nav-icon {
            width: 104px;
            height: 104px;
            min-width: 104px;
            min-height: 104px;
          }
          #nfttest-page .nft-image-modal-close-icon {
            width: 13px;
            height: 13px;
          }
          #nfttest-page .nft-image-modal-image {
            width: 100%;
          }
          #nfttest-page .nft-image-modal-image {
            width: 100%;
            max-width: none;
          }
          #nfttest-page .nft-image-modal-traits {
            grid-template-columns: 1fr;
            width: min(100%, 360px);
            margin-left: auto;
            margin-right: auto;
            gap: 8px;
          }
          #nfttest-page .nft-image-modal-image {
            width: min(100%, 360px);
            margin: 0 auto;
          }
          #nfttest-page .nft-image-modal-traits .nft-trait-item {
            min-height: 40px;
          }
          #nfttest-page .nft-mint-reveal-panel {
            --nft-reveal-pad: 12px;
            width: min(430px, 96vw);
            padding: 12px;
          }
          #nfttest-page .nft-mint-reveal-top {
            grid-template-columns: 1fr;
            justify-items: center;
          }
          #nfttest-page .nft-mint-reveal-top > div:first-child {
            width: 100%;
          }
          #nfttest-page .nft-mint-reveal-image-wrap {
            width: 100%;
            max-width: none;
            height: auto;
            aspect-ratio: 1 / 1;
            margin-inline: auto;
          }
          #nfttest-page .nft-mint-reveal-image-wrap img {
            width: 100% !important;
            height: auto !important;
            aspect-ratio: 1 / 1;
            object-fit: contain;
          }
          #nfttest-page .nft-mint-reveal-traits {
            width: 100%;
            max-width: none;
            margin-inline: auto;
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
        }
        @media (max-width: 390px) {
          #nfttest-page .nfttest-summary-lines {
            font-size: clamp(13px, 4.3vw, 16px);
            line-height: 1.24;
          }
        }
        /* Final bobtest mobile override: only active at <=768px */
        @media (max-width: 768px) {
          #nfttest-page .nft-token-card.nft-token-card--bobtest.nft-skeleton {
            border-radius: 14px;
            overflow: hidden;
            isolation: isolate;
          }
          #nfttest-page .nft-token-card.nft-token-card--bobtest.nft-skeleton::after {
            left: -72%;
            width: 68%;
            top: -1px;
            bottom: -1px;
            filter: blur(1px);
            background: linear-gradient(
              110deg,
              rgba(148, 163, 184, 0) 0%,
              rgba(148, 163, 184, 0.025) 28%,
              rgba(148, 163, 184, 0.11) 50%,
              rgba(148, 163, 184, 0.025) 72%,
              rgba(148, 163, 184, 0) 100%
            );
            animation-duration: 1.35s;
          }
          #nfttest-page .nft-token-card.nft-token-card--bobtest {
            --bobtest-row-h: 24px !important;
            --bobtest-trait-gap: 6px !important;
            --bobtest-media-size: clamp(92px, 30vw, 118px) !important;
            min-height: auto !important;
          }
          #nfttest-page .nft-token-card.nft-token-card--bobtest .nft-bobtest-card-layout {
            display: grid !important;
            grid-template-columns: var(--bobtest-media-size) minmax(0, 1fr) !important;
            align-items: stretch !important;
            gap: 8px !important;
            justify-content: stretch !important;
            width: 100% !important;
          }
          #nfttest-page .nft-token-card.nft-token-card--bobtest .nft-token-media {
            width: 100% !important;
            min-height: var(--bobtest-media-size) !important;
            height: var(--bobtest-media-size) !important;
            max-height: var(--bobtest-media-size) !important;
            aspect-ratio: 1 / 1 !important;
            align-self: start !important;
            justify-self: start !important;
          }
          #nfttest-page .nft-token-card.nft-token-card--bobtest .nft-token-media-image,
          #nfttest-page .nft-token-card.nft-token-card--bobtest .nft-token-media-image-button {
            width: 100% !important;
            height: 100% !important;
            aspect-ratio: 1 / 1 !important;
          }
          #nfttest-page .nft-token-card.nft-token-card--bobtest .nft-traits-grid--bobtest {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            grid-template-rows: repeat(3, minmax(0, 1fr)) !important;
            gap: var(--bobtest-trait-gap) !important;
            min-height: var(--bobtest-media-size) !important;
            height: var(--bobtest-media-size) !important;
            max-height: var(--bobtest-media-size) !important;
            align-self: start !important;
            margin-top: 0 !important;
          }
          #nfttest-page .nft-token-card.nft-token-card--bobtest .nft-traits-grid--bobtest .nft-trait-item {
            min-height: 0 !important;
            height: 100% !important;
            padding: 4px 6px !important;
            gap: 0 !important;
            flex-direction: column !important;
            align-items: center !important;
            justify-content: center !important;
          }
          #nfttest-page .nft-token-card.nft-token-card--bobtest .nft-traits-grid--bobtest .nft-trait-label,
          #nfttest-page .nft-token-card.nft-token-card--bobtest .nft-traits-grid--bobtest .nft-trait-value {
            margin: 0 !important;
            line-height: 1 !important;
            white-space: normal !important;
            text-align: center !important;
          }
          #nfttest-page .nft-token-card.nft-token-card--bobtest .nft-traits-grid--bobtest .nft-trait-label {
            font-size: 10px !important;
            letter-spacing: 0.04em !important;
            line-height: 1.1 !important;
          }
          #nfttest-page .nft-token-card.nft-token-card--bobtest .nft-traits-grid--bobtest .nft-trait-value {
            font-size: 12px !important;
            margin-top: 4px !important;
            line-height: 1.1 !important;
          }
        }
        /* Lock desktop/tablet NFT card styling to >=769px */
        @media (min-width: 769px) {
          #nfttest-page .nft-token-grid.nft-token-grid--bobtest {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            gap: 10px !important;
          }
          #nfttest-page .nft-token-card--bobtest {
            --bobtest-trait-gap: 6px !important;
            --bobtest-media-size: 126px !important;
          }
          #nfttest-page .nft-token-card--bobtest .nft-bobtest-card-layout {
            grid-template-columns: var(--bobtest-media-size) minmax(0, 1fr) !important;
            gap: 10px !important;
            align-items: stretch !important;
          }
          #nfttest-page .nft-token-card--bobtest .nft-token-media {
            width: var(--bobtest-media-size) !important;
            height: var(--bobtest-media-size) !important;
            min-height: var(--bobtest-media-size) !important;
            max-height: var(--bobtest-media-size) !important;
            aspect-ratio: 1 / 1 !important;
          }
          #nfttest-page .nft-token-card--bobtest .nft-traits-grid--bobtest {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            grid-template-rows: repeat(3, calc((var(--bobtest-media-size) - (2 * var(--bobtest-trait-gap))) / 3)) !important;
            height: var(--bobtest-media-size) !important;
            min-height: var(--bobtest-media-size) !important;
            max-height: var(--bobtest-media-size) !important;
            gap: var(--bobtest-trait-gap) !important;
          }
          #nfttest-page .nft-token-card--bobtest .nft-traits-grid--bobtest .nft-trait-item {
            flex-direction: column !important;
            align-items: center !important;
            justify-content: center !important;
            gap: 0 !important;
          }
          #nfttest-page .nft-token-card--bobtest .nft-traits-grid--bobtest .nft-trait-label,
          #nfttest-page .nft-token-card--bobtest .nft-traits-grid--bobtest .nft-trait-value {
            text-align: center !important;
            white-space: normal !important;
            margin: 0 !important;
            line-height: 1.1 !important;
          }
        }
        /* Final popup narrow-screen override (kept last to win cascade) */
        @media (max-width: 1400px) {
          #nfttest-page .nft-image-modal-panel .nft-image-modal-content {
            width: 100% !important;
            margin: 0 auto !important;
            justify-content: flex-start !important;
          }
          #nfttest-page .nft-image-modal-panel .nft-image-modal-image {
            width: 100% !important;
            max-width: none !important;
            margin: 0 auto !important;
            aspect-ratio: 1 / 1 !important;
            flex: 0 0 auto !important;
          }
          #nfttest-page .nft-image-modal-panel .nft-image-modal-traits {
            width: 100% !important;
            max-width: none !important;
            margin: 10px auto 0 !important;
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            grid-template-rows: repeat(3, minmax(0, 1fr)) !important;
            gap: 8px !important;
            flex: 1 1 auto !important;
            min-height: 0 !important;
            align-content: stretch !important;
          }
          #nfttest-page .nft-image-modal-panel .nft-image-modal-traits .nft-trait-item {
            min-height: 0 !important;
            height: 100% !important;
            padding: 6px 8px !important;
          }
          #nfttest-page .nft-image-modal-panel .nft-image-modal-traits .nft-trait-label {
            font-size: 12px !important;
            line-height: 1.1 !important;
          }
          #nfttest-page .nft-image-modal-panel .nft-image-modal-traits .nft-trait-value {
            font-size: 14px !important;
            line-height: 1.1 !important;
            margin-top: 4px !important;
          }
        }
      `}</style>
      <div className="presale-bg" aria-hidden="true" style={{ "--presale-bg-image": 'url("/presale/bobchest.webp")' }} />
      {!isBobTest ? (
      <header className="nfttest-hero">
        <div className={`nfttest-container nfttest-hero-grid ${isBobTest ? "nfttest-hero-grid--bobtest" : ""}`}>
          <div className="nfttest-hero-title-row">
            <h1 className="nfttest-title">
              <strong>BOB</strong> <span className="nfttest-outline">NFT System</span>
            </h1>
          </div>
          <div className="nfttest-hero-copy">
            {!isBobTest ? (
              <div className="nfttest-subtitle">
                <ul className="nfttest-summary-lines">
                  <li><span className="nfttest-summary-accent">Mint and Hold</span> Bob NFTs to earn <span className="nfttest-summary-accent">BOB</span></li>
                  <li><span className="nfttest-summary-accent">Level Up</span> Bob NFTs to earn more <span className="nfttest-summary-accent">BOB</span></li>
                  <li><span className="nfttest-summary-accent">Recharge</span> Bob NFTs to keep earning <span className="nfttest-summary-accent">BOB</span></li>
                </ul>
              </div>
            ) : null}
          </div>
          {!isBobTest ? (
            <div className="progress-block nft-hero-progress-block">
              <div className="progress-header">
                <span>Minted Progress</span>
                <span className="progress-percent">{mintProgressPct.toFixed(1)}%</span>
              </div>
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${mintProgressPct}%` }} />
              </div>
              <div className="progress-meta">
                <span>Minted: {mintedSupply.toLocaleString()} / {maxSupply.toLocaleString()}</span>
              </div>
            </div>
          ) : null}
          {!isBobTest ? (
            <div className="nfttest-hero-card">
              <div className="nfttest-hero-card-inner">
                <img src="/presale/bobchest.webp" alt="BOB NFT" />
              </div>
            </div>
          ) : null}
        </div>
      </header>
      ) : null}
      <main className={isBobTest ? "nfttest-main nfttest-main--bobtest" : "nfttest-main"}>
      <div className={isBobTest ? "nfttest-container nfttest-container--bobtest" : "nfttest-container"} style={{ paddingBottom: 40 }}>
      <div>

        {error ? (
          <div className="nft-status-toast-wrap" aria-live="polite" aria-atomic="true">
            <div className={`nft-status-toast ${toastVisible ? "nft-status-toast--show" : ""}`}>
              <div className="nft-status-toast-msg">{displayError || error}</div>
              <button
                type="button"
                className="nft-status-toast-close"
                aria-label="Dismiss status message"
                onClick={() => {
                  setToastVisible(false);
                  setError("");
                }}
              >
                ×
              </button>
            </div>
          </div>
        ) : null}

        {isBobTest ? (
          <div id="presale-page" className="bobtest-presale-clone">
            <header className="presale-hero in">
              <div className="presale-container hero-grid">
                <div className="hero-copy-block">
                  <div className="hero-top-row">
                    <span className="hero-kicker-pill">{heroKickerText}</span>
                    <span className={`status-pill ${heroStatus.className}`}>{heroStatus.label}</span>
                  </div>
                  <h1 className="hero-title">
                    <strong>BOB</strong> <span className="presale-outline">NFT Collection</span>
                  </h1>
                  <div className="monad-chain-pill">
                    <h3 className="made-on-text">Made on</h3>
                    <img src="/monad/monad.png" alt="Monad chain logo" className="monad-logo" />
                  </div>
                </div>
                <div className="hero-image-block">
                  <div className="hero-image-card">
                    <div className="hero-image-inner">
                      <img src="/presale/bobchest.webp" alt="BOB NFT Mint" />
                    </div>
                  </div>
                </div>
              </div>
            </header>

            <main className="presale-main">
              <div className="presale-container presale-layout">
                <section className="presale-panel stats-panel" style={{ "--panel-bg-image": "url('/presale/bobmoon.png')" }}>
                  <div className="panel-header">
                    <div><h2>BOB NFT Collection Overview</h2></div>
                  </div>

                  <div className="stats-grid">
                    <div className="stat">
                      <span className="label">Status</span>
                      <span className="value">{isSoldOut ? "Ended" : "Live"}</span>
                    </div>
                    <div className="stat">
                      <span className="label">Minted</span>
                      <span className="value">{mintedSupply.toLocaleString()} / {maxSupply.toLocaleString()}</span>
                    </div>
                    <div className="stat">
                      <span className="label">Total Supply</span>
                      <span className="value">1,000 NFTs</span>
                    </div>
                    <div className="stat">
                      <span className="label">Mint Price</span>
                      <span className="value">{fmtAmount(mintUnitPriceWei, 18, 3)} MON</span>
                    </div>
                  </div>

                  <div className="progress-block">
                    <div className="progress-header">
                      <span>Mint Progress</span>
                      <span className="progress-percent">{mintProgressPct.toFixed(1)}%</span>
                    </div>
                    <div className="progress-bar">
                      <div className="progress-fill" style={{ width: `${Math.min(mintProgressPct, 100)}%` }} />
                    </div>
                    <div className="progress-meta">
                      <span>Minted: {mintedSupply.toLocaleString()} / {maxSupply.toLocaleString()} NFTs</span>
                    </div>
                  </div>

                  <div className="allocation-block">
                    <div className="allocation-header">
                      <h3>Mint Info</h3>
                    </div>
                    <div className="bobtest-mint-info-visual">
                      <ul className="bobtest-mint-flow-list">
                        <li><span className="bobtest-mint-flow-dot" aria-hidden="true" /><span className="bobtest-mint-flow-text">1,000 Bob NFTs available to mint in total</span></li>
                        <li><span className="bobtest-mint-flow-dot" aria-hidden="true" /><span className="bobtest-mint-flow-text">Mint phases are first come first serve</span></li>
                      </ul>
                      <div className="bobtest-mint-phase-topline" aria-label="Mint phase flow">
                        <article className={`bobtest-flow-node bobtest-flow-node--one ${effectiveStage === "phase1" ? "is-active" : ""}`}>
                          <p className="bobtest-mint-phase-title">Phase 1</p>
                          <p className="bobtest-mint-phase-sub">Top Bob Holders</p>
                        </article>
                        <img className="bobtest-flow-arrow-icon" src="/icons/bracketarrowhollow-right.svg" alt="" aria-hidden="true" />
                        <article className={`bobtest-flow-node bobtest-flow-node--two ${effectiveStage === "phase2" ? "is-active" : ""}`}>
                          <p className="bobtest-mint-phase-title">Phase 2</p>
                          <p className="bobtest-mint-phase-sub">Whitelisted Users</p>
                        </article>
                        <img className="bobtest-flow-arrow-icon" src="/icons/bracketarrowhollow-right.svg" alt="" aria-hidden="true" />
                        <article className={`bobtest-flow-node bobtest-flow-node--three ${effectiveStage === "phase3" ? "is-active" : ""}`}>
                          <p className="bobtest-mint-phase-title">Phase 3</p>
                          <p className="bobtest-mint-phase-sub">Open to Everyone</p>
                        </article>
                      </div>
                    </div>
                  </div>
                </section>

                <section className="presale-panel form-panel" style={{ "--panel-bg-image": "url('/presale/bobhot.webp')" }}>
                  <div className="panel-header">
                    <div>
                      <h2>Participate in Bob NFT Mint</h2>
                      <p className="tagline">
                        {isSoldOut
                          ? "Mint is complete. Wallets hold minted NFTs directly"
                          : "Mint your Bob NFTs directly from this page"}
                      </p>
                    </div>
                  </div>

                  <div className={"buy-area " + (!hasWallet || isMinting || isSoldOut || stageMintDisabled ? "buy-area--disabled" : "")}>
                    <div className="field-group">
                      <label>
                        Fixed Mint Price (<span className="bob-font bob-font--small">MON</span>)
                      </label>
                      <div className="helper">Each NFT costs {fmtAmount(mintUnitPriceWei, 18, 3)} MON and wallet caps depend on the current phase</div>
                    </div>

                    <div className="field-group summary-row">
                      <div className="summary-item">
                        <span className="label">You will receive</span>
                        <span className="value">
                          {Number(mintQty || 1)} <span className="bob-font bob-font--small">BOB NFT{Number(mintQty || 1) === 1 ? "" : "s"}</span>
                        </span>
                      </div>
                      <div className="summary-item">
                        <span className="label">Current total</span>
                        <span className="value">
                          {fmtAmount(mintTotalPriceWei, 18, 2)} <span className="bob-font bob-font--small">MON</span>
                        </span>
                      </div>
                    </div>

                    <div className="cta-block">
                      <div className={"wallet-status " + (hasWallet ? "wallet-status--connected" : "wallet-status--disconnected")}>
                        {hasWallet ? `Wallet connected: ${wallet.shortAddress || wallet.address}` : "Wallet not connected"}
                      </div>

                      <div
                        className={"notice-bar notice-bar--" + (mintNotice.level || "info")}
                        aria-live="polite"
                        style={{
                          height: "28px",
                          display: "flex",
                          alignItems: "center",
                          marginTop: "10px",
                          padding: "0 10px",
                          borderRadius: "10px",
                          fontSize: "13px",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                          opacity: mintNotice.text ? 1 : 0,
                          transition: "opacity 160ms ease",
                        }}
                        title={mintNotice.text || ""}
                      >
                        {mintNotice.text}
                      </div>

                      <div className="nft-inline-action-wrap">
                        {!hasWallet ? (
                          <button
                            className="buy-btn nft-inline-action"
                            type="button"
                            onClick={() => {
                              if (typeof openConnectModal === "function") openConnectModal();
                            }}
                          >
                            Connect Wallet
                          </button>
                        ) : walletMaxedPreviewActive ? (
                          <button
                            className="buy-btn nft-inline-action"
                            type="button"
                            disabled
                            aria-disabled="true"
                          >
                            {isSoldOut || effectiveStage === "ended" ? "Sold Out" : "Wallet Limit Reached"}
                          </button>
                        ) : (
                          <>
                            {!isSoldOut ? (
                              <div className="nft-mint-qty">
                                <button
                                  type="button"
                                  className="nft-btn-primary nft-mint-step nft-mint-step--minus"
                                  onClick={() => setMintQty((q) => Math.max(1, Number(q || 1) - 1))}
                                  disabled={stageMintDisabled || isMinting || Number(mintQty || 1) <= 1}
                                  aria-label="Decrease mint quantity"
                                >
                                  <span className="nft-mint-step-glyph" aria-hidden="true">
                                    <svg className="nft-mint-step-icon" viewBox="0 0 18 18" focusable="false">
                                      <line x1="4" y1="9" x2="14" y2="9" />
                                    </svg>
                                  </span>
                                </button>
                                <span className="nft-mint-qty-value">{Number(mintQty || 1)}</span>
                                <button
                                  type="button"
                                  className="nft-btn-primary nft-mint-step nft-mint-step--plus"
                                  onClick={() => setMintQty((q) => Math.min(Math.max(1, maxMintableNow), 20, Number(q || 1) + 1))}
                                  disabled={stageMintDisabled || walletMaxedPreviewActive || isMinting || Number(mintQty || 1) >= Math.min(Math.max(1, effectiveMaxMintableNow), 20)}
                                  aria-label="Increase mint quantity"
                                >
                                  <span className="nft-mint-step-glyph" aria-hidden="true">
                                    <svg className="nft-mint-step-icon" viewBox="0 0 18 18" focusable="false">
                                      <line x1="4" y1="9" x2="14" y2="9" />
                                      <line x1="9" y1="4" x2="9" y2="14" />
                                    </svg>
                                  </span>
                                </button>
                              </div>
                            ) : null}
                            <button className="buy-btn nft-inline-action" type="button" onClick={mintRandom} disabled={stageMintDisabled || walletMaxedPreviewActive || isMinting || isSoldOut}>
                              {isSoldOut || effectiveStage === "ended" ? "Sold Out" : walletMaxedPreviewActive ? "Wallet Limit Reached" : isMinting ? `Minting ${Number(mintQty || 1)}...` : "Mint"}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="info-panel" style={{ marginTop: "14px" }}>
                    <h3>Mint Details</h3>
                    <ul>
                        <li>
                          <span className="info-label">Mint Price</span>
                          <span className="info-value">
                          {fmtAmount(mintUnitPriceWei, 18, 3)} <span className="bob-font bob-font--small">MON</span> per NFT
                          </span>
                        </li>
                        <li>
                          <span className="info-label">Collection Supply</span>
                          <span className="info-value">
                          1,000 total <span className="bob-font bob-font--small">Bob NFTs</span>
                          </span>
                        </li>
                        <li>
                          <span className="info-label">Phase 1 - Bob Holders</span>
                        <span className="info-value">Max 4 per wallet</span>
                        </li>
                        <li>
                          <span className="info-label">Phase 2 - Whitelist</span>
                        <span className="info-value">Max 2 per wallet</span>
                        </li>
                        <li>
                          <span className="info-label">Phase 3 - Public</span>
                        <span className="info-value">Max 2 per wallet</span>
                        </li>
                    </ul>
                  </div>
                </section>
              </div>
            </main>
          </div>
        ) : (
          <div className="bobtest-mint-row">
            <div className="nft-card bobtest-mint-card" style={{ "--panel-bg-image": "url('/presale/bobmoon.png')" }}>
              <div className="nft-card-inline">
                <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                  <div className="bobtest-mint-price-line">
                    <h3 style={{ marginBottom: 0, lineHeight: 1.1 }}>
                      <span className="bobtest-mint-label">Mint</span>
                      <span className="bobtest-mint-label">Price</span>
                    </h3>
                    <div style={{ fontSize: 22, fontWeight: 700, color: "#f59e0b", whiteSpace: "nowrap" }}>
                      {`${fmtAmount(mintTotalPriceWei, 18, 2)} MON`}
                    </div>
                  </div>
                </div>
                <div className="nft-inline-action-wrap">
                  {!hasWallet ? (
                    <button
                      className="nft-btn-primary nft-inline-action"
                      type="button"
                      onClick={() => {
                        if (typeof openConnectModal === "function") openConnectModal();
                      }}
                    >
                      Connect Wallet
                    </button>
                  ) : walletMaxedPreviewActive ? (
                    <button
                      className="nft-btn-primary nft-inline-action"
                      type="button"
                      disabled
                      aria-disabled="true"
                    >
                      {isSoldOut || effectiveStage === "ended" ? "Sold Out" : "Wallet Limit Reached"}
                    </button>
                  ) : (
                    <>
                      {!isSoldOut ? (
                        <div className="nft-mint-qty">
                          <button
                            type="button"
                            className="nft-btn-primary nft-mint-step nft-mint-step--minus"
                            onClick={() => setMintQty((q) => Math.max(1, Number(q || 1) - 1))}
                            disabled={stageMintDisabled || isMinting || Number(mintQty || 1) <= 1}
                            aria-label="Decrease mint quantity"
                          >
                            <span className="nft-mint-step-glyph" aria-hidden="true">
                              <svg className="nft-mint-step-icon" viewBox="0 0 18 18" focusable="false">
                                <line x1="4" y1="9" x2="14" y2="9" />
                              </svg>
                            </span>
                          </button>
                          <span className="nft-mint-qty-value">{Number(mintQty || 1)}</span>
                          <button
                            type="button"
                            className="nft-btn-primary nft-mint-step nft-mint-step--plus"
                            onClick={() => setMintQty((q) => Math.min(Math.max(1, maxMintableNow), 20, Number(q || 1) + 1))}
                            disabled={stageMintDisabled || walletMaxedPreviewActive || isMinting || Number(mintQty || 1) >= Math.min(Math.max(1, effectiveMaxMintableNow), 20)}
                            aria-label="Increase mint quantity"
                          >
                            <span className="nft-mint-step-glyph" aria-hidden="true">
                              <svg className="nft-mint-step-icon" viewBox="0 0 18 18" focusable="false">
                                <line x1="4" y1="9" x2="14" y2="9" />
                                <line x1="9" y1="4" x2="9" y2="14" />
                              </svg>
                            </span>
                          </button>
                        </div>
                      ) : null}
                      <button className="nft-btn-primary nft-inline-action" type="button" onClick={mintRandom} disabled={stageMintDisabled || walletMaxedPreviewActive || isMinting || isSoldOut}>
                        {isSoldOut || effectiveStage === "ended" ? "Sold Out" : walletMaxedPreviewActive ? "Wallet Limit Reached" : isMinting ? `Minting ${Number(mintQty || 1)}...` : "Mint"}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="nft-card" style={{ "--panel-bg-image": "url('/presale/bobsale.png')" }}>
              {isBobTest ? (
                <div className="progress-block nft-inline-progress nft-inline-progress-centered">
                  <div className="progress-header">
                    <span>Minted Progress</span>
                    <span className="progress-percent">{mintProgressPct.toFixed(1)}%</span>
                  </div>
                  <div className="progress-bar">
                    <div className="progress-fill" style={{ width: `${mintProgressPct}%` }} />
                  </div>
                  <div className="progress-meta">
                    <span>Minted: {mintedSupply.toLocaleString()} / {maxSupply.toLocaleString()}</span>
                  </div>
                </div>
              ) : (
                <div className="nft-card-inline">
                  <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                    <h3 style={{ marginBottom: 6, lineHeight: 1.1 }}>
                      <span style={{ display: "block", whiteSpace: "nowrap" }}>Claimable</span>
                      <span style={{ display: "block", whiteSpace: "nowrap" }}>Rewards</span>
                    </h3>
                    <div style={{ fontSize: 22, fontWeight: 700, color: "#f59e0b", whiteSpace: "nowrap" }}>
                      {rewardsBootstrapped ? `${fmtAmount(ownerRewardStats.pending || 0n, 18, 2)} BOB` : "Loading..."}
                    </div>
                  </div>
                  <button
                    className="nft-btn-primary nft-inline-action"
                    type="button"
                    disabled={!rewardsBootstrapped || (ownerRewardStats.pending || 0n) <= 0n || isClaimingAll}
                    onClick={claimAllRewardsSingleTx}
                  >
                    {isClaimingAll ? "Claiming..." : "Claim"}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        <div
          style={{ marginTop: isBobTest ? 4 : 12, "--panel-bg-image": "url('/presale/bobtrade.png')" }}
          className="nft-card nft-card--dashboard"
        >
          <div className="nft-dashboard-head">
            <div><h2 className="nft-dashboard-title">Dashboard</h2></div>
            {hasWallet ? (
              <div className="nft-dash-controls" ref={dashControlsRef}>
                <button
                  type="button"
                  className={`nft-dash-control-btn ${showSortPanel ? "nft-dash-control-btn--active" : ""}`}
                  onClick={() => {
                    setShowSortPanel((v) => !v);
                    setShowFilterPanel(false);
                    setOpenSelectKey("");
                  }}
                >
                  Sort
                </button>
                <button
                  type="button"
                  className={`nft-dash-control-btn ${showFilterPanel ? "nft-dash-control-btn--active" : ""}`}
                  onClick={() => {
                    setShowFilterPanel((v) => !v);
                    setShowSortPanel(false);
                    setOpenSelectKey("");
                  }}
                >
                  Filter
                </button>

                <div className={`nft-dash-panel ${showSortPanel ? "nft-dash-panel--open" : ""}`}>
                  <p className="nft-dash-panel-title">Sort NFTs</p>
                  <div className="nft-dash-panel-row">
                    <div className={`nft-dash-select ${openSelectKey === "sort" ? "nft-dash-select--open" : ""}`}>
                      <button
                        type="button"
                        className={`nft-dash-select-trigger ${openSelectKey === "sort" ? "nft-dash-select-trigger--open" : ""}`}
                        onClick={() => setOpenSelectKey((v) => (v === "sort" ? "" : "sort"))}
                      >
                        <span>
                          {{
                            name_asc: "Name (Bob # Low to High)",
                            name_desc: "Name (Bob # High to Low)",
                          }[sortKey] || "Select order"}
                        </span>
                        <svg className="nft-dash-select-chevron" viewBox="0 0 16 16" aria-hidden="true">
                          <path d="M3 6.5L8 11l5-4.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                      <div className={`nft-dash-select-menu ${openSelectKey === "sort" ? "nft-dash-select-menu--open" : ""}`}>
                        {[
                          ["name_asc", "Name (Bob # Low to High)"],
                          ["name_desc", "Name (Bob # High to Low)"],
                        ].map(([value, label]) => (
                          <button
                            key={`sort-opt-${value}`}
                            type="button"
                            className={`nft-dash-select-option ${sortKey === value ? "nft-dash-select-option--active" : ""}`}
                            onClick={() => {
                              setSortKey(value);
                              setOpenSelectKey("");
                            }}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className={`nft-dash-panel ${showFilterPanel ? "nft-dash-panel--open" : ""}`}>
                  <p className="nft-dash-panel-title">Filter NFTs</p>
                  <div className="nft-dash-trait-grid">
                    {[
                      ["head", "Head"],
                      ["eyes", "Eyes"],
                      ["mouth", "Mouth"],
                      ["outfit", "Outfit"],
                      ["accessory", "Accessory"],
                      ["back", "Back"],
                    ].map(([key, label]) => (
                      <div key={`trait-filter-${key}`} className="nft-dash-panel-row">
                        <label>{label}</label>
                        <div className={`nft-dash-select ${openSelectKey === `trait-${key}` ? "nft-dash-select--open" : ""}`}>
                          <button
                            type="button"
                            className={`nft-dash-select-trigger ${openSelectKey === `trait-${key}` ? "nft-dash-select-trigger--open" : ""}`}
                            onClick={() => setOpenSelectKey((v) => (v === `trait-${key}` ? "" : `trait-${key}`))}
                          >
                            <span>{filterTraits[key] === "any" ? "Any" : `Trait ${filterTraits[key]}`}</span>
                            <svg className="nft-dash-select-chevron" viewBox="0 0 16 16" aria-hidden="true">
                              <path d="M3 6.5L8 11l5-4.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </button>
                          <div className={`nft-dash-select-menu ${openSelectKey === `trait-${key}` ? "nft-dash-select-menu--open" : ""}`}>
                            {["any", "1", "2", "3", "4", "5"].map((value) => (
                              <button
                                key={`${key}-opt-${value}`}
                                type="button"
                                className={`nft-dash-select-option ${filterTraits[key] === value ? "nft-dash-select-option--active" : ""}`}
                                onClick={() => {
                                  setFilterTraits((prev) => ({ ...prev, [key]: value }));
                                  setOpenSelectKey("");
                                }}
                              >
                                {value === "any" ? "Any" : `Trait ${value}`}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="nft-dash-filter-footer">
                    <button
                      type="button"
                      className="nft-dash-clear-btn"
                      onClick={() => {
                        setFilterLevel("any");
                        setFilterState("any");
                        setFilterTraits({
                          head: "any",
                          eyes: "any",
                          mouth: "any",
                          outfit: "any",
                          accessory: "any",
                          back: "any",
                        });
                      }}
                    >
                      Clear
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
          <div>
            {isBobTest ? null : (
              <div className="nft-summary-layout">
                <div className="nft-summary-grid">
                  <div className="nft-summary-item">
                    <div className="nft-summary-label">Total Owned</div>
                    <div className="nft-summary-value">
                      {hasWallet ? (
                        <>
                          {dashboardSummary.total} <span className="nfttest-summary-accent">{dashboardSummary.total === 1 ? "NFT" : "NFTs"}</span>
                        </>
                      ) : (
                        "Connect Wallet"
                      )}
                    </div>
                  </div>
                  <div className="nft-summary-item">
                    <div className="nft-summary-label">Total Earned</div>
                    <div className="nft-summary-value">
                      {hasWallet ? (
                        <>
                          {fmtAmount(dashboardSummary.earned, 18, 2)} <span className="nfttest-summary-accent">BOB</span>
                        </>
                      ) : (
                        "Connect Wallet"
                      )}
                    </div>
                  </div>
                </div>
                <div className="nft-summary-explainer">
                  <p className="nft-summary-explainer-title">How Bob NFTs Work</p>
                  <div className="nft-summary-explainer-lines">
                    <p className="nft-summary-explainer-line"><span className="nft-summary-line-accent">Active</span> Bob NFTs earn BOB tokens continuously over time.</p>
                    <p className="nft-summary-explainer-line"><span className="nft-summary-line-accent">Level Up</span> boosts your multiplier and increases BOB rewards.</p>
                    <p className="nft-summary-explainer-line"><span className="nft-summary-line-accent">Recharge</span> monthly to keep your NFT active and earning.</p>
                  </div>
                </div>
              </div>
            )}
            {(isLoading && allMints.length === 0) ? (
              <div className={`nft-token-grid ${isBobTest ? "nft-token-grid--bobtest" : ""}`}>
                {[1, 2].map((idx) => (
                  isBobTest ? (
                    <div key={`nft-skeleton-${idx}`} className="nft-card nft-token-card nft-token-card--bobtest nft-skeleton">
                      <div className="nft-token-top-actions" />
                      <div className="nft-bobtest-card-head">
                        <div className="nft-skel-line" style={{ width: 82, height: 14 }} />
                      </div>
                      <div className="nft-bobtest-card-layout">
                        <div className="nft-token-media">
                          <div className="nft-token-media-image nft-skel-box" />
                        </div>
                        <div className="nft-traits-grid nft-traits-grid--bobtest">
                          {[1, 2, 3, 4, 5, 6].map((k) => (
                            <div key={`nft-skel-trait-${idx}-${k}`} className="nft-trait-item nft-skel-trait" />
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div key={`nft-skeleton-${idx}`} className="nft-card nft-token-card nft-skeleton">
                      <div className="nft-skel-pill nft-skel-pill-corner" />
                      <div className="nft-token-top">
                        <div className="nft-token-media">
                          <div className="nft-token-media-label-wrap">
                            <div className="nft-skel-line" style={{ width: 78, height: 14 }} />
                          </div>
                          <div className="nft-token-media-image nft-skel-box" />
                        </div>
                        <div className="nft-token-body">
                          <div className="nft-token-mainrow">
                            <div className="nft-token-meta-grid">
                              <div className="nft-token-stat">
                                <div style={{ display: "grid", gap: 8, alignContent: "center", height: "100%" }}>
                                  <div className="nft-skel-line" style={{ width: "100%", height: 12, justifySelf: "center" }} />
                                  <div className="nft-skel-box" style={{ height: 42 }} />
                                </div>
                              </div>
                              <div className="nft-token-stat">
                                <div style={{ display: "grid", gap: 8, alignContent: "center", height: "100%" }}>
                                  <div className="nft-skel-line" style={{ width: "100%", height: 12, justifySelf: "center" }} />
                                  <div className="nft-skel-box" style={{ height: 42 }} />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="nft-skel-traits">
                        {[1, 2, 3, 4, 5, 6].map((k) => <div key={`nft-skel-trait-${idx}-${k}`} className="nft-skel-trait" />)}
                      </div>
                        <div className="nft-skel-xp">
                        <div className="nft-skel-line" style={{ width: "100%", height: 11 }} />
                        <div className="nft-skel-xp-bar" />
                      </div>
                    </div>
                  )
                ))}
              </div>
            ) : allMints.length === 0 ? (
              <div className="nft-empty-cta">
                <p className="nft-empty-cta-title">No mints detected yet.</p>
                <p className="nft-empty-cta-sub">Newly minted Bob NFTs from all wallets will appear here in real time.</p>
              </div>
            ) : dashboardVisibleMints.length === 0 ? (
              <div className="nft-empty-cta">
                <p className="nft-empty-cta-title">No NFTs match your current filters.</p>
                <p className="nft-empty-cta-sub">Try clearing filters to show all your Bob NFTs.</p>
              </div>
            ) : (
              <>
              <div className={`nft-token-grid ${isBobTest ? "nft-token-grid--bobtest" : ""}`}>
                {dashboardPageMints.map((m) => {
                  const rt = runtimeByTokenId[String(m.tokenId)];
                  const progress = computeXpProgress(rt, levelThresholds);
                  const currentLevel = Number(rt?.level || 1);
                  const levelUpCostMonWei =
                    currentLevel >= 5
                      ? 0n
                      : (protocolData?.levelUpPriceUsdWadByLevel?.[currentLevel] ?? 0n);
                  const rechargeCostMonWei =
                    (protocolData?.rechargePriceUsdWadByLevel?.[currentLevel] ?? 0n);
                  const levelUpCostLabel = levelUpCostMonWei > 0n ? `${fmtAmount(levelUpCostMonWei, 18, 2)} MON` : "-";
                  const rechargeCostLabel = rechargeCostMonWei > 0n ? `${fmtAmount(rechargeCostMonWei, 18, 2)} MON` : "-";
                  const shouldShowRecharge = rt && Number(rt.status) !== 0;
                  const canLevelUp = Boolean(rt && progress.nextLevel && (rt.xp || 0n) >= (progress.needed || 0n));
                  const showRechargeButton = Boolean(shouldShowRecharge);
                  const showLevelUpButton = Boolean(canLevelUp);
                  const status = rt ? statusLabel(rt.status) : "loading";
                  const statusClass = status.toLowerCase();
                  const rechargeLabel = rt && Number(rt.status) !== 0 ? "Recharge" : "Recharge In";
                  const rechargeValue = rt
                    ? (Number(rt.status) !== 0 ? "Now" : fmtRemaining(rt.activeUntil, nowSec))
                    : "-";
                  const traitDisplay = [
                    { label: "Head", value: m?.traits ? `Trait ${(Number(m.traits.hat ?? 0) + 1)}` : "TBD" },
                    { label: "Eyes", value: m?.traits ? `Trait ${(Number(m.traits.eyes ?? 0) + 1)}` : "TBD" },
                    { label: "Mouth", value: m?.traits ? `Trait ${(Number(m.traits.mouth ?? 0) + 1)}` : "TBD" },
                    { label: "Outfit", value: m?.traits ? `Trait ${(Number(m.traits.clothes ?? 0) + 1)}` : "TBD" },
                    { label: "Accessory", value: m?.traits ? `Trait ${(Number(m.traits.accessory ?? 0) + 1)}` : "TBD" },
                    { label: "Back", value: m?.traits ? `Trait ${(Number(m.traits.background ?? 0) + 1)}` : "TBD" },
                  ];
                  if (isBobTest) {
                    return (
                      <div key={`dashboard-token-${m.tokenId}`} className="nft-card nft-token-card nft-token-card--bobtest nft-token-card--fade-in">
                        <div className="nft-token-top-actions">
                          <a
                            className="nft-token-explorer-link"
                            href={`${MONAD_EXPLORER_BASE}/token/${contractAddress}/instance/${m.tokenId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={`View Bob #${m.tokenId} on explorer`}
                            title="View on Explorer"
                          >
                            <svg viewBox="0 0 24 24" aria-hidden="true">
                              <path d="M14 5h5v5" />
                              <path d="M10 14L19 5" />
                              <path d="M19 14v5h-14v-14h5" />
                            </svg>
                          </a>
                        </div>
                        <div className="nft-bobtest-card-head">
                          <div className="nft-token-media-label">Bob #{m.tokenId}</div>
                        </div>
                        <div className="nft-bobtest-card-layout">
                          <div className="nft-token-media">
                            <div className="nft-token-media-image">
                              <button
                                type="button"
                                className="nft-token-media-image-button"
                                onClick={() => setPreviewMint(m)}
                                aria-label={`Open larger image for Bob #${m.tokenId}`}
                              >
                                <MintImage mint={m} size={isBobTest ? "100%" : 110} assetBase={resolvedAssetBase} />
                              </button>
                            </div>
                          </div>
                          <div className="nft-traits-grid nft-traits-grid--bobtest">
                            {traitDisplay.map((trait) => (
                              <div key={`${m.tokenId}-${trait.label}`} className="nft-trait-item">
                                <div className="nft-trait-label">{trait.label}</div>
                                <div className="nft-trait-value">{trait.value}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div key={`dashboard-token-${m.tokenId}`} className="nft-card nft-token-card nft-token-card--fade-in">
                      <div className="nft-token-top-actions">
                        <a
                          className="nft-token-explorer-link"
                          href={`${MONAD_EXPLORER_BASE}/nft/${contractAddress}/${m.tokenId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`View Bob #${m.tokenId} on explorer`}
                          title="View on Explorer"
                        >
                          <svg viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M14 5h5v5" />
                            <path d="M10 14L19 5" />
                            <path d="M19 14v5h-14v-14h5" />
                          </svg>
                        </a>
                        <span className={`nft-status-chip nft-status-chip--corner ${statusClass}`}>{status}</span>
                      </div>
                      <div className="nft-token-top">
                        <div className="nft-token-media">
                          <div className="nft-token-media-label-wrap">
                            <div className="nft-token-media-label">Bob #{m.tokenId}</div>
                          </div>
                          <div className="nft-token-media-image">
                            <button
                              type="button"
                              className="nft-token-media-image-button"
                              onClick={() => setPreviewMint(m)}
                              aria-label={`Open larger image for Bob #${m.tokenId}`}
                            >
                              <MintImage mint={m} size={110} assetBase={resolvedAssetBase} />
                            </button>
                          </div>
                        </div>
                        <div className="nft-token-body">
                          <div className="nft-token-mainrow">
                            <div className="nft-token-meta-grid">
                              <div className={`nft-token-stat ${showLevelUpButton ? "" : "nft-token-stat--no-action"}`}>
                                <div className="nft-token-stat-line">
                                  <span className="nft-token-stat-label">NFT Level</span>
                                  <span className="nft-token-stat-value">{rt?.level ?? "..."}</span>
                                </div>
                                <div className={`nft-token-actions ${showLevelUpButton ? "" : "nft-token-actions--empty"}`}>
                                  {showLevelUpButton ? (
                                    <button
                                      type="button"
                                      className="nft-token-action-btn nft-token-action-btn--stack"
                                      disabled={!canLevelUp}
                                      onClick={() => runWrite({ address: contractAddress, abi: NFT_ABI, functionName: "levelUp", args: [BigInt(m.tokenId)] })}
                                    >
                                      <span className="nft-token-action-title">Level Up</span>
                                      <span className="nft-token-action-cost">({levelUpCostLabel})</span>
                                    </button>
                                  ) : null}
                                </div>
                              </div>
                              <div className={`nft-token-stat ${showRechargeButton ? "" : "nft-token-stat--no-action"}`}>
                                <div className="nft-token-stat-line">
                                  <span className="nft-token-stat-label">{rechargeLabel}</span>
                                  <span className="nft-token-stat-value">{rechargeValue}</span>
                                </div>
                                <div className={`nft-token-actions ${showRechargeButton ? "" : "nft-token-actions--empty"}`}>
                                  {showRechargeButton ? (
                                    <button
                                      type="button"
                                      className="nft-token-action-btn nft-token-action-btn--stack"
                                      disabled={!shouldShowRecharge}
                                      onClick={() => runWrite({ address: contractAddress, abi: NFT_ABI, functionName: "recharge", args: [BigInt(m.tokenId)] })}
                                    >
                                      <span className="nft-token-action-title">Recharge</span>
                                      <span className="nft-token-action-cost">({rechargeCostLabel})</span>
                                    </button>
                                  ) : null}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="nft-traits-grid">
                        {traitDisplay.map((trait) => (
                          <div key={`${m.tokenId}-${trait.label}`} className="nft-trait-item">
                            <div className="nft-trait-label">{trait.label}</div>
                            <div className="nft-trait-value">{trait.value}</div>
                          </div>
                        ))}
                      </div>
                      <div style={{ marginTop: 8 }}>
                        <div className="nft-xp-label" style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                          <span>
                            XP: {rt ? fmtAmount(progress.displayCurrent || 0n, 18, 1) : "-"}
                            {progress.nextLevel ? ` / ${progress.needsKnown ? fmtAmount(progress.needed || 0n, 18, 1) : "..."}` : " (Max)"}
                          </span>
                          <span className="nft-xp-percent">{fmtPct(progress.pct)}</span>
                        </div>
                        <div className="nft-xp-bar">
                          <div className="nft-xp-bar-fill" style={{ width: fmtPct(progress.pct) }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{ display: "flex", alignItems: "center", marginTop: 12 }}>
                <div style={{ color: "#94a3b8", fontSize: 12, display: "none" }}>
                  Page {dashboardPageSafe} of {dashboardTotalPages}
                  {" · "}
                  {isBobTest ? dashboardTotalServer : dashboardVisibleMints.length} NFTs
                </div>
                <div className="nft-dashboard-pager">
                  {dashboardPageSafe > 1 ? (
                    <button
                      type="button"
                      className="nft-dashboard-pager-btn"
                      onClick={() => setDashboardPage((p) => Math.max(1, p - 1))}
                    >
                      Previous
                    </button>
                  ) : <span />}
                  {dashboardPageSafe < dashboardTotalPages ? (
                    <button
                      type="button"
                      className="nft-dashboard-pager-btn"
                      onClick={() => setDashboardPage((p) => Math.min(dashboardTotalPages, p + 1))}
                    >
                      Next
                    </button>
                  ) : <span />}
                </div>
              </div>
              </>
            )}
          </div>
        </div>

      </div>
      </div>
      </main>

        {mintRevealOpen ? (
          <div className="nft-mint-reveal" role="dialog" aria-modal="true" aria-label="Mint reveal">
            <div className="nft-mint-reveal-panel">
              <div className="nft-mint-reveal-head">
                <p className="nft-mint-reveal-title">{mintRevealLoading ? "Forging your Bob NFT..." : "Mint Complete"}</p>
                <p className="nft-mint-reveal-sub">
                  {mintRevealLoading ? "Finalizing mint and preparing your reveal." : (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                      {lastTxHash ? (
                        <a
                          className="nft-token-explorer-link"
                          href={`https://monadvision.com/tx/${lastTxHash}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="View mint transaction on Monadvision"
                          title="View mint transaction"
                          style={{ width: 20, height: 20 }}
                        >
                          <svg viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M14 5h5v5" />
                            <path d="M10 14L19 5" />
                            <path d="M19 14v5h-14v-14h5" />
                          </svg>
                        </a>
                      ) : null}
                      <span>
                        Minted {mintRevealItems.length}{" "}
                        <span className="nft-mint-reveal-sub-accent">
                          Bob NFT{mintRevealItems.length === 1 ? "" : "s"}
                        </span>
                      </span>
                    </span>
                  )}
                </p>
                {mintRevealLoading ? (
                  <p className="nft-mint-reveal-sub" style={{ marginTop: 6 }}>
                    {rendererSyncStatus || "Processing mint..."}
                  </p>
                ) : null}
              </div>

              <div className="nft-mint-reveal-scroll-shell">
                <div className="nft-mint-reveal-inner nft-mint-reveal-scroll">
                  {(() => {
                    const status = String(rendererSyncStatus || "");
                    const showRevealCardSkeleton =
                      mintRevealLoading &&
                      mintRevealItems.length === 0 &&
                      /(Mint confirmed|Preparing NFT reveal|Synced|Transaction submitted)/i.test(status);
                    if (showRevealCardSkeleton) {
                      return (
                    <div className="nft-mint-reveal-grid">
                      {Array.from({ length: Math.max(1, Math.min(8, Number(mintQty || 1))) }).map((_, idx) => (
                        <div key={`reveal-loading-skeleton-${idx}`} className="nft-mint-reveal-card nft-mint-reveal-skeleton nft-skeleton">
                          <h4 className="nft-mint-reveal-card-header" />
                          <div className="nft-mint-reveal-top">
                            <div>
                              <div className="nft-mint-reveal-image-wrap" />
                            </div>
                            <div className="nft-mint-reveal-traits">
                              {TRAIT_UI_ORDER.map(([key]) => (
                                <div key={`reveal-loading-trait-${idx}-${key}`} className="nft-mint-reveal-trait" />
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                      );
                    }
                    if (mintRevealLoading && mintRevealItems.length === 0) {
                      return (
                        <div className="nft-mint-reveal-loader">
                          <div className="nft-mint-reveal-loader-fill" />
                        </div>
                      );
                    }
                    return mintRevealItems.length > 0 ? (
                    <>
                      <div className="nft-mint-reveal-grid">
                        {(() => {
                          const allRevealReady = mintRevealItems.every((it) => {
                            const hasTraits = Boolean(it?.traits);
                            const hasImage = Boolean(resolveNftImageSrc(it?.metadataImage));
                            return hasTraits && hasImage && Boolean(it?.revealResolved);
                          });
                          return mintRevealItems.map((item) => {
                          const revealHasTraits = Boolean(item?.traits);
                          const revealHasImage = Boolean(resolveNftImageSrc(item?.metadataImage));
                          const revealReady =
                            allRevealReady &&
                            revealHasTraits &&
                            revealHasImage &&
                            Boolean(item?.revealResolved);
                          return (
                            <div key={`reveal-card-${item.tokenId}`} className={`nft-mint-reveal-card ${revealReady ? "" : "nft-mint-reveal-skeleton nft-skeleton"}`}>
                              <div className="nft-mint-reveal-top-actions">
                                <a
                                  className="nft-token-explorer-link"
                                  href={`${MONAD_EXPLORER_BASE}/nft/${contractAddress}/${item.tokenId}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  aria-label={`View Bob #${item.tokenId} on explorer`}
                                  title="View on Explorer"
                                >
                                  <svg viewBox="0 0 24 24" aria-hidden="true">
                                    <path d="M14 5h5v5" />
                                    <path d="M10 14L19 5" />
                                    <path d="M19 14v5h-14v-14h5" />
                                  </svg>
                                </a>
                              </div>
                              {revealReady ? (
                                <>
                                  <h4 className="nft-mint-reveal-card-header">Bob #{item.tokenId}</h4>
                                  <div className="nft-mint-reveal-top">
                                    <div>
                                      <div className="nft-mint-reveal-image-wrap">
                                        <MintImage mint={item} size={"100%"} assetBase={resolvedAssetBase} />
                                      </div>
                                    </div>
                                    <div className="nft-mint-reveal-traits">
                                      {TRAIT_UI_ORDER.map(([key, label, getter]) => (
                                        <div key={`reveal-${item.tokenId}-${key}`} className="nft-mint-reveal-trait">
                                          <div className="nft-mint-reveal-trait-label">{label}</div>
                                          <div className="nft-mint-reveal-trait-value">Trait {getter(item.traits)}</div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <h4 className="nft-mint-reveal-card-header" />
                                  <div className="nft-mint-reveal-top">
                                    <div>
                                      <div className="nft-mint-reveal-image-wrap" />
                                    </div>
                                    <div className="nft-mint-reveal-traits">
                                      {TRAIT_UI_ORDER.map(([key]) => (
                                        <div key={`reveal-skel-${item.tokenId}-${key}`} className="nft-mint-reveal-trait" />
                                      ))}
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                          );
                        });
                        })()}
                      </div>
                    </>
                    ) : (
                      <div className="nft-mint-reveal-sub">Preparing reveal...</div>
                    );
                  })()}
                </div>
              </div>
              <div className="nft-mint-reveal-actions">
                <button type="button" onClick={() => setMintRevealOpen(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {previewMint ? (
          <div
            className={`nft-image-modal ${isPreviewClosing ? "is-closing" : ""}`}
            onClick={closePreview}
            role="dialog"
            aria-modal="true"
            aria-label={`Bob #${previewMint.tokenId} image preview`}
          >
            <div className="nft-image-modal-panel" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                className="nft-image-modal-close"
                onClick={closePreview}
                aria-label="Close image preview"
              >
                ×
              </button>
              <div className="nft-image-modal-content">
                <div className="nft-bobtest-card-head">
                  <div className="nft-token-media-label">Bob #{previewMint.tokenId}</div>
                </div>
                <div className="nft-image-modal-image">
                  {previewNav.source.length > 1 && previewNav.hasPrev ? (
                    <button
                      type="button"
                      className="nft-image-nav-btn nft-image-nav-btn--prev"
                      onClick={() => goPreviewBy(-1)}
                      aria-label="Previous NFT"
                    >
                      <svg className="nft-image-nav-icon" viewBox="0 0 8 16" aria-hidden="true">
                        <path d="M6.5 15c-0.137 0-0.271-0.057-0.367-0.161l-6-6.5c-0.177-0.191-0.177-0.486 0-0.678l6-6.5C6.272 1.009 6.491 0.96 6.682 1.034C6.874 1.109 7 1.294 7 1.5v3.79c0 0.125-0.047 0.247-0.132 0.339L4.68 8l2.188 2.371C6.953 10.463 7 10.584 7 10.71v3.79c0 0.206-0.126 0.391-0.318 0.466C6.623 14.989 6.561 15 6.5 15zM1.181 8L6 13.221v-2.315L3.632 8.339c-0.176-0.191-0.176-0.486 0-0.678L6 5.095V2.779L1.181 8z" />
                      </svg>
                    </button>
                  ) : null}
                  <MintImage mint={previewMint} size={"100%"} assetBase={resolvedAssetBase} />
                  {previewNav.source.length > 1 && previewNav.hasNext ? (
                    <button
                      type="button"
                      className="nft-image-nav-btn nft-image-nav-btn--next"
                      onClick={() => goPreviewBy(1)}
                      aria-label="Next NFT"
                    >
                      <svg className="nft-image-nav-icon" viewBox="8 0 8 16" aria-hidden="true">
                        <path d="M9.5 15c-0.062 0-0.123-0.011-0.182-0.034C9.126 14.891 9 14.706 9 14.5v-3.79c0-0.126 0.047-0.247 0.133-0.339L11.319 8 9.133 5.629C9.047 5.537 9 5.416 9 5.29V1.5c0-0.206 0.126-0.391 0.318-0.466c0.188-0.073 0.408-0.025 0.549 0.126l6 6.5c0.177 0.192 0.177 0.487 0 0.678l-6 6.5C9.771 14.943 9.637 15 9.5 15zM10 10.905v2.315L14.819 8 10 2.779v2.316l2.367 2.566c0.177 0.191 0.177 0.486 0 0.678L10 10.905z" />
                      </svg>
                    </button>
                  ) : null}
                </div>
                <div className="nft-image-modal-traits">
                  {[
                    { label: "Head", value: previewMint?.traits ? `Trait ${(Number(previewMint.traits.hat ?? 0) + 1)}` : "TBD" },
                    { label: "Eyes", value: previewMint?.traits ? `Trait ${(Number(previewMint.traits.eyes ?? 0) + 1)}` : "TBD" },
                    { label: "Mouth", value: previewMint?.traits ? `Trait ${(Number(previewMint.traits.mouth ?? 0) + 1)}` : "TBD" },
                    { label: "Outfit", value: previewMint?.traits ? `Trait ${(Number(previewMint.traits.clothes ?? 0) + 1)}` : "TBD" },
                    { label: "Accessory", value: previewMint?.traits ? `Trait ${(Number(previewMint.traits.accessory ?? 0) + 1)}` : "TBD" },
                    { label: "Back", value: previewMint?.traits ? `Trait ${(Number(previewMint.traits.background ?? 0) + 1)}` : "TBD" },
                  ].map((trait) => (
                    <div key={`preview-${previewMint.tokenId}-${trait.label}`} className="nft-trait-item">
                      <div className="nft-trait-label">{trait.label}</div>
                      <div className="nft-trait-value">{trait.value}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {isDevConnected ? (
          <button className="nft-dev-fab" type="button" onClick={() => setShowDevConsole(true)}>
            Dev Console
          </button>
        ) : null}

        {showDevConsole && isDevConnected ? (
          <div style={{ position: "fixed", top: 78, right: 12, bottom: 12, zIndex: 9999, width: "min(460px, calc(100vw - 24px))", pointerEvents: "none" }}>
            <div className="nft-dev-panel" style={{ height: "100%", overflow: "auto", background: "rgba(15,23,42,0.98)", border: "1px solid rgba(30, 64, 175, 0.85)", borderRadius: 18, padding: 16, boxShadow: "0 16px 40px rgba(0,0,0,0.55)", pointerEvents: "auto" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <h2 style={{ margin: 0 }}>Dev Console</h2>
                <button type="button" onClick={() => setShowDevConsole(false)}>Close</button>
              </div>

              <div className="nft-card">
                <h3>Wallet + Contract</h3>
                <div>Connected: {wallet.isConnected ? "yes" : "no"}</div>
                <div>Address: {wallet.address || "-"}</div>
                <div>ChainId: {wallet.chainId || "-"}</div>
                <div>Expected ChainId: {EXPECTED_CHAIN_ID}</div>
                <div>Wrong network: {isWrongNetwork ? "yes" : "no"}</div>
                <div style={{ marginTop: 8 }}>
                  <label htmlFor="nftContractAddress">Address: </label>
                  <input id="nftContractAddress" value={contractAddress} onChange={(e) => setContractAddress(e.target.value)} placeholder="0x..." style={{ width: 520 }} />
                </div>
                <div style={{ marginTop: 8 }}>Detected V5: {isV5 === true ? "yes" : isV5 === false ? "no" : "checking"}</div>
                <div>Contract bytecode present: {contractCodePresent === null ? "checking" : contractCodePresent ? "yes" : "no"}</div>
                <div>Renderer API: {RENDERER_API_BASE}</div>
                <div>Asset base in use: {resolvedAssetBase}</div>
                {protocolReadError ? (
                  <div style={{ marginTop: 8, color: "#fca5a5", wordBreak: "break-word" }}>
                    Last protocol read error: {protocolReadError}
                  </div>
                ) : null}
              </div>

              <div className="nft-card" style={{ marginTop: 10 }}>
                <h3>Protocol (V5)</h3>
                {isV5 ? (
                  <>
                    <div>Owner: {protocolData.owner || "-"}</div>
                    <div>Payment mode: Native MON</div>
                    <div>Wallet MON balance: {fmtAmount(nativeBalanceWei || 0n, 18, 4)}</div>
                    <div>Mint price (MON): {fmtAmount(mintUnitPriceWei, 18, 6)} each</div>
                    <div>Mint total (MON): {fmtAmount(mintTotalPriceWei, 18, 6)} for {safeMintQty}</div>
                    <div>totalSupply (raw): {String(protocolData.totalSupply ?? "-")}</div>
                    <div>MAX_SUPPLY (raw): {String(protocolData.maxSupply ?? "-")}</div>
                    <div>Reward pool (BOB): {fmtAmount(protocolData.rewardPoolBalance || 0n, 18, 4)}</div>
                    <div>Total treasury (MON): {fmtAmount(protocolData.totalTreasuryCollected || 0n, 18, 4)}</div>
                    <div>Total BOB bought for rewards: {fmtAmount(protocolData.totalBobBoughtForRewards || 0n, 18, 4)}</div>
                    <div>Buyback reserve (MON): {fmtAmount(protocolData.buybackReserveMon || 0n, 18, 4)}</div>
                    <div>Split (reward/treasury): {String(protocolData.rewardSplitBps || 0n)} / {String(protocolData.treasurySplitBps || 0n)} bps</div>
                    <div>Buyback config: slippage {String(protocolData.buybackSlippageBps || 0n)} bps, min BOB/MON {fmtAmount(protocolData.minBobPerMonWad || 0n, 18, 6)}, max swap {fmtAmount(protocolData.maxSwapMonPerTx || 0n, 18, 4)} MON</div>
                    <div>Reward drip rate (wad/sec): {String(protocolData.rewardDripRateWadPerSecond || 0n)}</div>
                    <div>Last reward accrual at: {fmtTs(protocolData.lastRewardAccrualAt)}</div>
                    <div>Mint paused: {String(Boolean(protocolData.mintPaused))}</div>
                  </>
                ) : <div>Not a V5 contract (or read failed).</div>}
              </div>

              <div className="nft-card" style={{ marginTop: 10 }}>
                <h3>Phased Mint Admin (Test Contract)</h3>
                {isV5 ? (
                  <div style={{ marginTop: 10, display: "grid", gap: 12 }}>
                    <div style={{ fontSize: 13, opacity: 0.9 }}>
                      Use this section for this phased mint contract only. Each button sends an on-chain admin transaction.
                    </div>

                    <div style={{ border: "1px solid rgba(148,163,184,0.35)", borderRadius: 10, padding: 10 }}>
                      <div style={{ marginBottom: 8, fontWeight: 700 }}>Mint Config</div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                        <label htmlFor="mintMon">Mint Price (MON)</label>
                        <input id="mintMon" value={mintMon} onChange={(e) => setMintMon(e.target.value)} style={{ width: 120 }} />
                        <label htmlFor="maxSupplyPerRoundInput">Max Supply (Round)</label>
                        <input
                          id="maxSupplyPerRoundInput"
                          value={maxSupplyPerRoundInput}
                          onChange={(e) => setMaxSupplyPerRoundInput(e.target.value)}
                          style={{ width: 120 }}
                        />
                        <button
                          type="button"
                          onClick={() =>
                            runWrite({
                              address: contractAddress,
                              abi: NFT_ABI,
                              functionName: "setMintConfig",
                              args: [parseUnits(mintMon || "0", 18), BigInt(maxSupplyPerRoundInput || "0")],
                            })
                          }
                        >
                          Update Mint Config
                        </button>
                      </div>
                    </div>

                    <div style={{ border: "1px solid rgba(148,163,184,0.35)", borderRadius: 10, padding: 10 }}>
                      <div style={{ marginBottom: 8, fontWeight: 700 }}>Phase Control</div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button type="button" onClick={() => runWrite({ address: contractAddress, abi: NFT_ABI, functionName: "setPhase", args: [0] })}>Set Upcoming</button>
                        <button type="button" onClick={() => runWrite({ address: contractAddress, abi: NFT_ABI, functionName: "setPhase", args: [1] })}>Set Phase 1</button>
                        <button type="button" onClick={() => runWrite({ address: contractAddress, abi: NFT_ABI, functionName: "setPhase", args: [2] })}>Set Phase 2</button>
                        <button type="button" onClick={() => runWrite({ address: contractAddress, abi: NFT_ABI, functionName: "setPhase", args: [3] })}>Set Phase 3 (Public)</button>
                        <button type="button" onClick={() => runWrite({ address: contractAddress, abi: NFT_ABI, functionName: "setPhase", args: [4] })}>Set Ended</button>
                      </div>
                    </div>

                    <div style={{ border: "1px solid rgba(148,163,184,0.35)", borderRadius: 10, padding: 10 }}>
                      <div style={{ marginBottom: 8, fontWeight: 700 }}>Per-Wallet Caps</div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                        <label htmlFor="phase1Cap">Phase 1</label>
                        <input id="phase1Cap" value={phase1Cap} onChange={(e) => setPhase1Cap(e.target.value)} style={{ width: 70 }} />
                        <label htmlFor="phase2Cap">Phase 2</label>
                        <input id="phase2Cap" value={phase2Cap} onChange={(e) => setPhase2Cap(e.target.value)} style={{ width: 70 }} />
                        <label htmlFor="phase3Cap">Phase 3</label>
                        <input id="phase3Cap" value={phase3Cap} onChange={(e) => setPhase3Cap(e.target.value)} style={{ width: 70 }} />
                      </div>
                      <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button type="button" onClick={() => runWrite({ address: contractAddress, abi: NFT_ABI, functionName: "setPhaseCap", args: [1, Number(phase1Cap || "0")] })}>Update Phase 1 Cap</button>
                        <button type="button" onClick={() => runWrite({ address: contractAddress, abi: NFT_ABI, functionName: "setPhaseCap", args: [2, Number(phase2Cap || "0")] })}>Update Phase 2 Cap</button>
                        <button type="button" onClick={() => runWrite({ address: contractAddress, abi: NFT_ABI, functionName: "setPhaseCap", args: [3, Number(phase3Cap || "0")] })}>Update Phase 3 Cap</button>
                        <button
                          type="button"
                          onClick={async () => {
                            await runWrite({ address: contractAddress, abi: NFT_ABI, functionName: "setPhaseCap", args: [1, Number(phase1Cap || "0")] });
                            await runWrite({ address: contractAddress, abi: NFT_ABI, functionName: "setPhaseCap", args: [2, Number(phase2Cap || "0")] });
                            await runWrite({ address: contractAddress, abi: NFT_ABI, functionName: "setPhaseCap", args: [3, Number(phase3Cap || "0")] });
                          }}
                        >
                          Update All Caps
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            setPhase1Cap("4");
                            setPhase2Cap("2");
                            setPhase3Cap("20");
                            await runWrite({ address: contractAddress, abi: NFT_ABI, functionName: "setPhaseCap", args: [1, 4] });
                            await runWrite({ address: contractAddress, abi: NFT_ABI, functionName: "setPhaseCap", args: [2, 2] });
                            await runWrite({ address: contractAddress, abi: NFT_ABI, functionName: "setPhaseCap", args: [3, 20] });
                          }}
                        >
                          Quick Test Caps (4/2/20)
                        </button>
                      </div>
                    </div>

                    <div style={{ border: "1px solid rgba(148,163,184,0.35)", borderRadius: 10, padding: 10 }}>
                      <button
                        type="button"
                        onClick={() => setWhitelistPanelOpen((v) => !v)}
                        style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center" }}
                      >
                        <span style={{ fontWeight: 700 }}>Whitelist Manager (Phase 1 / Phase 2)</span>
                        <span>{whitelistPanelOpen ? "Hide" : "Show"}</span>
                      </button>
                      {whitelistPanelOpen ? (
                        <div style={{ marginTop: 10, display: "grid", gap: 10 }}>
                          {!whitelistSupported ? (
                            <div style={{ color: "#fca5a5", fontSize: 12 }}>
                              This contract does not expose whitelist list/read functions. Switch to the latest deployment address.
                            </div>
                          ) : null}
                          <div style={{ fontSize: 12, opacity: 0.9 }}>
                            Enter one or multiple wallet addresses (comma or newline separated), then add/remove.
                          </div>

                          <div>
                            <div style={{ marginBottom: 6, fontWeight: 600 }}>Phase 1</div>
                            <textarea
                              value={phase1WhitelistInput}
                              onChange={(e) => setPhase1WhitelistInput(e.target.value)}
                              placeholder="0x... , 0x..."
                              rows={3}
                              style={{ width: "100%" }}
                            />
                            <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
                              <button
                                type="button"
                                disabled={!whitelistSupported}
                                onClick={async () => {
                                  const addrs = parseAddressList(phase1WhitelistInput);
                                  if (!addrs.length) return setError("Enter at least one valid Phase 1 wallet.");
                                  await runWrite({ address: contractAddress, abi: NFT_ABI, functionName: "setPhase1WhitelistBatch", args: [addrs, true] });
                                  await refreshWhitelists();
                                }}
                              >
                                Add to Phase 1
                              </button>
                              <button
                                type="button"
                                disabled={!whitelistSupported}
                                onClick={async () => {
                                  const addrs = parseAddressList(phase1WhitelistInput);
                                  if (!addrs.length) return setError("Enter at least one valid Phase 1 wallet.");
                                  await runWrite({ address: contractAddress, abi: NFT_ABI, functionName: "setPhase1WhitelistBatch", args: [addrs, false] });
                                  await refreshWhitelists();
                                }}
                              >
                                Remove from Phase 1
                              </button>
                            </div>
                          </div>

                          <div>
                            <div style={{ marginBottom: 6, fontWeight: 600 }}>Phase 2</div>
                            <textarea
                              value={phase2WhitelistInput}
                              onChange={(e) => setPhase2WhitelistInput(e.target.value)}
                              placeholder="0x... , 0x..."
                              rows={3}
                              style={{ width: "100%" }}
                            />
                            <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
                              <button
                                type="button"
                                disabled={!whitelistSupported}
                                onClick={async () => {
                                  const addrs = parseAddressList(phase2WhitelistInput);
                                  if (!addrs.length) return setError("Enter at least one valid Phase 2 wallet.");
                                  await runWrite({ address: contractAddress, abi: NFT_ABI, functionName: "setPhase2WhitelistBatch", args: [addrs, true] });
                                  await refreshWhitelists();
                                }}
                              >
                                Add to Phase 2
                              </button>
                              <button
                                type="button"
                                disabled={!whitelistSupported}
                                onClick={async () => {
                                  const addrs = parseAddressList(phase2WhitelistInput);
                                  if (!addrs.length) return setError("Enter at least one valid Phase 2 wallet.");
                                  await runWrite({ address: contractAddress, abi: NFT_ABI, functionName: "setPhase2WhitelistBatch", args: [addrs, false] });
                                  await refreshWhitelists();
                                }}
                              >
                                Remove from Phase 2
                              </button>
                              <button type="button" disabled={!whitelistSupported} onClick={() => void refreshWhitelists()}>
                                Refresh Lists
                              </button>
                            </div>
                          </div>

                          <div style={{ fontSize: 12, opacity: 0.9 }}>
                            {whitelistLoading ? "Loading whitelist from chain..." : `Phase 1: ${phase1Whitelist.length} wallets | Phase 2: ${phase2Whitelist.length} wallets`}
                          </div>

                          <details>
                            <summary>View Phase 1 Whitelist</summary>
                            <div style={{ marginTop: 8, maxHeight: 160, overflow: "auto", fontSize: 12 }}>
                              {phase1Whitelist.length ? phase1Whitelist.map((a) => <div key={`p1-${a}`}>{a}</div>) : <div>No addresses.</div>}
                            </div>
                          </details>
                          <details>
                            <summary>View Phase 2 Whitelist</summary>
                            <div style={{ marginTop: 8, maxHeight: 160, overflow: "auto", fontSize: 12 }}>
                              {phase2Whitelist.length ? phase2Whitelist.map((a) => <div key={`p2-${a}`}>{a}</div>) : <div>No addresses.</div>}
                            </div>
                          </details>
                        </div>
                      ) : null}
                    </div>

                    <div style={{ border: "1px solid rgba(148,163,184,0.35)", borderRadius: 10, padding: 10 }}>
                      <div style={{ marginBottom: 8, fontWeight: 700 }}>Mint Live Toggle</div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button type="button" onClick={() => runWrite({ address: contractAddress, abi: NFT_ABI, functionName: "setMintPaused", args: [true] })}>Pause Mint</button>
                        <button type="button" onClick={() => runWrite({ address: contractAddress, abi: NFT_ABI, functionName: "setMintPaused", args: [false] })}>Unpause Mint</button>
                      </div>
                    </div>

                  </div>
                ) : null}
              </div>

              <div className="nft-card" style={{ marginTop: 10 }}>
                <h3>Debug</h3>
                <div style={{ marginBottom: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button type="button" onClick={() => setPreviewSoldOut(false)}>
                    Sold-out OFF
                  </button>
                  <button
                    type="button"
                    onClick={() => setPreviewSoldOut(true)}
                    style={previewSoldOut ? { borderColor: "rgba(245, 158, 11, 0.9)", boxShadow: "0 0 0 1px rgba(245, 158, 11, 0.25) inset" } : {}}
                  >
                    Sold-out ON
                  </button>
                </div>
                <div style={{ marginBottom: 10 }}>
                  <div style={{ marginBottom: 6 }}>Stage Preview</div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {[
                      { key: "live", label: "Live" },
                      { key: "upcoming", label: "Upcoming" },
                      { key: "phase1", label: "Phase 1" },
                      { key: "phase2", label: "Phase 2" },
                      { key: "phase3", label: "Phase 3" },
                      { key: "ended", label: "Ended" },
                    ].map((opt) => (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => setPreviewStage(opt.key)}
                        style={previewStage === opt.key ? { borderColor: "rgba(245, 158, 11, 0.9)", boxShadow: "0 0 0 1px rgba(245, 158, 11, 0.25) inset" } : {}}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{ marginBottom: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button
                    type="button"
                    onClick={() => setPreviewWalletMaxed(false)}
                    style={!previewWalletMaxed ? { borderColor: "rgba(245, 158, 11, 0.9)", boxShadow: "0 0 0 1px rgba(245, 158, 11, 0.25) inset" } : {}}
                  >
                    Wallet Max OFF
                  </button>
                  <button
                    type="button"
                    onClick={() => setPreviewWalletMaxed(true)}
                    style={previewWalletMaxed ? { borderColor: "rgba(245, 158, 11, 0.9)", boxShadow: "0 0 0 1px rgba(245, 158, 11, 0.25) inset" } : {}}
                  >
                    Wallet Max ON
                  </button>
                </div>
                <div>Refreshing: {String(isLoading)}</div>
                <div>Inventory source: {ownedLoadStatus || "loading"}</div>
                <div>On-chain owner balance: {ownedBalanceOnChain}</div>
                <div>On-chain token IDs: {ownedTokenIdsOnChain.length ? ownedTokenIdsOnChain.join(", ") : "-"}</div>
                <div>Latest minted token: {latestMint?.tokenId || "-"}</div>
                <div>Last tx hash: {lastTxHash || "-"}</div>
                <div>Error: {error || "-"}</div>
                <div>Stage preview: {effectiveStage}</div>
                <div>Live phase value: {livePhaseNum === null ? "unavailable" : String(livePhaseNum)}</div>
                <div>Contract read address: {contractAddress}</div>
                <div>Sold-out preview: {previewSoldOut ? "ON" : "OFF"}</div>
                <div>Wallet-max preview: {previewWalletMaxed ? "ON" : "OFF"}</div>
                <div>Can read: {String(canRead)}</div>
                <div>Renderer sync: {rendererSyncStatus || "-"}</div>
                <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button type="button" onClick={() => openMockMintReveal(1)}>
                    Preview Mint Reveal (1)
                  </button>
                  <button type="button" onClick={() => openMockMintReveal(3)}>
                    Preview Mint Reveal (3)
                  </button>
                  <button type="button" onClick={() => openMockMintReveal(10)}>
                    Preview Mint Reveal (10)
                  </button>
                  <button type="button" onClick={() => setError("Wrong network detected. Please switch to Monad Mainnet (143), then try again.")}>
                    Test Wrong Network
                  </button>
                  <button type="button" onClick={() => setError("Network switch was cancelled.")}>
                    Test Cancelled
                  </button>
                  <button type="button" onClick={() => setError("Network switched. Click Mint again.")}>
                    Test Switch Prompt
                  </button>
                  <button type="button" onClick={() => setError("Transaction reverted: execution reverted.")}>
                    Test Tx Revert
                  </button>
                  <button type="button" onClick={() => setError("")}>
                    Clear Status
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}
    </div>
    </>
  );
}
